/**
 * SphereTypeComposerOrganism.tsx
 *
 * Pre-create check (atomic-design Rule 3): considered `SphereEditor` — rejected because it edits
 * a sphere INSTANCE's values against a settled schema, while this edits the schema itself, where
 * every section is a definition surface with its own ghost/apply lifecycle. Reusing it would have
 * meant giving an instance editor a draft model, a section index and a publish gate. The reusable
 * parts were taken instead: this composes `SphereTypeHeaderMolecule`, `EditorBlockMolecule`,
 * `ComposerIndexRailMolecule` and the per-section row molecules rather than restyling any of them.
 *
 * Read-only Sphere Type Editor shell (EAINT-892) — identity header, an eleven-entry section
 * index with in-page anchors, and one collapsible section per entry. Purely presentational:
 * `useSphereType` (the caller) owns the fetch/lineage-walk/cache; this organism only ever
 * renders what it's handed. Phase 2 filled in seven content sections (Identity, Placement,
 * Aspects — which also carries the merged property summary since EAINT-921 retired the
 * standalone Own properties section — Business rules, Relationships, Perspectives, Arcs). Phase 3 adds
 * Advanced (a read-only derived shelf, presence/counts only) and named placeholders for the
 * three sections whose own design (Health, Presentation, Languages) is still in flight, plus
 * the loading/not-found/degraded states and the `onSectionView` viewport hook analytics rides.
 *
 * Considered: StudioTemplateDrawerOrganism — rejected because it is a drawer keyed on a
 * template-type switch, and an eleven-section audit surface with a sticky section index does not
 * fit a drawer's width or its one-body-per-case shape (its `case 'schema'` placeholder is what
 * this replaces). No other existing organism composes a section index over a fetched definition.
 * Every section body composes existing atoms/molecules; this file adds layout, not primitives.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { filterHiddenPropertiesExcluding, getTranslation } from '@center-platform/spheres/identity';
import { compareSectionGroups, DEFAULT_SECTION_KEY, HEADER_RENDERED_PROPERTY_NAMES } from '../../../utils/resolveSectionLayout';
import { validateApplicability } from '@center-platform/spheres/validation';
import type { ArcDefault, Schema, SchemaProperty, SchemaRelationship, SchemaPerspective, SchemaLockReason, SchemaStructuralGuidance } from 'types/schema';
import type { BusinessRule } from 'types/businessRules';
import type { UnifiedFilters } from 'types/filters';
import type { GlyphColor } from 'types/glyph';
import type { AspectAttachmentConfig, AspectRef, AspectRow } from 'types/aspect';
import type { SphereTypeStatus, LineageResult } from '../../../hooks/useSphereType';
import type { UpdateSphereTypeFields, UpdateSphereTypeOutcome } from '../../../api/updateSphereType';
import type { SchemaGate, SchemaAspectNeeds, SchemaNeed } from '../../../api/schemaApi';
import type { PublishSphereTypeOutcome } from '../../../api/publishSphereType';
import type { ComposerAgentTypeEditProposal, ComposerGhostProvenance, ComposerTypeCritique } from '../../../composer/agentTransport';
import { usePublisherName } from '../../../hooks/usePublisherName';
import { useOrgAvailableTypes } from '../../../hooks/useOrgAvailableTypes';
import { useAttachableAspectCatalog } from '../../../hooks/useAttachableAspectCatalog';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import SphereTypeHeaderMolecule from '../../molecules/studio/SphereTypeHeaderMolecule';
import EditorBlockMolecule, { type EditorBlockCompletion } from '../../molecules/EditorBlock/EditorBlockMolecule';
import ComposerIndexRailMolecule from '../../molecules/ComposerIndexRail/ComposerIndexRailMolecule';
import GhostBarMolecule from '../../molecules/studio/GhostBarMolecule';
import { describePropertyRules, machineKey, propTypeLabel } from '../../molecules/studio/PropertyEditorMolecule';
import TypeFieldRowMolecule, { type TypeFieldRowMode, type TypeDefinitionRowControl } from '../../molecules/studio/TypeFieldRowMolecule';
import TypePerspectiveRowMolecule, { PERSPECTIVE_GONE_ROW_PARITY } from '../../molecules/studio/TypePerspectiveRowMolecule';
import ParentOptionRowMolecule from '../../molecules/studio/ParentOptionRowMolecule';
import PerspectiveEditorMolecule, {
  PERSPECTIVE_DECLARE_PARITY,
  PERSPECTIVE_GONE_ROW_EDITOR_PARITY,
  type PerspectiveAssigneeKind,
  type PerspectiveEditorField,
} from '../../molecules/studio/PerspectiveEditorMolecule';
import TypeRuleRowMolecule, { ruleSentence } from '../../molecules/studio/TypeRuleRowMolecule';
import { blockingProblemCount } from '../../../utils/ruleProblems';
import DeclaredInheritedGroupMolecule, { type RowGroup } from '../../molecules/studio/DeclaredInheritedGroupMolecule';
import MergedPropertySummaryMolecule from '../../molecules/studio/MergedPropertySummaryMolecule';
import TranslationCoverageChipAtom from '../../atoms/TranslationCoverageChip/TranslationCoverageChipAtom';
import RelationshipSheetMolecule, { type RelationshipSheetPatch } from '../../molecules/studio/RelationshipSheetMolecule';
import RelationshipOverrideSheetMolecule from '../../molecules/studio/RelationshipOverrideSheetMolecule';
import { deriveApplicability } from '../../../utils/contextFlowSerialization';
import { relAdjustments, relApplyOverride, type RelationshipOverridePatch } from '../../molecules/studio/relationshipOverride';
import RuleSheetMolecule, { type RuleSheetPatch } from '../../molecules/studio/RuleSheetMolecule';
import AspectDefSummaryMolecule, { type AspectDefSummaryMeta } from '../../molecules/studio/AspectDefSummaryMolecule';
import AspectAttachmentSheetMolecule, {
  needCopy,
  type AttachmentConfig,
  type AttachmentDefinition,
  type AttachmentFillWord,
  type AttachmentNeedRow,
} from '../../molecules/studio/AspectAttachmentSheetMolecule';
import { answerAspectNeed, type NeedAnswerAt } from '../../../api/answerAspectNeed';
import PublishGateBarMolecule from '../../molecules/studio/PublishGateBarMolecule';

/** The footer gate-status sentence the header's Publish button `aria-describedby`s. */
const SPHERE_TYPE_GATE_STATUS_ID = 'sphere-type-editor-gate-status';
import GlyphPickerMolecule, { type GlyphPickerDatums } from '../../molecules/studio/GlyphPickerMolecule';
import InspectorRailOrganism, { type ComposerSideRailHandle, type InspectorRailSelection } from '../InspectorRail/InspectorRailOrganism';
import type { AgentThreadParity } from '../composer/AgentThreadOrganism';
import type { AgentThreadItem } from '../../molecules/composer/AgentMessageMolecule';
import SampleRecordPanelMolecule from '../../molecules/studio/SampleRecordPanelMolecule';
import SampleRecordDialogMolecule from '../../molecules/studio/SampleRecordDialogMolecule';
import NewAspectDialogMolecule, { type NewAspectDraft } from '../../molecules/studio/NewAspectDialogMolecule';
import { AspectSystemNameCollisionError } from '../../../api/aspectApi';
import { sampleRecordName } from '../../molecules/studio/sampleRecordRows';
import { useSandboxSphere } from './useSandboxSphere';
import PickerShellMolecule from '../../molecules/PickerShell/PickerShellMolecule';
import TagAtom from '../../atoms/Tag/TagAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import ArcRowMolecule from '../../molecules/studio/ArcRowMolecule';
import InlineConfirmMolecule from '../../molecules/studio/InlineConfirmMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import InheritanceManifestPanelMolecule, { type InheritanceManifestRow } from '../../molecules/studio/InheritanceManifestPanelMolecule';
import InputAtom from '../../atoms/Input/InputAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { StatusBadgeAtom } from '../../atoms/StatusBadge';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import { SkeletonAtom } from '../../atoms/Skeleton';
import MessageAtom from '../../atoms/Message/MessageAtom';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import EditorRailShellOrganism from '../EditorRailShell/EditorRailShellOrganism';
import './SphereTypeComposerOrganism.scss';

export interface SphereTypeComposerOrganismProps {
  status: SphereTypeStatus;
  definition: Schema | null;
  lineage: LineageResult | null;
  /** Fired at most once per section id, when that section's anchor enters the viewport. */
  onSectionView?: (sectionId: string) => void;
  /**
   * `type-composer.jsx:800` (`s-back`) — leaves the composer for the gallery it was opened
   * from. Absent renders no back button (Phase 1a shell); the caller supplies it once a
   * return route exists.
   */
  onBack?: () => void;
  /**
   * The locked composer's primary action (spec § Primary action and reversal:
   * "Composer (locked) — Extend it instead — the fork path"). The fork flow
   * itself has no route in this spec, so this is left to the caller; the
   * button renders regardless so the locked view is never actionless.
   */
  onExtendInstead?: () => void;
  /**
   * The index rail's "Inherited & derived" entry. Absent, it routes to the side rail's own
   * Inherited tab (`ComposerSideRailHandle.focusInheritedTab`) — the entry is always a way
   * there, never a section of its own; a host supplies this only to route somewhere else.
   */
  onInheritedClick?: () => void;
  /**
   * Patches one field group onto the working draft (EAINT-895 Phase 3).
   * Absent — the same as the caller's `useSphereType().saveField` returning
   * `'locked'` — every field renders in its non-interactive `'disabled'` or
   * `'locked'` treatment; nothing in this organism assumes the prop exists.
   */
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  /**
   * True for the duration of an in-flight `saveField` call (`useSphereType`'s own `saving`).
   * Surfaced beside `conflict`/`saveError` — both at the top of the document and, when a rail
   * selection is open, inside the rail body itself (EAINT-933 Phase 3.3), so an author editing a
   * relationship/override/perspective in the rail sees the save's outcome where they're looking,
   * not only in a banner that can sit scrolled out of view above the rail.
   */
  saving?: boolean;
  /**
   * True after the caller's most recent `saveField` hit a stale `If-Match`
   * (spec `staleEditSurfacesConflictNotClobber`). Surfaced as a banner; never
   * used to reset any field's local value — that would be exactly the
   * clobber the spec forbids.
   */
  conflict?: boolean;
  /**
   * True after the caller's most recent `saveField` failed for a reason other
   * than a stale `If-Match` (spec § States per component — `save-failed`).
   * Surfaced as a banner alongside `conflict`; the field's local value is left
   * exactly as typed, same reasoning as `conflict`.
   */
  saveError?: boolean;
  /** The publish-readiness checklist (EAINT-895 B4). Absent/`'idle'` renders no footer — a locked composer has nothing to publish. */
  gate?: SchemaGate | null;
  gateStatus?: 'idle' | 'loading' | 'ready' | 'error';
  /**
   * Per-aspect resolved needs (EAINT-897 C4, `GET /schemas/:id/needs`), keyed by
   * `AspectRef.systemName` — fetched by the caller's `useSphereType`, same
   * pattern as `gate`. Absent renders every aspect with no needs chips, which
   * is honest for a schema whose needs haven't loaded yet, not a missing
   * feature.
   */
  needsByAspect?: Record<string, SchemaAspectNeeds['needs']>;
  /**
   * Every aspect definition this workspace can resolve — the caller org's ∪ the platform
   * tenant's (`GET /aspects/list`), read by the page's `useSphereType` exactly like
   * `needsByAspect`. Joined onto each composed `AspectRef` by system name, it is what
   * supplies the row's description (`compose.r-desc`), its marketplace origin
   * (`compose.installed-from`) and — by ABSENCE of a row — the reading that blocks publish
   * (`compose.not-defined-yet-blocks-publish`).
   *
   * `null`/absent means "not loaded yet", which is deliberately NOT "not defined": every
   * resolvability claim stays silent until a real list has arrived, so a slow read can
   * never make a well-formed type look broken.
   */
  aspectCatalog?: AspectRow[] | null;
  /**
   * `compose.draft-it` — creates a brand-new aspect definition for the `blank`/`extend` routes
   * of the "Draft a new aspect" dialog (`NewAspectDialogMolecule`, EAINT-933 Phase 3.4). This
   * organism owns the dialog's UI, its own-catalog duplicate detection, and the `describe`
   * route (forwarded to the live agent thread via `ComposerSideRailHandle.submitAgentMessage`
   * — a proposal, never a direct write); only `blank`/`extend`'s actual `POST /aspects/` call
   * is delegated to the caller, exactly like `onCreateSandboxSphere` delegates sandbox creation.
   * Resolves the created aspect's `system_name` so this organism can attach and open it, or
   * rejects (e.g. a `system_name` collision) so the dialog stays open with the draft intact
   * (`draftAspectCollisionKeepsTheDraft`). Absent, the `[Draft a new aspect]` affordance is not
   * rendered at all — a submit that closed the dialog and discarded the author's intent read as
   * success (`/standards/frontend/ux-craft.md` I2).
   */
  onDraftAspect?: (draft: NewAspectDraft) => Promise<string | undefined>;
  /**
   * Every type Placement's picker may browse to (`place.picker`, `type-composer.jsx:1073-1112`)
   * — the platform's read of "what can this type inherit from", already excluding sealed types,
   * this type's own descendants, and this type itself. Absent alongside `onMoveParent`, same
   * reasoning as `onDraftAspect`: no capability to browse, so no `Move it` button to open it.
   */
  parentOptions?: ParentOption[];
  /**
   * Fires when the author picks a row in the parent picker. This organism does not reparent
   * itself — `parent_schema_id` arrives back through `definition` once the caller's own
   * `update-schema` write resolves (Phase 3), the same one-way flow `onSaveField` uses for
   * every other field. Absent, `Move it` / `Change` does not render at all.
   */
  onMoveParent?: (option: ParentOption) => void;
  publishing?: boolean;
  /** Cuts the working draft over to published (EAINT-895 Phase 4). Absent, same as `saveField` absent: nothing renders as actionable. */
  onPublish?: () => Promise<PublishSphereTypeOutcome | 'locked'>;
  /**
   * `type-composer.jsx:831` (`Save draft`) — an explicit commit distinct from the per-field
   * autosave (spec C11: flushes any in-progress edit through the existing `mode:'draft'` write
   * and ensures the draft row exists, creating it from published if absent). The organism itself
   * blurs whatever field currently has focus before calling this — a focused text/multiline
   * control only commits `onBlur` (`TypeFieldRowMolecule`), so without the blur an edit still
   * mid-keystroke would be silently skipped by Save draft. Absent renders the button with a
   * no-op click rather than omitting it — the header actions area must render in the default
   * editable state regardless of whether the caller has wired the handler yet.
   */
  onSaveDraft?: (pendingFields: number) => void;
  savingDraft?: boolean;
  /**
   * Opens a sphere by id — what the Sample tab's `[Full sphere]` does once a sandbox sphere
   * exists (spec Phase 5). Absent, `[Full sphere]` opens the synthetic preview dialog it
   * always has, so a host with no router is not left holding a dead button.
   */
  onOpenSphere?: (sphereId: string) => void;
  /**
   * The agent rail (EAINT-895 Phase 5). All four absent — the default — renders the side rail
   * with the Sample tab only, exactly like Phase 4 (agent-unavailable per spec § States: "rail
   * hidden, manual path unaffected"). `composer-agent-service`'s base URL; a caller with no
   * dynamic config / env var set simply never passes this.
   */
  agentEndpoint?: string;
  /** Story/test seam, mirroring `InspectorRailOrganism`'s own: mounts the side rail with a
   * given tab selected, so a story can show the Inherited panel the composer builds. */
  initialSideTab?: 'sample' | 'agent' | 'inherits';
  /**
   * Story/test seam, threaded straight to `InspectorRailOrganism`'s own `initialAgentItems`.
   * Seeding history is the honest way to show the agent-available header/rail states without
   * faking a live `agentEndpoint` — `AgentThreadOrganism` (see its own stories file) never
   * fakes a connection, so availability there is `endpoint && !unavailable` OR "this thread
   * already has turns" (`AgentThreadOrganism.tsx:215`). A caller with a real prior session has
   * real items; a story representing one seeds them the same way.
   */
  initialAgentItems?: AgentThreadItem[];
  agentGetToken?: () => Promise<string | undefined>;
  /** Present together with `agentEndpoint` whenever the schema id is known — absent while the
   * definition hasn't loaded yet. */
  agentTarget?: { kind: 'schema'; id: string };
  /**
   * The arc templates the caller may see (`useArcTemplates`), for the Arcs
   * block's picker and for naming and gating the arcs a type already declares.
   * Prop-drilled rather than fetched here, like `needsByAspect`: the page owns
   * the reads, the composer renders them. Empty means "none visible", which the
   * picker states rather than guesses at.
   */
  arcTemplates?: ArcTemplateOption[];
  /**
   * Which view the Arcs block opens on. Absent (the norm) means the block
   * decides from the definition: `declared` when the type has its own list,
   * `following` otherwise.
   */
  arcsInitialView?: ArcsView;
  /**
   * Proposals already standing when the composer mounts. The ONLY seam through which a ghost
   * state can be reached without a live agent: `ghosts` is otherwise internal state fed solely by
   * `onTypeEditProposal`, so a story or a test could not render a section's ghost bar at all. It
   * seeds the initial state and is not read again - the user's Keep/Discard is the state after
   * that, exactly as it is for a proposal that arrives over the wire.
   */
  initialGhosts?: (ComposerAgentTypeEditProposal & { provenance?: ComposerGhostProvenance })[];
  /**
   * The attachment whose drop confirm stands open when the composer mounts, by system name. The
   * confirm is otherwise reached only by clicking a row's detach control, so this is the seam a
   * story or a test uses to render the confirmed state — the same shape as `arcsInitialView`.
   */
  aspectsInitialConfirmDrop?: string;
  /**
   * Attachments already detached in this session when the composer mounts. Session state with no
   * schema representation (a detach is not published until the type is), so it has no other seam.
   */
  aspectsInitialDropped?: AspectRef[];
  /**
   * The attachment whose configuration sheet stands open when the composer mounts, by system
   * name. The sheet is otherwise reached only through a row's `Configure` control, so this is
   * the seam a story or a test uses to render its states — same shape as `aspectsInitialConfirmDrop`.
   */
  aspectsInitialConfiguring?: string;
  /** The business rule whose sheet stands open when the composer mounts, by `id`. */
  rulesInitialOpen?: string;
  /**
   * The INHERITED relationship whose override sheet stands open when the composer mounts,
   * by `system_name` — acting on the row's Adjust is the only other way in.
   */
  relsInitialAdjust?: string;
  /** The perspective whose body stands open when the composer mounts, by `system_name`. */
  perspInitialOpen?: string;
  /** True when the perspectives declare form stands open on mount — the add row's other way in. */
  perspInitialDrafting?: boolean;
  /** The inherited perspective whose SUPPRESS ask stands open when the composer mounts, by `system_name`. */
  perspInitialAskSuppress?: string;
  /** The perspective whose delete confirm stands open on mount, by `system_name`. */
  perspInitialConfirmDelete?: string;
  /** Perspective declarations already deleted this session on mount — the undo state. */
  perspInitialDeleted?: string[];
  /** This session's per-row field overlay on mount, by `system_name` — the seam for a "changed" row without a live edit. */
  perspInitialEdits?: Record<string, Partial<SchemaPerspective>>;
  /** The declare form's own overlay on mount — the seam for its own "changed" fields. */
  perspInitialDraft?: Partial<SchemaPerspective>;
  /**
   * The agent's ambient, advisory-only observations about the draft (`compose.callout` etc.,
   * EAINT-933 Phase 4; spec § Agentic UX — Allow: read and comment, never write). Absent/empty
   * renders no critique cards, which is honest for a host with no live agent or nothing to say —
   * the same "capability absent, not greyed" reasoning as `onDraftAspect`. A card's own Dismiss
   * is session-only state owned by the section that renders it, mirroring `initialDropped`.
   */
  critique?: ComposerTypeCritique[];
}

const SECTION_VIEW_ROOT_MARGIN = '0px 0px -70% 0px';

/** Stable identity, so the arcs block's memoised lookups don't rebuild every render. */
const EMPTY_ARC_TEMPLATES: ArcTemplateOption[] = [];

/**
 * The contract ids the `side` region gives the thread's own elements (`type-composer.jsx:1871-1898`):
 * the turn's jump link and shared chip, and the compose bar's input and send. Passed to
 * `InspectorRailOrganism` (EAINT-921 Phase 2: relocated from that organism itself, which now takes
 * `parity` as a prop rather than owning this constant) rather than stamped inside
 * `AgentThreadOrganism`, which other hosts (the Context Builder, the gallery) mount outside this
 * decomposition.
 */
const SIDE_RAIL_AGENT_PARITY: AgentThreadParity = {
  threadContainer: 'side > panel#2 > list#2',
  itemContainer: 'side > panel#2 > list#2 > each(t.interview)',
  jump: 'side.see-it',
  shared: 'side.asked-once-for-both',
  composeContainer: 'side > panel#2 > group',
  input: 'side.tell-it-what-to-change',
  send: 'side.send',
  critiqueCard: 'side.watching-as-you-edit',
  critiqueHeading: 'side.watching-as-you-edit#2',
  critiqueChip: 'side.chip',
  critiqueDismiss: 'side.button#6',
};

interface SectionSpec {
  id: string;
  icon: string;
  labelKey: string;
  /**
   * The SECTION HEADER's count (`SECTION_COUNT_DATUM` maps each to its contract datum). It is
   * deliberately NOT forwarded to the index rail: `type-composer.jsx:858-868` is mark + label +
   * the `adv` badge, and the spec's UI Components row pins the rail as marks-only, no counts.
   */
  count?: number;
  /** Badged `adv` in the rail, per `type-data.jsx:17` / `type-composer.jsx:867`. */
  advanced?: boolean;
  /**
   * Contract region `side` (`arcs`/`health`/`present` all carry `region: "side"` in the
   * decomposition — every one of their datums' states include `side=inherits`). A `side` block
   * is never in the doc scroll or the index rail's `TYPE_BLOCKS.filter(b => !b.side)` list; it
   * renders inside the rail's Inherited tab instead, via the same card chrome.
   */
  side?: boolean;
}

/**
 * The full section catalogue, established from the contract's document flow (Identity →
 * Placement → Aspects → Business rules → Relationships → Perspectives → Languages → Agent
 * guidance → Advanced; the prototype's Own properties entry was folded into Aspects by
 * EAINT-921), with Languages placed alongside the placeholder group rather than the
 * prototype's live position, per this spec's decision to defer Languages to a placeholder
 * (Phase 3). Arcs/Health/Presentation carry `side: true` — the decomposition gives every one
 * of their datums `region: "side"` (the prototype groups them in its own "Inherited" side
 * panel), so they never render in this list's doc scroll or index rail; `sections` still
 * includes them for section-id validity (`SECTION_IDS`, agent proposal targeting, ghost
 * bookkeeping) — only the doc/index consumers filter `!section.side`.
 */
export function buildSections(definition: Schema | null): SectionSpec[] {
  return [
    { id: 'identity', icon: 'pi pi-tag', labelKey: 'studio.typeEditor.section.identity' },
    { id: 'placement', icon: 'pi pi-sitemap', labelKey: 'studio.typeEditor.section.placement' },
    {
      id: 'aspects',
      icon: 'pi pi-th-large',
      labelKey: 'studio.typeEditor.section.aspects',
      count: definition?.aspects?.length ?? 0,
    },
    {
      id: 'business-rules',
      icon: 'pi pi-shield',
      labelKey: 'studio.typeEditor.section.businessRules',
      count: definition?.businessRules?.length ?? 0,
    },
    {
      id: 'relationships',
      icon: 'pi pi-link',
      labelKey: 'studio.typeEditor.section.relationships',
      count: definition?.relationships?.length ?? 0,
    },
    {
      id: 'perspectives',
      icon: 'pi pi-eye',
      labelKey: 'studio.typeEditor.section.perspectives',
      count: definition?.perspectives?.length ?? 0,
    },
    {
      id: 'arcs',
      icon: 'pi pi-directions',
      labelKey: 'studio.typeEditor.section.arcs',
      count: definition?.defaultArcs?.length ?? (definition?.arcTemplateSystemName ? 1 : 0),
      side: true,
    },
    { id: 'health', icon: 'pi pi-heart', labelKey: 'studio.typeEditor.section.health', side: true },
    { id: 'presentation', icon: 'pi pi-desktop', labelKey: 'studio.typeEditor.section.presentation', side: true },
    { id: 'languages', icon: 'pi pi-globe', labelKey: 'studio.typeEditor.section.languages' },
    { id: 'guide', icon: 'pi pi-android', labelKey: 'studio.typeEditor.section.agentGuidance' },
    // `type-data.jsx:17` — `adv` is the one block flagged `advanced: true`, and the
    // rail badges it. The rail carries no counts: `type-composer.jsx:858-868` is
    // mark + label + the badge, which is what the spec's UI Components row pins.
    { id: 'advanced', icon: 'pi pi-cog', labelKey: 'studio.typeEditor.section.advanced', advanced: true },
  ];
}

/**
 * A short, non-authoritative summary of the working draft (EAINT-895 Phase 5) — the agent's
 * grounding starting point, not a source of truth (it still calls `get_sphere_schema` for the
 * real definition before proposing anything). Kept to counts, never full field contents: the
 * system prompt fences this as `<untrusted_registry_data>`, so it should be small.
 */
function buildDraftDigest(definition: Schema | null): string | undefined {
  if (!definition) return undefined;
  return [
    `type: ${definition.system_name}`,
    `own properties: ${ownProperties(definition).length}`,
    `relationships: ${(definition.relationships ?? []).length}`,
    `perspectives: ${(definition.perspectives ?? []).length}`,
    `business rules: ${(definition.businessRules ?? []).length}`,
    `aspects: ${(definition.aspects ?? []).length}`,
  ].join(', ');
}

/**
 * Resolves a server-supplied display name through its localization key, falling
 * back to the raw value. Server-supplied names (property/relationship/perspective
 * labels, schema names) carry `*_key` fields; rendering the raw value would leave
 * the whole document in English while the chrome translates.
 */
type Localize = (key: string | undefined, fallback: string) => string;

/**
 * Number of sections the DOC SCROLL AND INDEX RAIL render — `side` blocks (Arcs/Health/
 * Presentation) live in the rail's Inherited tab instead, so they don't count here. Exported
 * so analytics reports the real figure rather than a duplicated literal that silently drifts
 * when a phase adds or removes a section.
 */
export const SPHERE_TYPE_SECTION_COUNT = buildSections(null).filter((section) => !section.side).length;

/** The section ids a proposal can land in — `buildSections`' ids, not a second list to drift. */
const SECTION_IDS: ReadonlySet<string> = new Set(buildSections(null).map((section) => section.id));

/** The `side` block ids (Arcs/Health/Presentation) — mounted in the rail's Inherited tab, not the
 * doc scroll, so a jump to one of these opens that tab instead of scrolling `sectionsRef`. */
const SIDE_SECTION_IDS: ReadonlySet<string> = new Set(
  buildSections(null)
    .filter((section) => section.side)
    .map((section) => section.id)
);

/**
 * Defence-in-depth against a proposal that could never be reviewed: the agent service's
 * `propose_type_edit` already rejects a `sectionId` outside the composer's sections and a
 * `properties` key (the rail migration removed inline property authoring), but a skewed
 * service, a stale `initialGhosts` fixture or future drift would otherwise land a ghost that is
 * COUNTED — it holds Publish — while rendering nowhere, reachable only through Discard all.
 * Returns the reason a proposal is refused, or `null` when it may land.
 */
export function rejectedProposalReason(proposal: ComposerAgentTypeEditProposal): string | null {
  if (!SECTION_IDS.has(proposal.sectionId)) return `unknown sectionId "${proposal.sectionId}"`;
  if (Object.prototype.hasOwnProperty.call(proposal.fields, 'properties')) {
    return 'a `properties` field — a property belongs to an aspect, never to a type edit';
  }
  return null;
}

function anchorId(sectionId: string): string {
  return `sphere-type-section-${sectionId}`;
}

/**
 * Which sections have a caption/body sentence, and under which key — a fixed
 * map rather than a computed key path, so a section absent from
 * `en-US.json`'s `composer.*` block (Perspectives, Advanced — see the plan's
 * open question on `everySectionRendersItsCaption`) renders no caption
 * instead of falling back to a raw, untranslated key on screen.
 *
 * Presentation/Languages reuse their existing `placeholderTitle` as the
 * section's caption — that key already exists and already reads as the
 * section's one-line description, so it satisfies the caption slot without a
 * duplicate translation. Health has no entry: its card caption is forced
 * `undefined` at the render site above, because its real contract caption
 * (`health.change-what-s-composed`) is a button, not descriptive text — it
 * renders in the body instead (see `HealthSection`).
 */
const SECTION_CAPTION_KEYS: Partial<Record<string, { caption?: string; captionReadOnly?: string }>> = {
  identity: {
    caption: 'studio.typeEditor.composer.identity.caption',
    captionReadOnly: 'studio.typeEditor.composer.identity.captionReadOnly',
  },
  placement: {
    caption: 'studio.typeEditor.composer.placement.caption',
    captionReadOnly: 'studio.typeEditor.composer.placement.captionReadOnly',
  },
  aspects: { caption: 'studio.typeEditor.composer.aspects.caption' },
  perspectives: { caption: 'studio.typeEditor.composer.perspectives.caption' },
  arcs: { caption: 'studio.typeEditor.composer.arcs.caption' },
  presentation: { caption: 'studio.typeEditor.presentation.placeholderTitle' },
  languages: { caption: 'studio.typeEditor.composer.languages.caption' },
  guide: { caption: 'studio.typeEditor.composer.guide.caption' },
  advanced: { caption: 'studio.typeEditor.advanced.caption' },
};

/**
 * The contract datum each block's `caption` (container="caption", always its row 0) maps to.
 * `ident`/`place`'s contract caption text ("part of the platform definition" / "set by the
 * platform") is a DIFFERENT string from the `caption`/`captionReadOnly` copy `sectionCaption()`
 * renders above — that copy mismatch is a PAR-2/3 content defect for Phase 4, not this map's
 * concern; this only has to point the existing caption slot at the right contract leaf so PAR-4/5
 * (kind + container) pass regardless of which string is showing.
 */
const SECTION_CAPTION_DATUM: Partial<Record<string, string>> = {
  advanced: 'adv.present-folded-never-raised-in-the-inter',
  arcs: 'arcs.replaces-doesn-t-merge',
  identity: 'ident.part-of-the-platform-definition',
  placement: 'place.set-by-the-platform',
  perspectives: 'persp.standing-on-a-sphere-and-what-it-grants',
  languages: 'languages.english-is-the-source-everything-else-tr',
  guide: 'guide.what-other-agents-are-told-about-this-ty',
};

/**
 * The contract datum each block's header COUNT renders (`count`-kind, `caption`
 * container, row 0) — the sibling of `SECTION_CAPTION_DATUM` for blocks whose
 * caption-row leaf is a number rather than descriptive text.
 */
const SECTION_COUNT_DATUM: Partial<Record<string, string>> = {
  aspects: 'compose.composed-length',
  perspectives: 'persp.live-length',
  relationships: 'rels.relrows-length',
  'business-rules': 'rules.rulerows-length',
  arcs: 'arcs.arcrows-length',
};

/**
 * `compose > group > each([…]) > group` (`trail-composer.jsx:1394-1398`) — the merged
 * property summary's per-group heading leaves. One `each(...)` in the contract, so
 * every group (from aspects / inherited / declared directly) carries the same ids.
 */
const MERGED_PROPERTY_SUMMARY_GROUP_DATUMS = {
  heading: 'compose.heading',
  label: 'compose.glabel',
  count: 'compose.rows-length',
  note: 'compose.gnote',
};

/** @see PropertySummaryRowDatums. The merged property summary's row leaves (`compose.p-label`..`compose.on-the-aspect`). */
const MERGED_PROPERTY_SUMMARY_ROW_DATUMS: PropertySummaryRowDatums = {
  label: 'compose.p-label',
  typeLabel: 'compose.p-typelabel',
  note: 'compose.p-note',
  unit: 'compose.p-unit',
  required: 'compose.required',
  readOnly: 'compose.engine-written',
  fromAspect: 'compose.held-by-a-composed-aspect-changed-on-the',
  inherited: 'compose.from#2',
  legacy: 'compose.legacy',
  onAspect: 'compose.on-the-aspect',
  fromAspectTitleKey: 'studio.typeEditor.aspects.propertySummary.fromAspectTitle',
};

const SECTION_BODY_TEXT_KEYS: Partial<Record<string, string>> = {
  relationships: 'studio.typeEditor.composer.relationships.bodyText',
  'business-rules': 'studio.typeEditor.composer.businessRules.bodyText',
  arcs: 'studio.typeEditor.composer.arcs.bodyText',
  perspectives: 'studio.typeEditor.composer.perspectives.bodyText',
};

/**
 * The contract datum each block's body-text `note` (container=the block itself,
 * row 0) renders. `business-rules`'s own `intro` paragraph (this same string,
 * `businessRules.sectionIntro`) was a duplicate of this shell-rendered text —
 * removed alongside this wiring rather than left as a second, unannotated copy
 * (a PAR-6 extra-structure defect).
 */
const SECTION_BODY_TEXT_DATUM: Partial<Record<string, string>> = {
  relationships: 'rels.added-never-removed-a-subtype-gets-these',
  'business-rules': 'rules.constraints-spanning-more-than-one-prope',
  arcs: 'arcs.the-arcs-a-sphere-of-this-type-starts-ru',
  perspectives: 'persp.globals-always-arrive-non-globals-arrive',
};

/**
 * `type-composer.jsx:345-362` (`reviewOf` / `unsettled`) — the attachments whose setup is
 * THIS type's to finish. One derivation, read by the block header here and (later) by the
 * row's own settlement chip, so the two cannot disagree about what is outstanding.
 *
 * Two of the prototype's three open-item sources are computable from what this surface
 * reads, and the third is deliberately excluded rather than guessed:
 * - a type-level need with no answer (the C4 needs read), and
 * - an attachment whose definition the workspace registry cannot resolve (the catalog),
 * - but NOT "no decision on targets": that reads `cfg.targets` from the per-attachment
 *   config store, which nothing writes yet, so treating every evaluated aspect as
 *   unsettled would mark settled types as unfinished. It joins when the store lands.
 *
 * Inherited attachments are never counted: their answers belong to the type that attached
 * them, and there is nothing to do about them here.
 */
function unsettledAttachments(
  aspects: AspectRef[],
  needsByAspect: Record<string, SchemaAspectNeeds['needs']>,
  catalogByName: Map<string, AspectRow> | null
): AspectRef[] {
  return aspects.filter((aspect) => {
    if (aspect.is_inherited === true) return false;
    if (catalogByName && !catalogByName.has(aspect.systemName)) return true;
    return (needsByAspect[aspect.systemName] ?? []).some((need) => need.at === 'type' && !need.answer);
  });
}

function sectionCaption(sectionId: string, locked: boolean, t: (key: string) => string): string | undefined {
  const keys = SECTION_CAPTION_KEYS[sectionId];
  if (!keys) return undefined;
  if (locked && keys.captionReadOnly) return t(keys.captionReadOnly);
  return keys.caption ? t(keys.caption) : undefined;
}

function sectionBodyText(sectionId: string, t: (key: string) => string): string | undefined {
  const key = SECTION_BODY_TEXT_KEYS[sectionId];
  return key ? t(key) : undefined;
}

/**
 * `done` once a section carries content, `empty` while it doesn't — the prototype's own
 * rule (`trail-composer.jsx:435-436`, `blockFilled`): "a blue check over an empty block
 * overstates state, which is the one thing this screen must not do." Identity is done
 * only once name AND description are both set (`blockFilled.ident`); Placement once a
 * parent is set (`blockFilled.place`'s `!!parentName`, the durable half of that check —
 * the other half, an in-progress unsaved "new family" name, is transient UI state this
 * function has no access to). Advanced always renders fields (the derived shelf), so it
 * reads `done` unconditionally, matching `blockFilled.adv: true`. `ghost` — an agent
 * proposal awaiting review (EAINT-895 Phase 5) — always wins over `done`/`empty`: a
 * section with a pending ghost is neither finished nor untouched, it is awaiting the
 * user's Keep/Discard.
 */
function sectionCompletion(sectionId: string, definition: Schema, hasGhost = false): EditorBlockCompletion {
  if (hasGhost) return 'ghost';
  switch (sectionId) {
    case 'identity':
      return definition.name?.trim() && definition.description?.trim() ? 'done' : 'empty';
    case 'placement':
      return definition.parent_schema_id ? 'done' : 'empty';
    case 'advanced':
      return 'done';
    case 'aspects':
      return (definition.aspects?.length ?? 0) > 0 ? 'done' : 'empty';
    case 'relationships':
      return (definition.relationships?.length ?? 0) > 0 ? 'done' : 'empty';
    case 'perspectives':
      return (definition.perspectives?.length ?? 0) > 0 ? 'done' : 'empty';
    case 'business-rules':
      return (definition.businessRules?.length ?? 0) > 0 ? 'done' : 'empty';
    case 'arcs':
      return (definition.defaultArcs?.length ?? 0) > 0 || !!definition.arcTemplateSystemName ? 'done' : 'empty';
    case 'health':
    case 'presentation':
    case 'languages':
      return 'empty';
    default:
      return 'empty';
  }
}

/**
 * Resolves the reason-specific banner copy for a locked schema. Falls back to
 * the generic banner for an absent/unrecognized reason — fail-closed data
 * (malformed `lockReason`) must never claim a specific, possibly wrong,
 * reason for the lock.
 */
function lockedBannerKey(lockReason: SchemaLockReason | undefined): string {
  switch (lockReason) {
    case 'platform':
      return 'studio.typeEditor.composer.platformBanner';
    case 'sealed':
      return 'studio.typeEditor.composer.sealedBanner';
    case 'sealed_ancestor':
      return 'studio.typeEditor.composer.sealedAncestorBanner';
    case 'no_permission':
      return 'studio.typeEditor.composer.noPermissionBanner';
    default:
      return 'studio.typeEditor.composer.lockedGenericBanner';
  }
}

/** Own properties exclude aspect-contributed ones — those render attributed to their aspect. */
function ownProperties(definition: Schema): SchemaProperty[] {
  return definition.properties.filter((property) => !property.aspect_system_name);
}

/**
 * The declared-vs-inherited split within `ownProperties` (both already exclude
 * aspect-contributed rows, which never appear in Own properties at all — they
 * render attributed to their aspect in the Aspects section instead). Declared
 * is this type's own set and is what the composer edits and REPLACEs on save
 * (spec § API Design); inherited is read-only here so nobody authors a
 * duplicate of a property an ancestor already declares.
 */
function declaredProperties(definition: Schema): SchemaProperty[] {
  return ownProperties(definition).filter((property) => !property.is_inherited);
}
function inheritedOwnProperties(definition: Schema): SchemaProperty[] {
  return ownProperties(definition).filter((property) => property.is_inherited);
}

/**
 * Properties a COMPOSED ASPECT contributes. A third group, not a flavour of
 * inherited (`type-composer.jsx:1420`): they are edited on the aspect and shared
 * by every type that attaches it, so folding them into "inherited" pointed the
 * reader at the ancestor when the place to change one is the aspect.
 */
function aspectContributedProperties(definition: Schema): SchemaProperty[] {
  return definition.properties.filter((property) => !!property.aspect_system_name);
}

/** The same declared-vs-inherited split as properties, for the relationships REPLACE array (EAINT-895 Phase 3c). */
function declaredRelationships(definition: Schema): SchemaRelationship[] {
  return (definition.relationships ?? []).filter((relationship) => !relationship.is_inherited);
}
function inheritedRelationships(definition: Schema): SchemaRelationship[] {
  return (definition.relationships ?? []).filter((relationship) => !!relationship.is_inherited);
}

/**
 * The ancestor's target ids — families and types as ONE list, because narrowing acts on
 * both alike and only the picker cares which is which. An empty list means the ancestor
 * allows any sphere, the one case where the subtype authors a rule rather than dropping
 * from one.
 */
function relBaseTargetIds(relationship: SchemaRelationship): string[] {
  const base = relationship.adjusted_from ?? relationship;
  const scope = deriveApplicability(base.target_filter);
  if (scope.applicabilityMode !== 'specific') return [];
  return [...(scope.applicabilityFamilies ?? []), ...(scope.applicabilityTypes ?? [])];
}

/** The property axes a specific-type list is written onto — see `relApplyOverride`. */
const REL_TARGET_LIST_PROPERTIES = ['label', 'type'];

/**
 * The patch the sheet opens on, read back OUT of an already-adjusted row. The
 * adjustment is stored as a full declaration (so graph-service enforces it), but the
 * sheet edits a patch — each control has to know whether THIS type moved the field or
 * is showing the ancestor's, and only the diff against `adjusted_from` answers that.
 */
function relOverrideFromRow(relationship: SchemaRelationship): RelationshipOverridePatch | null {
  const base = relationship.adjusted_from;
  if (!base) return null;
  const baseIds = relBaseTargetIds(relationship);
  const effIds = (() => {
    const scope = deriveApplicability(relationship.target_filter);
    if (scope.applicabilityMode !== 'specific') return baseIds;
    return [...(scope.applicabilityFamilies ?? []), ...(scope.applicabilityTypes ?? [])];
  })();
  const baseProps = new Set((base.target_filter?.propertyFilters ?? []).map((f) => JSON.stringify(f)));
  // Anything on the effective rule that the ancestor did not author and that is not one
  // of the narrowed list axes is a condition this type ANDed on.
  const extraFilters = (relationship.target_filter?.propertyFilters ?? []).filter(
    (f) => !REL_TARGET_LIST_PROPERTIES.includes(f.property) && !baseProps.has(JSON.stringify(f))
  );
  return {
    name: relationship.name,
    description: relationship.description ?? null,
    reverse_label: relationship.reverse_label ?? null,
    cardinality: relationship.cardinality,
    required: !!relationship.required,
    keep: baseIds.length && effIds.length !== baseIds.length ? effIds : null,
    scope: baseIds.length === 0 ? relationship.target_filter ?? null : null,
    extraFilters,
  };
}

/** The same declared-vs-inherited split as properties, for the perspectives REPLACE array (EAINT-895 Phase 3c). */
function declaredPerspectives(definition: Schema): SchemaPerspective[] {
  return (definition.perspectives ?? []).filter((perspective) => !perspective.is_inherited);
}
/**
 * The merge key for a newly declared perspective, derived from the type's system name and the
 * label rather than typed by an author — a UUID would not be portable across environments, and
 * a free-typed key can collide with an ancestor's by accident (`type-perspectives.jsx:11-14`).
 */
function perspectiveSystemName(typeSystemName: string, label: string): string {
  return [typeSystemName, label].map(machineKey).filter(Boolean).join('_');
}

/** What a perspective is before anything is authored onto it (`type-perspectives.jsx:15-16`). */
const BLANK_PERSPECTIVE: SchemaPerspective = {
  name: '',
  label: '',
  required: false,
  cardinality: 'single',
  description: '',
  permissions: [],
  system_name: '',
  metaspace_id: '',
  assignee_filter: [],
  permission_system_relation: '',
  is_inherited: false,
  inherited_from_schema: null,
  label_key: '',
  description_key: '',
};

function inheritedPerspectives(definition: Schema): SchemaPerspective[] {
  return (definition.perspectives ?? []).filter((perspective) => !!perspective.is_inherited);
}

/** The fixed `PropertyDataType` vocabulary (types/schema.d.ts). `type` stays untranslated by policy — see the localization note in the spec. */
const PROPERTY_TYPE_OPTIONS = ['string', 'number', 'boolean', 'date', 'datetime', 'array', 'json', 'unit', 'reference'].map((value) => ({
  label: value,
  value,
}));

/**
 * Resolves `inherited_from_schema` — a `schema_id` on the wire (B7) — to the
 * ancestor's display name via the lineage walk the caller already performed.
 * Never returns the raw id: an ancestor absent from the walked chain (denied,
 * missing, or beyond `LINEAGE_MAX_DEPTH`) falls back to the same
 * unknown-ancestor label a truncated chain already uses, so a denied hop and
 * a missing one render identically (spec `provenanceFallsBackWhenAncestorUnviewable`).
 */
export function resolveAncestorName(
  schemaId: string | null | undefined,
  lineage: LineageResult | null,
  localize: Localize,
  t: (key: string) => string
): string | undefined {
  if (!schemaId) return undefined;
  const ancestor = lineage?.ancestors.find((candidate) => candidate.schemaId === schemaId);
  return ancestor ? localize(ancestor.nameKey, ancestor.name) : t('studio.typeEditor.row.unknownAncestor');
}

/**
 * One row of the Inherited tab: something this type runs or carries that it did not declare,
 * and the ancestor it arrives from. `type-composer.jsx:1900-1906` frames the panel as
 * "everything below arrives from the lineage… it just isn't yours to edit here", so a row's
 * whole job is to name the thing and name its origin — there is no affordance on it.
 */
export interface InheritedFact {
  kind: 'aspect' | 'arc';
  /** `systemName` / `templateSystemName` — also the row's stable key and testid suffix. */
  id: string;
  /** The ancestor `schema_id` the row arrives from, unresolved. */
  fromSchemaId: string | null;
}

/**
 * The facts the Inherited panel holds — inherited composed aspects and inherited default
 * arcs. Deliberately NOT every inherited thing the type has: inherited properties,
 * relationships, rules and perspectives each render attributed inside the block that owns
 * them, and listing them twice would make the tab's count name other blocks
 * (`type-composer.jsx:576` — "a count that names other blocks is a count that lies about
 * the tab it sits on").
 */
export function inheritedFacts(definition: Schema | null): InheritedFact[] {
  if (!definition) return [];
  return [
    ...(definition.aspects ?? [])
      .filter((aspect) => aspect.is_inherited)
      .map<InheritedFact>((aspect) => ({
        kind: 'aspect',
        id: aspect.systemName,
        fromSchemaId: aspect.inherited_from_schema ?? null,
      })),
    ...(definition.defaultArcs ?? [])
      .filter((arc) => arc.is_inherited)
      .map<InheritedFact>((arc) => ({
        kind: 'arc',
        id: arc.templateSystemName,
        fromSchemaId: arc.inherited_from_schema ?? null,
      })),
  ];
}

/**
 * A `propose_type_edit` ghost awaiting review (EAINT-895 Phase 5), landed in this organism's
 * state. `fields` is the agent's original proposal, never mutated — `edited` carries only the
 * keys the user has hand-changed since, so `editingAGhostByHandKeepsIt` holds even across a
 * re-render: the effective value for a key is `edited[key] ?? fields[key]`, and nothing here ever
 * resets `edited` except discarding the ghost outright. Keeping a ghost REPLACE-saves
 * `{ ...fields, ...edited }` through the exact same `onSaveField` path every hand-edited field
 * already uses — there is no separate "ghost save" route.
 */
interface PendingGhost {
  id: string;
  sectionId: string;
  rationale: string;
  fields: Record<string, unknown>;
  edited: Record<string, unknown>;
  /** The turn that produced it, stamped by the thread. Absent for a ghost that reached this
   * organism without one (a seeded story), in which case its bar states no origin rather than
   * inventing one (`/standards/agentic/agentic-ux-standards.md` Rule 7). */
  provenance?: ComposerGhostProvenance;
  /** `'keeping'` disables this ghost's own Keep button while its save is in flight, so a second
   * click cannot issue a second `update-schema` call (`keptGhostAppliesExactlyOnce`). */
  status: 'pending' | 'keeping';
}

function ghostEffectiveValue(ghost: PendingGhost, key: string): unknown {
  return key in ghost.edited ? ghost.edited[key] : ghost.fields[key];
}

/**
 * Maps one field's current value to a real, editable control — generically, since a ghost's
 * `fields` keys are whatever the agent proposed (any of `update-schema`'s accepted body keys),
 * not a fixed per-section shape. A boolean edits as a switch; every REPLACE array/object edits as
 * its pretty-printed JSON, which is honest about what will actually be sent (the same
 * REPLACE-shaped payload `onSaveField` always REPLACEs the own set with) rather than inventing a
 * bespoke per-field diff UI this phase's scope doesn't call for. Invalid JSON while typing is
 * simply not applied to `edited` — the last valid parse stands until the text becomes valid again.
 */
function ghostControlFor(key: string, value: unknown, ariaLabel: string, onChange: (next: unknown) => void): TypeDefinitionRowControl {
  if (typeof value === 'boolean') {
    return { kind: 'switch', checked: value, ariaLabel, onChange: (checked) => onChange(checked) };
  }
  if (typeof value === 'string') {
    return { kind: 'text', value, ariaLabel, onChange: (next) => onChange(next) };
  }
  const text = JSON.stringify(value, null, 2) ?? '';
  return {
    kind: 'multiline',
    value: text,
    ariaLabel,
    onChange: (next) => {
      try {
        onChange(JSON.parse(next));
      } catch {
        // Invalid JSON mid-edit — the effective value stays whatever last parsed successfully.
      }
    },
  };
}

/**
 * One row per field the ghost proposes, each carrying the ghost's rationale. The rows show and
 * edit what was proposed; they do NOT carry their own Keep/Discard. A ghost's fields are saved
 * together in one `onSaveField` call, so a per-row pair was N copies of one decision - the
 * contract puts that decision once, in the block's ghost bar (`type-composer.jsx:694-699`), and
 * one primary action per proposal is also what `/standards/frontend/ux-craft.md` I2 asks for.
 */
function renderGhostRows(
  ghost: PendingGhost,
  t: (key: string, params?: Record<string, unknown>) => string,
  onFieldEdit: (ghostId: string, key: string, value: unknown) => void,
  onGhostEscape: () => void
): React.ReactNode {
  return Object.keys(ghost.fields).map((key) => {
    const ariaLabel = t('studio.typeEditor.composer.ghost.fieldLabel', { field: key });
    const value = ghostEffectiveValue(ghost, key);
    return (
      <TypeFieldRowMolecule
        key={`${ghost.id}-${key}`}
        data-testid={`type-ghost-row-${ghost.id}-${key}`}
        label={ariaLabel}
        mode="ghost"
        controls={[ghostControlFor(key, value, ariaLabel, (next) => onFieldEdit(ghost.id, key, next))]}
        ghostRationale={ghost.rationale}
        onGhostEscape={onGhostEscape}
      />
    );
  });
}

/**
 * The contract's `ghost-bar` leaf and its Keep button, per implementation section
 * (`type-composer.jsx:694-699` arcs, `:1351-1356` compose, `:740-745` health). A section with no
 * entry renders the bar unannotated - the proposal is still answerable, it just isn't a datum the
 * contract names. `health` is a waived block, so it has no row here.
 */
const SECTION_GHOST_BAR_DATUM: Partial<Record<string, { message: string; keep: string }>> = {
  aspects: { message: 'compose.3-attachments-and-their-answers-are-prop', keep: 'compose.keep-them' },
  arcs: { message: 'arcs.renewal-proposed-as-a-second-arc', keep: 'arcs.keep-it' },
};

/**
 * The bar's sentence and its Keep label, derived from what the proposal actually changes rather
 * than from the section alone - the prototype hard-codes both because nothing in it ever proposes
 * anything (`type-composer.jsx:695`, `:1354`), so the rule is ours to state.
 *
 * The subject count decides the Keep label ("Keep it" for one, "Keep them" for several), which is
 * why the two are computed together: a bar that says three things were proposed and then offers
 * "Keep it" is reading from two different facts.
 */
function ghostBarCopy(
  ghost: PendingGhost,
  arcTemplates: ArcTemplateOption[],
  definition: Schema,
  t: (key: string, params?: Record<string, unknown>) => string
): { message: string; keepLabel: string } {
  const keep = (count: number) => (count === 1 ? t('studio.typeEditor.composer.ghost.keepIt') : t('studio.typeEditor.composer.ghost.keepThem'));

  if (ghost.sectionId === 'aspects') {
    const proposed = ghostEffectiveValue(ghost, 'aspects');
    const count = Array.isArray(proposed) ? proposed.length : Object.keys(ghost.fields).length;
    return { message: t('studio.typeEditor.composer.ghost.bar.aspects', { count }), keepLabel: keep(count) };
  }

  if (ghost.sectionId === 'arcs') {
    const proposed = ghostEffectiveValue(ghost, 'defaultArcs');
    const rows = Array.isArray(proposed) ? (proposed as { templateSystemName?: string }[]) : [];
    const existing = new Set((definition.defaultArcs ?? []).map((arc) => arc.templateSystemName));
    const added = rows.filter((row) => row.templateSystemName && !existing.has(row.templateSystemName));
    const first = added[0] ?? rows[0];
    const name =
      arcTemplates.find((template) => template.systemName === first?.templateSystemName)?.name ??
      first?.templateSystemName ??
      t('studio.typeEditor.arcs.unknownTemplate');
    // "a second arc" is only true when one already stands; with none, the proposal IS the type's
    // arc and saying otherwise would misdescribe the change the Keep button applies.
    const message =
      rows.length > 1 ? t('studio.typeEditor.composer.ghost.bar.arcsAdditional', { name }) : t('studio.typeEditor.composer.ghost.bar.arcsOnly', { name });
    return { message, keepLabel: keep(Math.max(added.length, 1)) };
  }

  const count = Object.keys(ghost.fields).length;
  return { message: t('studio.typeEditor.composer.ghost.bar.generic', { count }), keepLabel: keep(count) };
}

/**
 * One property row, as the contract states it (`type-composer.jsx:1436-1470`): a
 * summary line — name, type, the flags that change what can be done to a value,
 * where it comes from. Summary-only, always: whether a row is an aspect's,
 * inherited from an ancestor, or declared directly on the type (legacy), it says
 * where it CAN be changed instead of offering a control — `OwnPropertiesSection`'s
 * own editor (`Configure`, opened body, add/delete/undo) went with that section
 * (plan Phase 3a); a legacy row is now changed by composing an aspect for it.
 */
/**
 * The contract datum each of `PropertySummaryRow`'s leaves renders. Defaults to the
 * (now dead — the `props` block does not exist in the `sphere-type-editor-rail`
 * contract) ids `OwnPropertiesSection` always used, so that caller keeps rendering
 * unchanged; the merged property summary inside Aspects (`compose > group > each(…) >
 * group > list > each(rows) > list-item > group`, `compose.p-label`.. `compose.on-the-
 * aspect`) passes its own `compose.*` ids explicitly instead of taking the default.
 */
interface PropertySummaryRowDatums {
  label: string;
  typeLabel: string;
  note: string;
  unit: string;
  required: string;
  readOnly: string;
  fromAspect: string;
  inherited: string;
  /** The "declared inline before properties became aspects" state — no default; only the merged summary renders it. */
  legacy?: string;
  onAspect: string;
  /** Translation key for the `fromAspect` chip's `title`. Defaults to `properties.fromAspectChip`. */
  fromAspectTitleKey?: string;
}

const OWN_PROPERTIES_ROW_DATUMS: PropertySummaryRowDatums = {
  label: 'props.new-property',
  typeLabel: 'props.p-typelabel',
  note: 'props.p-note',
  unit: 'props.p-unit',
  required: 'props.required',
  readOnly: 'props.engine-written',
  fromAspect: 'props.contributed-by-a-composed-aspect-edit-it',
  inherited: 'props.from',
  onAspect: 'props.on-the-aspect',
};

function PropertySummaryRow({
  property,
  t,
  localize,
  lineage,
  onOpenAspect,
  aspectLabel,
  datums = OWN_PROPERTIES_ROW_DATUMS,
}: {
  property: SchemaProperty;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  lineage: LineageResult | null;
  /** Jumps to the aspect that contributes this property — where it CAN be edited. */
  onOpenAspect?: () => void;
  /**
   * What the contributing aspect is CALLED (`{p.fromAspect}` is `r.name`,
   * `type-composer.jsx:565`) — resolved by the section, which has the catalog; the row
   * has only the system name and would otherwise print it.
   */
  aspectLabel?: string;
  /** @see PropertySummaryRowDatums. Defaults to the (dead) `props.*` ids. */
  datums?: PropertySummaryRowDatums;
}): React.ReactElement {
  const name = localize(property.label_key, property.label || property.name);
  const own = !property.is_inherited && !property.aspect_system_name;
  const note = localize(property.description_key, property.description ?? '');
  const unit = property.unitConfig?.defaultUnit;
  // What the row says about a property nobody has described: its checks, else its
  // choices (`type-composer.jsx:1447` — `p.note || p.rulesText || p.values`).
  const choices = ((property.metadata?.options ?? []) as { label?: string; value?: string }[])
    .map((option) => option.label ?? option.value ?? '')
    .filter(Boolean)
    .join(' · ');
  const inline = note.trim() || describePropertyRules(property.rules, t) || choices;
  return (
    <div
      className="sphere-type-editor__property-row"
      data-container="list > each(rows) > list-item"
      data-testid={`type-property-row-${property.name || 'draft'}`}
      data-inherited={own ? undefined : '1'}
    >
      <div className="sphere-type-editor__property-row-top">
        <span className="sphere-type-editor__property-name" data-datum={datums.label} data-kind="label">
          {name || t('studio.typeEditor.properties.newProperty')}
        </span>
        {/*
          What the type is called (`{p.typeLabel}`, `type-composer.jsx:1444`) — the same
          word the open row's Type select uses, so a row and its editor never name one
          thing two ways. The identifier is on `title`: it is what the API and the MCP
          tools say, and an author who writes against them will look for it.
        */}
        <span className="sphere-type-editor__property-type" data-datum={datums.typeLabel} data-kind="chip" title={property.type}>
          {propTypeLabel(property.type, t)}
        </span>
        {/*
          The description rides the row and truncates (`t-prop-inline`,
          `type-composer.jsx:1447`) — on every row, open or closed, so a list of
          properties reads as what each one holds rather than as a list of names.
          The full text is on `title` and in the editor under the row.
        */}
        <span className="sphere-type-editor__property-inline" data-datum={datums.note} data-kind="text" title={inline || undefined}>
          {inline}
        </span>
        {unit && (
          <span className="sphere-type-editor__property-unit" data-datum={datums.unit} data-kind="text">
            {unit}
          </span>
        )}
        {property.required && (
          <span className="sphere-type-editor__property-flag" data-datum={datums.required} data-kind="chip">
            {t('studio.typeEditor.properties.row.required')}
          </span>
        )}
        {property.immutable && (
          <span className="sphere-type-editor__property-flag" title={t('studio.typeEditor.properties.immutableChip')} data-datum="props.once" data-kind="chip">
            {t('studio.typeEditor.properties.row.once')}
          </span>
        )}
        {property.unique && (
          <span className="sphere-type-editor__property-unit" data-datum="props.unique" data-kind="text">
            {t('studio.typeEditor.properties.row.unique')}
          </span>
        )}
        {property.read_only && (
          <span className="sphere-type-editor__property-lock" data-datum={datums.readOnly} data-kind="lock-chip">
            <i className="pi pi-lock" aria-hidden="true" />
            {t('studio.typeEditor.properties.row.engineWritten')}
          </span>
        )}
        {property.aspect_system_name && (
          <span
            className="sphere-type-editor__property-provenance"
            title={t(datums.fromAspectTitleKey ?? 'studio.typeEditor.properties.fromAspectChip')}
            data-datum={datums.fromAspect}
            data-kind="note"
          >
            <i className="pi pi-th-large" aria-hidden="true" />
            {aspectLabel ?? property.aspect_system_name}
          </span>
        )}
        {property.is_inherited && (
          <span className="sphere-type-editor__property-provenance" data-testid="type-definition-row-provenance" data-datum={datums.inherited} data-kind="note">
            <i className="pi pi-lock" aria-hidden="true" />
            {t('studio.typeEditor.properties.row.fromAncestor', {
              schema: resolveAncestorName(property.inherited_from_schema, lineage, localize, t) ?? t('studio.typeEditor.row.unknownAncestor'),
            })}
          </span>
        )}
        {/*
          "Declared inline before properties became aspects" — the merged summary's
          third, legacy group (`compose.legacy`, `p.legacy+p.inherited+p.fromAspect
          =yes+no+no`): a property that is neither inherited nor an aspect's is, in this
          reduced contract, exactly `own`. The aspect and inherited groups pass no
          `datums.legacy`, so they render nothing here; the aspect-contributed row list
          under each aspect's own summary (`contributed.map`) passes no explicit datums
          at all and so takes the (dead) `props.*` default, which also carries none.
        */}
        {own && datums.legacy && (
          <span
            className="sphere-type-editor__property-provenance"
            title={t('studio.typeEditor.properties.legacyChip')}
            data-datum={datums.legacy}
            data-kind="note"
          >
            <i className="pi pi-history" aria-hidden="true" />
            {t('studio.typeEditor.properties.row.legacy')}
          </span>
        )}
        {property.aspect_system_name && onOpenAspect && (
          <ButtonAtom
            variant="text"
            size="small"
            icon="pi pi-external-link"
            label={t('studio.typeEditor.properties.row.onTheAspect')}
            onClick={onOpenAspect}
            data-testid={`type-property-on-the-aspect-${property.name}`}
            data-datum={datums.onAspect}
            data-kind="button"
          />
        )}
      </div>
    </div>
  );
}

/**
 * The summary-only reading of a property row — inherited from an ancestor, or
 * contributed by a composed aspect. Neither is configurable here, and the row
 * says where it can be changed instead of offering a control that refuses.
 */
function propertyRow(
  property: SchemaProperty,
  t: (key: string, params?: Record<string, unknown>) => string,
  localize: Localize,
  { onOpenAspect, aspectLabel, datums }: { onOpenAspect?: () => void; aspectLabel?: string; datums?: PropertySummaryRowDatums } = {},
  lineage: LineageResult | null = null
): React.ReactElement {
  return (
    <PropertySummaryRow
      key={property.name}
      property={property}
      t={t}
      localize={localize}
      aspectLabel={aspectLabel}
      lineage={lineage}
      onOpenAspect={onOpenAspect}
      datums={datums}
    />
  );
}

/**
 * What an attached aspect is CALLED. The per-type override wins; otherwise the catalog's
 * own `name` (a publisher-authored `LocalizedString`, so `getLocalizedText`, not `t`).
 * `system_name` is the last resort and means the catalog has not arrived — it is an
 * identifier, and it was reaching the row title, the detach confirm and the undo notice.
 */
const aspectDisplayName = (aspect: { systemName: string; overrides?: { label?: string } | null }, catalogByName: Map<string, AspectRow>): string =>
  aspect.overrides?.label || getLocalizedText(catalogByName.get(aspect.systemName)?.name, '') || aspect.systemName;

/**
 * The contract's `ident` datum ids, threaded into the glyph picker so it can
 * self-describe. The picker serves Placement's new-family mark too, under a
 * different set of ids — which is why they are a prop rather than baked in.
 */
const IDENTITY_GLYPH_DATUMS: GlyphPickerDatums = {
  tile: 'ident.tile',
  mark: 'ident.glyph#2',
  markText: 'ident.glyph-text',
  hint: 'ident.the-type-s-face-in-every-list-panel-and-',
  preview: 'ident.text',
  previewMark: 'ident.glyph#3',
  tab: 'ident.label',
  search: 'ident.search-icons',
  iconResult: 'ident.button#2',
  noHits: 'ident.nothing-matches',
  iconNote: 'ident.a-letter-always-works-it-needs-no-icon-a',
  letterHint: 'ident.a-letter-is-the-fallback-when-no-icon-sa',
  letterInput: 'ident.v',
  useIcon: 'ident.use-an-icon-instead',
  themeLabel: 'ident.theme-colour',
  swatch: 'ident.button#3',
  hueLabel: 'ident.brand-hue',
  hueSlider: 'ident.input',
  hueValue: 'ident.not-in-use',
  exactLabel: 'ident.exact-colour',
  hexInput: 'ident.26a3e3',
  backToToken: 'ident.back-to-a-theme-colour',
  pickedForYou: 'ident.picked-for-you',
  overridesDefault: 'ident.overrides-the-default',
  backToDefault: 'ident.back-to-default',
};

/**
 * The `place.picker#2` (`namingFamily`) branch's glyph mark — the same picker,
 * a different set of contract ids (`sphere-type-editor-rail.md:471-475`). Only
 * the five leaves the contract actually enumerates for this branch are named;
 * the rest of the picker's internal grid (search/tabs/hue) is the same UI
 * `ident` already covers and carries no distinct `place.*` id here.
 */
const PLACEMENT_NEW_FAMILY_GLYPH_DATUMS: GlyphPickerDatums = {
  tile: 'place.tile#3',
  markText: 'place.glyph-text',
  pickedForYou: 'place.picked-for-you',
  overridesDefault: 'place.overrides-the-default',
  backToDefault: 'place.back-to-default',
};

/**
 * The three fields the contract's `ident` block actually declares as editable
 * (glyph, name, description) plus `useCaseTags`. `systemName`, `version`,
 * `is_abstract`, `sealed` and `is_hidden` have no row in `ident` at all — the
 * header's breadcrumb chips are their only rendering (`header.abstract`,
 * `.sealed`, `.hidden`, `.t-version`); duplicating them here as locked/switch
 * rows was extra structure the contract's tree diff does not call for.
 *
 * `readOnly` is a DIFFERENT tree, not the same tree switched off
 * (`type-composer.jsx:892-902`): a platform-owned type shows its name with a
 * `platform-named` lock chip and its description as a paragraph. Disabled
 * inputs would offer an authoring affordance that does not exist here at all.
 */
function IdentitySection({
  definition,
  t,
  localize,
  locked,
  onSaveField,
  conflict,
  onOpenSection,
}: {
  definition: Schema;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  conflict?: boolean;
  /** Focuses another section — the translations chip opens Languages, as the prototype's does. */
  onOpenSection?: (sectionId: string) => void;
}) {
  const editable = !locked && !!onSaveField;
  // The read-only TREE is chosen by the LOCK, not by whether a save handler
  // happens to be wired: an editable type whose caller passes no `onSaveField`
  // is still an editable type, and must render its form with the controls
  // switched off — not the platform readback, which claims a provenance it
  // does not have.
  const fieldMode = editable ? 'edit' : 'disabled';

  const [name, setName] = useState(() => localize(definition.localization_name_key, definition.name));
  const [description, setDescription] = useState(() => localize(definition.localization_description_key, definition.description || ''));
  const [useCaseTags, setUseCaseTags] = useState(() => definition.use_case_tags);
  const [glyph, setGlyph] = useState<string | null>(() => definition.glyph ?? null);
  const [glyphColor, setGlyphColor] = useState<GlyphColor | null>(() => definition.glyph_color ?? null);
  const [addingTag, setAddingTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const fieldIdBase = React.useId();
  const glyphLabelId = `${fieldIdBase}-glyph`;
  const tagsLabelId = `${fieldIdBase}-tags`;

  // Re-baselines on the identity actually changing (a different type loaded,
  // or a successful save moved `version_id` forward) — never in response to
  // `conflict`, which must leave whatever the caller already typed alone
  // (spec `staleEditSurfacesConflictNotClobber`).
  useEffect(() => {
    setName(localize(definition.localization_name_key, definition.name));
    setDescription(localize(definition.localization_description_key, definition.description || ''));
    setUseCaseTags(definition.use_case_tags);
    setGlyph(definition.glyph ?? null);
    setGlyphColor(definition.glyph_color ?? null);
    setAddingTag(false);
    setNewTag('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.system_name, definition.version_id]);

  const commitTags = (values: string[]) => {
    setUseCaseTags(values);
    onSaveField?.({ useCaseTags: values });
  };

  if (locked) {
    return (
      <div className="sphere-type-editor__identity" data-testid="sphere-type-identity">
        {/* `ident > list > list-item` (`type-composer.jsx:891-900`): the platform readback is one row of a row list. */}
        <div className="sphere-type-editor__identity-readback" data-container="identity > list">
          <div className="sphere-type-editor__identity-readback-item" data-container="identity > list > list-item">
            <div className="sphere-type-editor__identity-readback-top">
              <span className="sphere-type-editor__identity-readback-name" data-datum="ident.t-name" data-kind="label">
                {name || definition.name}
              </span>
              <TagAtom
                tone="outline"
                size="small"
                icon="pi-lock"
                label={t('studio.typeEditor.identity.platformNamed')}
                data-datum="ident.platform-named"
                data-kind="lock-chip"
                data-testid="sphere-type-identity-platform-named"
              />
            </div>
            <p
              className="sphere-type-editor__identity-readback-desc"
              data-datum="ident.no-description-on-the-platform-definitio"
              data-kind="text"
              data-empty={description.trim() ? undefined : '1'}
              data-testid="sphere-type-identity-no-description-note"
            >
              {description.trim() || t('studio.typeEditor.identity.noDescriptionPlatform')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sphere-type-editor__identity" data-testid="sphere-type-identity">
      {conflict && <MessageAtom severity="warn" data-testid="sphere-type-identity-conflict" text={<p>{t('studio.typeEditor.composer.conflictBanner')}</p>} />}
      {/*
        The mark sits beside the name it marks — you read them together
        (`type-composer.jsx:903-918`, the `t-identrow` / `t-ident-glyph` pair).
      */}
      <div className="sphere-type-editor__identity-row">
        <div
          className="sphere-type-editor__identity-glyph-field"
          data-testid="sphere-type-identity-glyph"
          data-datum="ident.glyph"
          data-kind="field"
          data-layout="stacked"
          role="group"
          aria-labelledby={glyphLabelId}
        >
          <span className="sphere-type-editor__identity-glyph-label" id={glyphLabelId}>
            {t('studio.typeEditor.identity.glyph')}
          </span>
          <GlyphPickerMolecule
            glyph={glyph}
            glyphColor={glyphColor}
            fallbackText={name || definition.name || '?'}
            hideHint
            tileSize={44}
            datums={IDENTITY_GLYPH_DATUMS}
            onChange={(next) => {
              setGlyph(next.glyph);
              setGlyphColor(next.glyphColor);
              onSaveField?.({ glyph: next.glyph ?? '', glyphColor: next.glyphColor });
            }}
          />
        </div>
        <div className="sphere-type-editor__identity-fields">
          <TypeFieldRowMolecule
            datum="ident.name"
            layout="stacked"
            label={t('studio.typeEditor.identity.name')}
            mode={fieldMode}
            controls={[
              {
                kind: 'text',
                value: name,
                placeholder: t('studio.typeEditor.identity.namePlaceholder'),
                ariaLabel: t('studio.typeEditor.identity.name'),
                grow: true,
                onChange: setName,
                onCommit: () => onSaveField?.({ name }),
              },
            ]}
            hintDatum="ident.hint"
            hint={
              /*
                Translation coverage is authored where the string is authored
                (`type-composer.jsx:917`, `LocChip`) — the chip states how much of
                this name is translated and opens Languages, rather than making
                the author go looking for a separate screen.
              */
              onOpenSection ? (
                <TranslationCoverageChipAtom
                  done={0}
                  total={name.trim() ? 1 : 0}
                  label={name.trim() ? t('studio.typeEditor.identity.translations', { name }) : t('studio.typeEditor.identity.translationsNone')}
                  onClick={() => onOpenSection('languages')}
                  data-datum="ident.chip"
                  data-testid="sphere-type-identity-translations"
                />
              ) : undefined
            }
          />
          <TypeFieldRowMolecule
            datum="ident.description"
            layout="stacked"
            label={t('studio.typeEditor.identity.description')}
            mode={fieldMode}
            controls={[
              {
                kind: 'multiline',
                value: description,
                placeholder: t('studio.typeEditor.identity.descriptionPlaceholder'),
                ariaLabel: t('studio.typeEditor.identity.description'),
                grow: true,
                onChange: setDescription,
                onCommit: () => onSaveField?.({ description }),
              },
            ]}
          />
          {/* Findability is part of what a type is called, not an advanced setting. */}
          {/*
            A group, not a labelled field: there is no single control to hang a
            `<label>` off (a tag list is n chips plus an add action), so the
            visible name is tied to the group with `aria-labelledby` instead of
            a `<label for>` pointing at nothing.
          */}
          <div
            className="sphere-type-editor__identity-tags-field"
            data-testid="sphere-type-identity-tags"
            data-datum="ident.use-case-tags"
            data-kind="chips"
            data-layout="stacked"
            role="group"
            aria-labelledby={tagsLabelId}
          >
            <span className="sphere-type-editor__identity-glyph-label" id={tagsLabelId}>
              {t('studio.typeEditor.identity.useCaseTags')}
            </span>
            <span className="sphere-type-editor__tag-list" data-container="chips">
              {useCaseTags.map((tag) => (
                <span key={tag} className="sphere-type-editor__identity-tag" data-container="chips > each(tags)" data-datum="ident.tg" data-kind="chip">
                  {tag}
                  <button
                    type="button"
                    className="sphere-type-editor__identity-tag-remove"
                    aria-label={t('studio.typeEditor.identity.removeTag', { tag })}
                    data-datum="ident.button"
                    disabled={!editable}
                    onClick={() => commitTags(useCaseTags.filter((x) => x !== tag))}
                  >
                    <i className="pi pi-times" aria-hidden="true" />
                  </button>
                </span>
              ))}
              {addingTag ? (
                <InputAtom
                  className="sphere-type-editor__identity-tag-input"
                  autoFocus
                  value={newTag}
                  aria-label={t('studio.typeEditor.identity.addTag')}
                  placeholder={t('studio.typeEditor.identity.addTag')}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setAddingTag(false);
                      setNewTag('');
                    }
                    if (e.key !== 'Enter') return;
                    const value = newTag.trim();
                    if (value && !useCaseTags.includes(value)) commitTags([...useCaseTags, value]);
                    setNewTag('');
                    setAddingTag(false);
                  }}
                  onBlur={() => {
                    const value = newTag.trim();
                    if (value && !useCaseTags.includes(value)) commitTags([...useCaseTags, value]);
                    setNewTag('');
                    setAddingTag(false);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="sphere-type-editor__identity-add-tag"
                  data-datum="ident.add-tag"
                  data-testid="sphere-type-identity-add-tag"
                  disabled={!editable}
                  onClick={() => setAddingTag(true)}
                >
                  <i className="pi pi-plus" aria-hidden="true" />
                  {t('studio.typeEditor.identity.addTag')}
                </button>
              )}
            </span>
            <span
              className="sphere-type-editor__identity-hint"
              data-datum="ident.how-this-type-is-found-in-the-gallery-an"
              data-kind="hint"
              data-testid="sphere-type-identity-tags-hint"
            >
              {t('studio.typeEditor.identity.useCaseTagsHint')}
            </span>
          </div>
        </div>
      </div>
      {/*
        `type-composer.jsx:941-944` — the nudge only exists when it applies. The
        platform variant of this sentence lives in the read-only tree above,
        because telling a reader to add a description they may not add is worse
        than saying nothing.
      */}
      {!description.trim() && (
        <p
          className="sphere-type-editor__identity-note"
          data-datum="ident.a-type-without-a-description-is-guessed-"
          data-kind="note"
          data-testid="sphere-type-identity-no-description-note"
        >
          {t('studio.typeEditor.identity.noDescriptionNote')}
        </p>
      )}
    </div>
  );
}

/**
 * One row of the parent picker (`place.picker`, `type-composer.jsx:1073-1112`) — a
 * candidate the type could inherit from instead. The caller supplies the list already
 * filtered to what is actually offerable (no sealed types, nothing below this type, not
 * this type itself); this component only renders what it is given.
 */
export interface ParentOption {
  schemaId: string;
  name: string;
  description?: string | null;
  isAbstract: boolean;
  glyph?: string | null;
  glyphColor?: GlyphColor | null;
  /** This option is the type's current parent — the row that names "where it is now". */
  isCurrent?: boolean;
  /** A platform-owned/sealed type — the row that carries the padlocked "Platform" pill. */
  isPlatform?: boolean;
  /** What choosing this brings that isn't already the type's own. Empty reads "brings nothing of its own". */
  brings?: string;
  /** A caution line under the row — e.g. moving under a type other things already inherit from. */
  caution?: string | null;
}

/**
 * `type-composer.jsx:950-1160`. Placement answers two questions the four old
 * outcome buttons ran together: what does this type inherit from, and are
 * spheres ever made OF it. The parent card names the family and offers the
 * manifest; the choice cards read out the second answer.
 *
 * Re-parenting the type — actually writing a new `parent_schema_id` — calls `update-schema`,
 * which accepts `parent` (EAINT-928 P8, `updateSchema.ts:302-306`); wiring the confirm flow to
 * that write is Phase 3's job. This section owns the picker's skeleton and hands the chosen
 * option to `onMoveParent`;
 * absent that prop, same reasoning as `onDraftAspect`, `Move it` doesn't render at all.
 */
function PlacementSection({
  definition,
  lineage,
  t,
  localize,
  locked,
  parentOptions,
  onMoveParent,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  parentOptions?: ParentOption[];
  onMoveParent?: (option: ParentOption) => void;
}) {
  const parent = lineage?.ancestors[0] ?? null;
  const parentName = parent ? localize(parent.nameKey, parent.name) : null;
  /*
    `place.member` (`trail-composer.jsx:1056`) — how many OTHER types already sit under this
    same parent. The prototype counts it off its whole in-memory type list; this workspace has
    no such list, so it reads the org-viewable catalog (`GET /schemas/org-available-types`, the
    same route the relationships sheet's applicability editor already reads) and counts siblings
    client-side — view-filtered, same as the prototype counting only what it can see. Fetched
    only once a parent exists, since a root type has no siblings to report.
  */
  const apiClients = useApiClientsOrNull();
  const { schemas: orgTypeSchemas } = useOrgAvailableTypes(parent ? apiClients : null);
  const memberCount = useMemo(() => {
    if (!parent) return 0;
    return orgTypeSchemas.filter(
      (s) => s.parent_schema_id === parent.schemaId && s.schema_id !== definition.schema_id
    ).length;
  }, [orgTypeSchemas, parent, definition.schema_id]);
  // `locked` is derived once, from `canEdit`, at the organism root (D8) — a
  // second read here could disagree with it on a malformed/unknown `canEdit`.
  const editable = !locked;
  /**
   * The parent picker (`place.picker`). Closed by default — it opens only from `Move it`
   * / `Change`, never on load, so a settled parent never shows the browser it took to
   * choose it.
   */
  const [browsingParent, setBrowsingParent] = useState(false);
  const [parentQuery, setParentQuery] = useState('');
  const canMove = !!onMoveParent;
  /**
   * The manifest is the acknowledgement at the moment of choosing, so it is
   * asked for — it does not sit open under a settled parent restating what the
   * author already accepted.
   */
  const [manifestAsked, setManifestAsked] = useState(false);
  /**
   * Read once, stays read — cleared whenever a NEW parent choice needs its own
   * acknowledgement (`trail-composer.jsx:1086`). A type still being drafted
   * (`definition.version_is_draft`) shows the manifest unread-by-default, the
   * same way choosing a parent for the first time does — nothing has been
   * acknowledged yet.
   */
  const [manifestRead, setManifestRead] = useState(false);
  /**
   * `namingFamily` (`place.picker#2`, `trail-composer.jsx:1091,1161`) — offered only
   * from inside the parent browser, since naming a new family is an alternative
   * answer to "what does it inherit from", not a separate action. This section owns
   * the form's local state only; creating the family schema itself is a bigger act
   * than `update-schema` accepting `parent` (Phase 3) — it needs a new schema to
   * exist before anything can be re-parented under it, so there is no submit here yet.
   */
  const [namingFamily, setNamingFamily] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyDesc, setNewFamilyDesc] = useState('');
  const [newFamilyGlyph, setNewFamilyGlyph] = useState<string | null>(null);
  const [newFamilyGlyphColor, setNewFamilyGlyphColor] = useState<GlyphColor | null>(null);
  /**
   * Move-it impact (`place.callout`, `trail-composer.jsx:1116-1141`) — re-parenting a live
   * type changes what its spheres inherit, so choosing a candidate states the change rather
   * than committing silently. Selecting a candidate calls `onMoveParent` and stands as the
   * choice (D8: no write is wired yet — the callback commits nothing on its own here); the
   * only way back is `Keep it under {parent}`, which calls `onMoveParent` with the option
   * moved away from.
   */
  const [movedFrom, setMovedFrom] = useState<ParentOption | null>(null);
  const [movedTo, setMovedTo] = useState<ParentOption | null>(null);
  /** Closing the browser abandons any family being named — reopening it starts fresh, same as the prototype's `setNamingFamily(false)` never being called on its own. */
  const closeParentBrowser = () => {
    setBrowsingParent(false);
    setNamingFamily(false);
    setNewFamilyName('');
    setNewFamilyDesc('');
    setNewFamilyGlyph(null);
    setNewFamilyGlyphColor(null);
  };

  const inheritedProperties = definition.properties.filter((p) => p.is_inherited);
  /**
   * The manifest's aspects/perspectives/relationships/arcs rows are a walk of the WHOLE
   * ancestor chain (`lineage.ancestors`, nearest parent first), each contributing only its
   * OWN declared items (`LineageAncestor.own*` — already filtered to `!is_inherited` per
   * ancestor, so summing every entry gives the chain's total with no double-counting).
   * This is deliberately NOT `definition.aspects`/etc — that array is Vendor's own fully
   * merged, on-type set, which the Aspects/Perspectives/Relationships blocks already render
   * and which differs from what the whole lineage brings (EAINT-933 Phase 1.2/4.1: with one
   * `definition`, the manifest and the blocks could not show different totals until this
   * split existed). `properties` is NOT part of this walk — the prototype's own manifest
   * never lists properties by ancestor, only these four kinds.
   */
  const ancestors = lineage?.ancestors ?? [];
  const inheritedAspectNames = ancestors.flatMap((a) => a.ownAspects ?? []);
  const perspectiveNames = ancestors.flatMap((a) => a.ownPerspectives ?? []);
  const relationshipNames = ancestors.flatMap((a) => a.ownRelationships ?? []);
  const arcNames = ancestors.flatMap((a) => a.ownArcs ?? []);
  // The parent answers a need for every aspect/perspective/arc it brings — the
  // prototype's "· answers N you'd otherwise set" (`type-composer.jsx:981`).
  const answeredCount = inheritedAspectNames.length + perspectiveNames.length + arcNames.length;

  /**
   * The manifest's rows. Default arcs REPLACE rather than merge (the child's
   * list wins whole), which is the one line here whose semantics differ from
   * the additive rest — so it says so.
   */
  const manifestRows: InheritanceManifestRow[] = [
    {
      kind: 'aspects',
      label: t('studio.typeEditor.placement.manifestRowAspects'),
      items: inheritedAspectNames,
    },
    {
      kind: 'properties',
      label: t('studio.typeEditor.placement.manifestRowProperties'),
      items: inheritedProperties.map((p) => localize(p.label_key, p.label || p.name)),
    },
    {
      kind: 'perspectives',
      label: t('studio.typeEditor.placement.manifestRowPerspectives'),
      items: perspectiveNames,
    },
    {
      kind: 'relationships',
      label: t('studio.typeEditor.placement.manifestRowRelationships'),
      items: relationshipNames,
    },
    { kind: 'arcs', label: t('studio.typeEditor.placement.manifestRowArcs'), items: arcNames, replaces: true },
  ].filter((row) => row.items.length > 0);

  return (
    <div className="sphere-type-editor__placement" data-testid="sphere-type-placement">
      {!editable ? (
        /*
          `type-composer.jsx:952-960` — a DIFFERENT tree, not the editable one
          switched off. The two read-only explanations are a pair: sealed says
          the engine owns the shape, unsealed-but-platform says re-parenting
          would change what every workspace inherits.
        */
        <div className="sphere-type-editor__placement-locked" data-datum="place.note" data-kind="note" data-testid="sphere-type-placement-locked">
          <i className="pi pi-lock" aria-hidden="true" />
          <div>
            <strong data-datum="place.extends" data-kind="strong">
              {parentName ? `${t('studio.typeEditor.placement.extends')} ${parentName}` : t('studio.typeEditor.placement.noParentRoot')}
            </strong>
            <span>{definition.sealed ? t('studio.typeEditor.placement.sealedByMigration') : t('studio.typeEditor.placement.platformNote')}</span>
          </div>
        </div>
      ) : (
        <>
          {parentName && !browsingParent && (
            <div className="sphere-type-editor__parent-card" data-datum="place.card" data-kind="card" data-testid="sphere-type-placement-parent">
              <GlyphAtom
                variant="tile"
                glyph={parent?.glyph}
                glyphColor={parent?.glyphColor ?? undefined}
                fallbackText={parentName}
                aria-hidden
                data-datum="place.tile"
                data-kind="tile"
              />
              <span className="sphere-type-editor__parent-main">
                <span className="sphere-type-editor__parent-top">
                  <strong className="sphere-type-editor__parent-name" data-datum="place.parentname" data-kind="strong">
                    {parentName}
                  </strong>
                  <span className="sphere-type-editor__parent-pill" data-datum="place.a-family" data-kind="pill">
                    {parent?.isAbstract ? t('studio.typeEditor.placement.parentIsFamily') : t('studio.typeEditor.placement.parentMakesSpheres')}
                  </span>
                  {/*
                    Always true here: this composer names the family the type
                    already sits under, and offers no way to browse another.
                  */}
                  <span className="sphere-type-editor__parent-chip" data-datum="place.where-it-is-now" data-kind="chip">
                    {t('studio.typeEditor.placement.whereItIsNow')}
                  </span>
                  {memberCount > 0 && (
                    <span className="a-hint" data-datum="place.member" data-kind="hint">
                      {t('studio.typeEditor.placement.memberCount', { count: memberCount })}
                    </span>
                  )}
                </span>
                <span className="sphere-type-editor__parent-desc" data-datum="place.pp-desc" data-kind="text">
                  {parent?.description}
                </span>
                <span className="sphere-type-editor__parent-brings" data-datum="place.answers" data-kind="text">
                  {(lineage?.ancestors ?? []).map((a) => localize(a.nameKey, a.name)).join(' ← ')}
                  {answeredCount > 0 && (
                    <>
                      {' '}
                      {t('studio.typeEditor.placement.answersPrefix')} {answeredCount} {t('studio.typeEditor.placement.answersNeed', { count: answeredCount })}{' '}
                      {t('studio.typeEditor.placement.answersSuffix')}
                    </>
                  )}
                </span>
              </span>
              <span className="sphere-type-editor__parent-acts">
                <button
                  type="button"
                  className="sphere-type-editor__parent-brings-button"
                  data-datum="place.what-it-brings"
                  data-testid="sphere-type-placement-what-it-brings"
                  aria-expanded={manifestAsked}
                  onClick={() => {
                    setManifestRead(false);
                    setManifestAsked(true);
                  }}
                >
                  <i className="pi pi-box" aria-hidden="true" />
                  {t('studio.typeEditor.placement.whatItBrings')}
                </button>
                {canMove && (
                  <button
                    type="button"
                    className="sphere-type-editor__parent-move-button"
                    data-datum="place.move-it"
                    data-kind="button"
                    data-testid="sphere-type-placement-move-it"
                    onClick={() => {
                      setParentQuery('');
                      setNamingFamily(false);
                      setBrowsingParent(true);
                    }}
                  >
                    <i className="pi pi-arrow-right-arrow-left" aria-hidden="true" />
                    {t('studio.typeEditor.placement.moveIt')}
                  </button>
                )}
              </span>
            </div>
          )}

          {browsingParent && (
            <div className="sphere-type-editor__parent-picker" data-datum="place.picker" data-kind="picker" data-testid="sphere-type-placement-picker">
              <div className="sphere-type-editor__parent-picker-head">
                <strong data-datum="place.what-does-it-inherit-from" data-kind="strong">
                  {t('studio.typeEditor.placement.whatDoesItInheritFrom')}
                </strong>
                <input
                  type="text"
                  className="sphere-type-editor__parent-picker-search"
                  data-datum="place.search-types"
                  data-kind="input"
                  value={parentQuery}
                  onChange={(e) => setParentQuery(e.target.value)}
                  placeholder={t('studio.typeEditor.placement.searchTypes')}
                  aria-label={t('studio.typeEditor.placement.searchTypes')}
                />
                {parentName && (
                  <button
                    type="button"
                    className="sphere-type-editor__parent-picker-cancel"
                    data-datum="place.cancel"
                    data-kind="button"
                    onClick={closeParentBrowser}
                  >
                    {t('studio.typeEditor.placement.cancel')}
                  </button>
                )}
              </div>
              <PickerShellMolecule<ParentOption>
                items={(() => {
                  const q = parentQuery.trim().toLowerCase();
                  const pool = parentOptions ?? [];
                  return (q ? pool.filter((option) => `${option.name} ${option.description ?? ''}`.toLowerCase().includes(q)) : pool).slice(0, 10);
                })()}
                getSearchText={(option) => `${option.name} ${option.description ?? ''}`}
                getRowKey={(option) => option.schemaId}
                // The shell's own `role="option"` wrapper is the actionable element the
                // contract's `place.button` names — `ParentOptionRowMolecule` is content only
                // (its own doc comment: an option may hold no interactive descendant).
                getOptionDatumId={() => 'place.button'}
                getOptionKind={() => 'button'}
                getOptionContainer={() => 'each(parentOptions)'}
                hideSearch
                // The search field above is the block's own, not the shell's, so the
                // listbox is its own tab stop — and it takes the block's question as its
                // name rather than sitting in the tab order unnamed.
                listLabel={t('studio.typeEditor.placement.whatDoesItInheritFrom')}
                noMatchesMessage={t('studio.typeEditor.placement.noMatchingTypes')}
                renderRow={(option) => (
                  <ParentOptionRowMolecule
                    key={option.schemaId}
                    option={option}
                    selected={parent?.schemaId === option.schemaId}
                    t={t}
                  />
                )}
                isItemSelected={(option) => parent?.schemaId === option.schemaId}
                onPickItem={(option) => {
                  if (!option.isCurrent) {
                    setManifestRead(false);
                    setManifestAsked(true);
                    setMovedFrom(parentOptions?.find((candidate) => candidate.isCurrent) ?? null);
                    setMovedTo(option);
                  } else {
                    setMovedFrom(null);
                    setMovedTo(null);
                  }
                  onMoveParent?.(option);
                  closeParentBrowser();
                }}
              />
              <p className="sphere-type-editor__parent-picker-note" data-datum="place.everything-sits-under-something-the-plai" data-kind="note">
                {t('studio.typeEditor.placement.everythingSitsUnderSomething', {
                  typeName: localize(definition.localization_name_key, definition.name),
                })}
              </p>
              <button
                type="button"
                className="sphere-type-editor__parent-picker-new-family"
                data-datum="place.cancel#2"
                data-kind="button"
                data-testid="sphere-type-placement-start-new-family"
                onClick={() => setNamingFamily((naming) => !naming)}
              >
                <i className="pi pi-sitemap" aria-hidden="true" />
                {namingFamily ? t('studio.typeEditor.placement.cancelNewFamily') : t('studio.typeEditor.placement.startNewFamily')}
              </button>
            </div>
          )}

          {/*
            `type-composer.jsx:1116-1141` — re-parenting a live type is a migration underneath
            its spheres, so the choice is stated rather than committed silently. Only one action:
            `Keep it under {parent}`, which is the sole way back — there is nothing to "confirm"
            beyond the selection itself (D8: no write is wired yet).
          */}
          {movedFrom && movedTo && (
            <InlineConfirmMolecule
              tone="warning"
              testId="sphere-type-placement-move-impact"
              datum="place.callout"
              icon="pi-arrow-right-arrow-left"
              titleId="sphere-type-place-move-impact-title"
              title={t('studio.typeEditor.placement.moveImpactTitle', {
                typeName: localize(definition.localization_name_key, definition.name),
                from: movedFrom.name,
                to: movedTo.name,
              })}
              titleDatum="place.moving"
              body={
                <>
                  {answeredCount === 0
                    ? t('studio.typeEditor.placement.moveImpactNothingLost', { from: movedFrom.name, to: movedTo.name })
                    : t('studio.typeEditor.placement.moveImpactLost', {
                        items: [...inheritedAspectNames, ...perspectiveNames, ...arcNames].join(', '),
                      })}
                  {movedTo.brings ? ` ${t('studio.typeEditor.placement.moveImpactGained', { brings: movedTo.brings })}` : null}
                </>
              }
              bodyDatum="place.nothing-is-lost-everything"
              actions={[
                {
                  label: t('studio.typeEditor.placement.moveImpactKeepItUnder', { parent: movedFrom.name }),
                  onClick: () => {
                    onMoveParent?.(movedFrom);
                    setMovedFrom(null);
                    setMovedTo(null);
                  },
                  'data-testid': 'sphere-type-placement-keep-it-under',
                  'data-datum': 'place.keep-it-under',
                },
              ]}
            />
          )}

          {/*
            `type-composer.jsx:1060-1071` — the question the four outcome
            buttons never asked. It is about SPHERES, not inheritance: a type
            that makes spheres can still have types under it, so "can other
            types inherit from this" was the wrong question.

            Locked, and it says why: abstractness is topology. Flipping it
            re-homes or orphans every sphere already made of the type, so it is
            settled when the type is created (`updateSchema.ts:95-96` refuses
            it as a structural field) — a disabled control that hid its reason
            would read as a bug.
          */}
          <h2 className="sphere-type-editor__instantiable-question" data-datum="place.are-spheres-ever-made-of-this-type" data-kind="heading">
            {t('studio.typeEditor.placement.instantiableQuestion')}
          </h2>
          <div className="sphere-type-editor__choices" role="radiogroup" aria-label={t('studio.typeEditor.placement.instantiableQuestion')}>
            {[
              {
                id: 'concrete',
                on: !definition.is_abstract,
                label: t('studio.typeEditor.placement.instantiableYesTitle'),
                note: t('studio.typeEditor.placement.instantiableYesNote'),
              },
              {
                id: 'abstract',
                on: !!definition.is_abstract,
                label: t('studio.typeEditor.placement.instantiableNoTitle'),
                note: t('studio.typeEditor.placement.instantiableNoNote'),
              },
            ].map((choice) => (
              <button
                key={choice.id}
                type="button"
                role="radio"
                aria-checked={choice.on}
                disabled
                className="sphere-type-editor__choice"
                data-container="each(choices)"
                data-on={choice.on ? '1' : undefined}
                data-datum="place.choice-card"
                data-kind="choice-card"
                data-testid={`sphere-type-placement-choice-${choice.id}`}
              >
                <span className="sphere-type-editor__choice-top">
                  <span className="sphere-type-editor__choice-radio" data-datum="place.radio-indicator#2" data-kind="radio-indicator" aria-hidden="true" />
                  <strong data-datum="place.label#2" data-kind="strong">
                    {choice.label}
                  </strong>
                </span>
                <em>{choice.note}</em>
              </button>
            ))}
          </div>
          <p className="sphere-type-editor__placement-note" data-testid="sphere-type-placement-instantiable-locked">
            {t('studio.typeEditor.placement.instantiableLockedReason')}
          </p>

          {namingFamily && (
            <div className="sphere-type-editor__new-family" data-datum="place.picker#2" data-kind="picker" data-testid="sphere-type-placement-new-family">
              <div className="sphere-type-editor__new-family-head">
                <strong data-datum="place.a-new-family-for-it" data-kind="strong">
                  {t('studio.typeEditor.placement.newFamilyHead')}
                </strong>
                <span className="a-hint" data-datum="place.it-sits-between" data-kind="hint">
                  {t('studio.typeEditor.placement.newFamilySitsBetween', {
                    typeName: localize(definition.localization_name_key, definition.name),
                    parentLabel: parentName || t('studio.typeEditor.placement.newFamilyItsParent'),
                  })}
                </span>
              </div>
              <TypeFieldRowMolecule
                datum="place.name"
                layout="stacked"
                label={t('studio.typeEditor.placement.newFamilyName')}
                mode="edit"
                controls={[
                  {
                    kind: 'text',
                    value: newFamilyName,
                    placeholder: t('studio.typeEditor.placement.newFamilyNamePlaceholder'),
                    ariaLabel: t('studio.typeEditor.placement.newFamilyName'),
                    onChange: setNewFamilyName,
                  },
                ]}
              />
              <div className="sphere-type-editor__new-family-glyph-field" data-datum="place.glyph" data-kind="field" data-layout="stacked">
                <span className="sphere-type-editor__identity-glyph-label">{t('studio.typeEditor.placement.newFamilyGlyph')}</span>
                <GlyphPickerMolecule
                  glyph={newFamilyGlyph}
                  glyphColor={newFamilyGlyphColor}
                  fallbackText={newFamilyName || '?'}
                  hint={t('studio.typeEditor.placement.newFamilyGlyphHint')}
                  tileSize={34}
                  datums={PLACEMENT_NEW_FAMILY_GLYPH_DATUMS}
                  onChange={(next) => {
                    setNewFamilyGlyph(next.glyph);
                    setNewFamilyGlyphColor(next.glyphColor);
                  }}
                />
              </div>
              <TypeFieldRowMolecule
                datum="place.the-boundary-in-one-sentence-e-g-orgs-we"
                layout="stacked"
                label={t('studio.typeEditor.placement.newFamilyBoundaryLabel')}
                mode="edit"
                controls={[
                  {
                    kind: 'multiline',
                    value: newFamilyDesc,
                    placeholder: t('studio.typeEditor.placement.newFamilyBoundaryPlaceholder'),
                    ariaLabel: t('studio.typeEditor.placement.newFamilyBoundaryLabel'),
                    onChange: setNewFamilyDesc,
                  },
                ]}
              />
              <p className="sphere-type-editor__new-family-note" data-datum="place.starts-empty-apart-from" data-kind="note">
                {newFamilyName.trim()
                  ? t('studio.typeEditor.placement.newFamilyStartsEmpty', {
                      name: newFamilyName.trim(),
                      typeName: localize(definition.localization_name_key, definition.name),
                      parentLabel: parentName || t('studio.typeEditor.placement.newFamilyItsParent'),
                    })
                  : t('studio.typeEditor.placement.newFamilyNameIsBoundary')}
              </p>
            </div>
          )}

          {parentName && !manifestRead && (definition.version_is_draft || browsingParent || manifestAsked) && (
            <InheritanceManifestPanelMolecule
              parentName={parentName}
              rows={manifestRows}
              answeredNeeds={answeredCount}
              perspectivesArriving={perspectiveNames}
              arcsArriving={arcNames}
              alreadyItsParent
              onAcknowledge={() => setManifestRead(true)}
            />
          )}
        </>
      )}
    </div>
  );
}

/** Monotonic — ghost identity, purely client-side bookkeeping (never sent to the server). */
let ghostIdSeq = 0;
function nextGhostId(): string {
  ghostIdSeq += 1;
  return `ghost-${ghostIdSeq}`;
}

/**
 * The sealed half of an attachment, read off the aspect's own catalog row so the sheet can
 * show what is being configured. `undefined` when the workspace registry has no row for the
 * system name — which the sheet reports as an unresolved definition rather than as an aspect
 * that declares nothing, because those are different facts.
 *
 * Three readings are derived rather than stored, because v1's `AspectDefinition` has no
 * per-property equivalent of the prototype's `FILL_WORD`:
 *
 * - `fill` comes from `valuePolicy.automation` and the presence of a `reducer` — who or what
 *   actually supplies a value is exactly what those two fields say;
 * - `computed` is the same reading at the definition level: nothing is asked of anyone, so no
 *   arc stage can raise a check-in for it;
 * - `temporal` maps `observed_at` onto the sheet's `point`, its own word for the same shape.
 */
export function attachmentDefinitionOf(row: AspectRow | null | undefined): AttachmentDefinition | undefined {
  if (!row) return undefined;
  const def = row.definition;
  const automation = def.valuePolicy?.automation;
  const rolledUp = !!def.reducer;
  const computed = rolledUp || automation === 'automatic';
  const fill: AttachmentFillWord = rolledUp ? 'rollup' : computed ? 'derived' : 'asked';
  const bands = def.evaluation?.bands;
  return {
    kind: def.collection?.kind ?? 'manual',
    holds: (def.properties ?? []).map((property) => ({
      name: property.name,
      type: String(property.type),
      fill,
    })),
    evaluated: !!def.evaluation,
    bands: bands
      ? [
          { status: 'on_track', pct: bands.onTrackAtOrAbove },
          { status: 'at_risk', pct: bands.atRiskAtOrAbove },
        ]
      : [],
    temporal: def.valuePolicy?.temporal === 'effective_interval' ? 'effective_interval' : def.valuePolicy?.temporal === 'observed_at' ? 'point' : 'timeless',
    expiryDays: def.valuePolicy?.expiry?.afterDays,
    computed,
  };
}

/** The fixed cadence vocabulary. These tokens ARE the stored answer; `t()` only names them. */
export const CADENCE_ANSWERS = ['daily', 'weekly', 'monthly', 'quarterly', 'annually'] as const;

/** One answer a need can take: what gets stored, and what a person reads. */
export interface NeedOption {
  /** Stored verbatim by `PUT /schemas/:id/needs/...`. Stable across locales and renames. */
  value: string;
  /** What the dropdown shows. Localized, or the perspective's display label. */
  label: string;
}

/**
 * What this type actually has to offer as an answer to one need. A need with nothing to
 * offer is left empty on purpose: the sheet then says so, and "there is no perspective on
 * this type to hand it to" is the answer, not a missing control.
 *
 * `cadence` is the one list that is not read off the type — it is a fixed vocabulary, so it
 * comes from the bundle rather than from the schema.
 *
 * Value and label are separate on purpose. The answer is PERSISTED, so it has to be the
 * stable identifier — the perspective's `name`, the arc's `templateSystemName`, a cadence
 * token — never the display string. Storing the display string would write `Hebdomadaire`
 * for an author working in fr-FR and `Weekly` for their colleague in en-US, for the same
 * answer to the same need, and nothing downstream could resolve either one.
 */
export function needOptionsFor(key: string, definition: Schema, t: (key: string, params?: Record<string, unknown>) => string): NeedOption[] {
  switch (key) {
    case 'perspective':
      return (definition.perspectives ?? []).map((perspective) => ({
        value: perspective.name,
        label: perspective.label || perspective.name,
      }));
    case 'arc':
      return (definition.defaultArcs ?? [])
        .map((arc) => arc.templateSystemName)
        .concat(definition.arcTemplateSystemName ? [definition.arcTemplateSystemName] : [])
        .filter((name, index, all): name is string => !!name && all.indexOf(name) === index)
        .map((name) => ({ value: name, label: name }));
    case 'cadence':
      return CADENCE_ANSWERS.map((cadence) => ({
        value: cadence,
        label: t(`studio.typeEditor.aspects.sheet.needs.cadenceOption.${cadence}`),
      }));
    default:
      return [];
  }
}

/**
 * How a stored answer reads. Falls back to the stored value itself when the option is gone
 * (a perspective deleted after it was named, an aspect whose vocabulary moved on) — the row
 * then shows what is actually on the record rather than an empty cell that reads "unanswered".
 */
export function needAnswerLabel(key: string, answer: string, definition: Schema, t: (key: string, params?: Record<string, unknown>) => string): string {
  return needOptionsFor(key, definition, t).find((option) => option.value === answer)?.label ?? answer;
}

/**
 * `compose.r-version` — the version line for an attachment this type OWNS. An
 * inherited one shows `compose.from` instead: the ancestor's pin governs, so
 * stating a version here would imply this type could change it.
 */
function describeAspectVersion(aspect: AspectRef, t: (key: string, params?: Record<string, unknown>) => string): string {
  return aspect.versionPin === undefined
    ? `${t('studio.typeEditor.aspects.versionFollows')} ${t('studio.typeEditor.aspects.versionFloats')}`
    : t('studio.typeEditor.aspects.versionPinned', { version: aspect.versionPin });
}

/**
 * An attachment isn't done when the aspect lands — it's done when the answers only this
 * site can give have been given (prototype `type-composer.jsx` `reviewOf`). One
 * derivation, so the row's mark, the attachment sheet's CTA and the publish gate cannot
 * disagree about whether an attachment is settled.
 *
 * `open` names what is still owed, in the words the person would have to answer:
 *
 * - the definition does not resolve in this workspace's catalog (`unresolved`);
 * - a need the TYPE is meant to answer has no answer — a need deferred to each sphere
 *   (`at === 'record'`) is answered by design, not open;
 * - an EVALUATED aspect with no `config.targets` — where its targets come from is a
 *   decision, and "nobody decided" is not the same as "deferred to each sphere".
 *
 * `unreviewed` (open is empty, `confirmed` is not set) is the reading the whole
 * per-attachment config store exists for: an attachment can be complete on every answer
 * and still be nothing but the defaults the aspect shipped with.
 */
export function reviewOfAttachment(
  aspect: AspectRef,
  needs: SchemaNeed[],
  unresolved: boolean,
  t: (key: string, params?: Record<string, unknown>) => string
): { open: string[]; confirmed: boolean; state: 'open' | 'unreviewed' | 'ok' } {
  const open: string[] = [];
  if (unresolved) open.push(t('studio.typeEditor.aspects.review.openUnresolved'));
  needs.forEach((need) => {
    if (need.at === 'type' && !need.answer) open.push(needCopy(need.key, 'ask', t));
  });
  if (aspect.evaluation && !aspect.config?.targets) {
    open.push(t('studio.typeEditor.aspects.review.openTargets'));
  }
  const confirmed = aspect.config?.confirmed === true;
  return { open, confirmed, state: open.length > 0 ? 'open' : confirmed ? 'ok' : 'unreviewed' };
}

/**
 * `compose.wired-to` — the arcs this attachment collects into, read off `config.wiring`.
 * An arc whose every stage role is `none` is an arc the attachment is deliberately NOT
 * wired into, so it is excluded rather than listed with an empty role set; when nothing
 * is wired the chip is absent entirely.
 */
export function wiredArcNames(aspect: AspectRef): string[] {
  return Object.entries(aspect.config?.wiring ?? {})
    .filter(([, stageRoles]) => Object.values(stageRoles ?? {}).some((role) => role && role !== 'none'))
    .map(([arcName]) => arcName);
}

/**
 * The aspect row's provenance / settlement strip (`compose > list > each(composed) >
 * list-item > group`). Every value is derived from what the merged schema read
 * actually carries — the composed `AspectRef` (C1 provenance, C3 kind/evaluation)
 * plus the C4 needs read — and nothing is invented:
 *
 * - `targets` reads `config.targets` (the C6 per-attachment config store). Absent means
 *   deferred: "each sphere enters its own" is what an evaluated aspect with no
 *   type-level answer actually does, and it is a different claim from the type having
 *   chosen to defer, which is why the review derivation still counts it as open.
 * - `noNeeds` deliberately does not consult `r.unresolved`, which is not a property of
 *   the merged read at all: an attachment is unresolvable when the workspace CATALOG
 *   has no definition for its system name, so `catalog` (`null` until the list has
 *   loaded) is what decides it — see `unresolved` below.
 * - `desc` and `installedFrom` likewise come from the catalog row, not the ref: the
 *   merged schema read carries an attachment's settings, never the definition's own
 *   description or where it was installed from.
 */
function aspectRowMeta(
  aspect: AspectRef,
  contributed: SchemaProperty[],
  needs: SchemaAspectNeeds['needs'],
  justAttached: boolean,
  lineage: LineageResult | null,
  /** This attachment's catalog row, or `null`/`undefined` — see `resolveAspectCatalogRow`. */
  catalogRow: AspectRow | null | undefined,
  /** False while the catalog has not loaded, so no resolvability claim is made yet. */
  catalogLoaded: boolean,
  localize: Localize,
  t: (key: string, params?: Record<string, unknown>) => string
): AspectDefSummaryMeta {
  const inherited = aspect.is_inherited === true;
  // Reconfiguring covers BOTH stores: `overrides` (what this type changes about the
  // aspect's properties) and `config` (the answers only this site gives). A type that
  // only wired an inherited attachment into its own arc has reconfigured it just as
  // surely as one that renamed a field.
  const reconfigured = inherited && (Object.keys(aspect.overrides ?? {}).length > 0 || Object.keys(aspect.config ?? {}).length > 0);
  const evaluated = !!aspect.evaluation;
  const unresolved = catalogLoaded && !catalogRow;
  const review = reviewOfAttachment(aspect, needs, unresolved, t);
  const wiredArcs = wiredArcNames(aspect);
  // Publisher-authored content, so `getLocalizedText` (the LocalizedString resolver), not
  // `localize` (which resolves a platform translation KEY).
  const description = getLocalizedText(catalogRow?.description, '');
  return {
    was: aspect.overrides?.label ? t('studio.typeEditor.aspects.row.was', { systemName: aspect.systemName }) : undefined,
    desc: description || undefined,
    /**
     * `template_id` is the only marketplace provenance the catalog row carries, and it is
     * an id, not a name — naming the listing would need a second read this surface does
     * not make. The chip therefore says THAT it was installed rather than from where.
     */
    installedFrom: catalogRow?.template_id ? t('studio.typeEditor.aspects.row.installedFrom') : undefined,
    unresolved: unresolved ? t('studio.typeEditor.aspects.row.unresolved') : undefined,
    reconfiguredTitle: reconfigured ? t('studio.typeEditor.aspects.row.reconfiguredTitle') : undefined,
    properties: contributed.length > 0 ? t('studio.typeEditor.aspects.row.properties', { count: contributed.length }) : undefined,
    reconfigured: reconfigured ? t('studio.typeEditor.aspects.row.reconfigured') : undefined,
    inheritedFrom: inherited
      ? t('studio.typeEditor.aspects.row.from', {
          schema: resolveAncestorName(aspect.inherited_from_schema, lineage, localize, t) ?? t('studio.typeEditor.row.unknownAncestor'),
        })
      : undefined,
    version: inherited ? undefined : describeAspectVersion(aspect, t),
    /**
     * Settled attachments carry no mark, and an INHERITED one is marked only while it
     * has answers still open: its settlement belongs to the ancestor that composed it,
     * so "nobody confirmed the defaults" is not this type's question to answer.
     */
    review:
      review.state === 'ok' || (inherited && review.state !== 'open')
        ? undefined
        : {
            label:
              review.open.length > 0 ? t('studio.typeEditor.aspects.row.toSet', { count: review.open.length }) : t('studio.typeEditor.aspects.row.unreviewed'),
            title:
              review.open.length > 0
                ? t('studio.typeEditor.aspects.row.stillToSet', {
                    items: review.open.join(t('studio.typeEditor.aspects.row.openSeparator')),
                  })
                : t('studio.typeEditor.aspects.row.unreviewedTitle'),
            blocking: review.open.length > 0,
          },
    draftedHere: aspect.drafted ? t('studio.typeEditor.aspects.row.draftedHere') : undefined,
    justAttached: justAttached ? t('studio.typeEditor.aspects.row.justAttached') : undefined,
    needCount: needs.length > 0 ? t('studio.typeEditor.aspects.row.needCount', { count: needs.length }) : undefined,
    targets: evaluated
      ? {
          label: t('studio.typeEditor.aspects.row.targets'),
          value: t(
            aspect.config?.targets === 'seed'
              ? 'studio.typeEditor.aspects.row.targetsSeeded'
              : aspect.config?.targets === 'default'
              ? 'studio.typeEditor.aspects.row.targetsDefault'
              : 'studio.typeEditor.aspects.row.targetsDeferred'
          ),
          atWord: t(
            !aspect.config?.targets || aspect.config.targets === 'defer' ? 'studio.typeEditor.aspects.needs.atRecord' : 'studio.typeEditor.aspects.needs.atType'
          ),
        }
      : undefined,
    wiredTo:
      wiredArcs.length > 0
        ? t('studio.typeEditor.aspects.row.wiredTo', {
            arcs: wiredArcs.join(t('studio.typeEditor.businessRules.valueSeparator')),
          })
        : undefined,
    answeredByFamily: needs.length === 0 && inherited ? t('studio.typeEditor.aspects.row.answeredByFamily') : undefined,
    noNeeds: needs.length === 0 && !inherited && !evaluated ? t('studio.typeEditor.aspects.row.noNeeds') : undefined,
  };
}

/**
 * The attach picker's render inputs, reported by `AspectsSection` up to the composer root via
 * `onPickerStateChange` — see that prop's doc comment.
 */
export interface AspectPickerRenderState {
  items: AspectRow[];
  loading: boolean;
  errorMessage: string | null;
  scopeLabelCount: number;
  onPick: (systemName: string) => void;
  onCancel: () => void;
}

/**
 * `sel.kind === "start"` — the "Draft a new aspect" dialog's render inputs, reported by
 * `AspectsSection` to the composer root the same way `onPickerStateChange` reports the attach
 * picker's (EAINT-933 Phase 3.2: the dialog moves from its own modal scrim to the rail's
 * current selection, no portal, mirroring `pick`). `null` while `draftingAspect` is false.
 */
export interface DraftAspectRenderState {
  aspectCatalog: AspectRow[] | null;
  onCreate: (draft: NewAspectDraft) => void;
  onUseExisting: (systemName: string) => void;
  /** `onCreate`'s most recent failure — see the dialog's own `error` prop. */
  error: string | null;
  /** True for the duration of an in-flight `onCreate` — see the dialog's own `submitting` prop. */
  submitting: boolean;
}

/**
 * Aspects — attach existing, detach with confirm, pin/float (EAINT-895 Phase 3d).
 * REPLACE semantics on `Schema.aspects`, same one-path-per-mutation shape as
 * `OwnPropertiesSection`: `commit()` always resends the full composed set, sealed
 * schemas are guarded server-side (B2a), and detach reuses the same
 * `confirmDialogAtom` + session-only-undo pattern as removing an own property.
 * The picker for "attach existing" composes `PickerShellMolecule` (the search/
 * scope-banner/empty-state chrome already extracted for `PerspectivePickerMolecule`)
 * rather than a new picker component.
 */
function AspectsSection({
  definition,
  lineage,
  t,
  localize,
  locked,
  onSaveField,
  needsByAspect = {},
  aspectCatalog,
  onDraftAspect,
  initialConfirmDrop,
  initialDropped,
  pickerOpen,
  onPickerOpenChange,
  onPickerStateChange,
  configuringAspect,
  onConfiguringAspectChange,
  configuringPortalNode,
  draftingAspect,
  onDraftingAspectChange,
  onDraftAspectStateChange,
  critique,
  dismissedCritiqueIds,
  onDismissCritique,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  /** `compose.draft-it` — see the organism prop. */
  onDraftAspect?: (draft: NewAspectDraft) => Promise<string | undefined>;
  /** The resolvable aspect definitions — see the organism prop. */
  aspectCatalog?: AspectRow[] | null;
  /** The attachment whose drop confirm stands open on mount — see the organism prop. */
  initialConfirmDrop?: string;
  /** Attachments already detached in this session on mount — see the organism prop. */
  initialDropped?: AspectRef[];
  /** Per-aspect resolved needs (EAINT-897 C4, `GET /schemas/:id/needs`) — fetched by the caller's
   * `useSphereType`, same pattern as `gate`. Renders regardless of `locked`/`onSaveField`, since the
   * prototype's `r.needs` chips are read-only in either mode. */
  needsByAspect?: Record<string, SchemaAspectNeeds['needs']>;
  /**
   * `sel.kind === "pick"` — whether the rail currently holds the attach picker
   * (`SphereTypeComposerOrganism`'s own state; EAINT-921 Phase 3a). This section still owns the
   * picker's catalog fetch and `handleAttach`; only whether it is the rail's open selection, and
   * where its markup mounts, live above it.
   */
  pickerOpen: boolean;
  onPickerOpenChange: (open: boolean) => void;
  /**
   * Reports the attach picker's current render inputs (its item list, load state and pick
   * handler) up to the composer root, which renders `PickerShellMolecule` itself as the rail's
   * `pick` selection body — `null` while the picker is closed. This section still owns the
   * catalog fetch and `handleAttach`; only the JSX that reads them moved, so the root never
   * needs a portal target to host markup this section built (EAINT-928 Phase 1).
   */
  onPickerStateChange?: (state: AspectPickerRenderState | null) => void;
  /**
   * `sel.kind === "attach"` — the system name of the attachment whose config sheet the rail
   * currently holds, or `null` (`SphereTypeComposerOrganism`'s own state; lifted the same way
   * `pickerOpen` was). This section still owns the sheet's fields, needs and `onChange`/
   * `onRemove` wiring; only which one is open, and where its markup mounts, live above it.
   */
  configuringAspect: string | null;
  onConfiguringAspectChange: (systemName: string | null) => void;
  /** The rail's portal target for the attachment sheet once `configuringAspect` is set — the
   * "renders nowhere without a rail" behaviour `onPickerStateChange` no longer needs a portal
   * for (EAINT-928 Phase 1: `pick` moved to a root-rendered branch; `attach` has not). */
  configuringPortalNode: HTMLDivElement | null;
  /**
   * `sel.kind === "start"` — whether the "Draft a new aspect" dialog is the rail's current
   * selection (`SphereTypeComposerOrganism`'s own state; lifted the same way `pickerOpen` was —
   * EAINT-933 Phase 3.2). This section still owns the catalog it searches and the create/use-
   * existing handlers; only whether it is open, and where its markup mounts, live above it.
   */
  draftingAspect: boolean;
  onDraftingAspectChange: (open: boolean) => void;
  /** Reports this dialog's render inputs up to the composer root, which renders
   * `NewAspectDialogMolecule` itself as the rail's `start` selection body (no portal, same as
   * `pick`) — `null` while `draftingAspect` is false. */
  onDraftAspectStateChange?: (state: DraftAspectRenderState | null) => void;
  /** `compose.callout` — see the organism prop. */
  critique?: ComposerTypeCritique[];
  /** The dismissal set shared with the Agent tab's "Watching as you edit" panel — see the organism prop. */
  dismissedCritiqueIds: string[];
  onDismissCritique: (id: string) => void;
}) {
  const editable = !locked && !!onSaveField;
  const apiClients = useApiClientsOrNull();

  const [rows, setRows] = useState<AspectRef[]>(() => definition.aspects ?? []);
  const logAnalyticsEvent = useAnalytics();
  /**
   * Which attachment's sheet stands open. One at a time: the sheet is the rail's current
   * `attach` selection, not a peer panel, so opening a second closes the first rather than
   * stacking two editors over one list. Controlled by the composer root (EAINT-928 Phase 1,
   * mirroring `pickerOpen`'s Phase 3a lift) so it can drive `InspectorRailOrganism`'s head and
   * portal target — this section keeps only the sheet's own fields and wiring.
   */
  const configuring = configuringAspect;
  const setConfiguring = onConfiguringAspectChange;
  /**
   * Need answers written in THIS session, keyed aspect system name then need key. The
   * answer store (`schema_aspect_need_answers`) is not the schema definition, so a landed
   * write does not change `needsByAspect`, which the page fetched once alongside the
   * schema. This overlay is what the row's chips, the review derivation and the sheet all
   * read, so a need answered here reads as answered without a refetch — and, because it is
   * only written on a 2xx, never reads as answered before the write landed.
   */
  const [answeredHere, setAnsweredHere] = useState<Record<string, Record<string, { answer: string; at: NeedAnswerAt }>>>({});
  /** Why the last need answer did not land, in the words of the outcome that refused it. */
  /**
   * A need-write refusal, keyed by the aspect it belongs to. Not a single string: the writes
   * are queued (`needWriteQueue`), so a refusal can land after the author has closed that
   * sheet or opened another aspect's. A lone string then rendered the refusal under whichever
   * sheet happened to be open — attributing aspect A's failure to aspect B — or, with every
   * sheet closed, nowhere at all.
   */
  const [needError, setNeedError] = useState<Record<string, string>>({});
  /**
   * The version lever as the author last left it, per attachment. `versionPin` is the
   * persisted truth, but "Pin" chosen with no number typed yet is not a pin — deriving the
   * lever from `versionPin` alone would snap it back to Follow on every keystroke.
   */
  const [pinDrafts, setPinDrafts] = useState<Record<string, Pick<AttachmentConfig, 'pin' | 'pinnedVersion'>>>({});
  /**
   * `compose.callout#2` — the drop confirm is RENDERED INSIDE the section, not raised as a modal.
   * The prototype asks in place because the sentence is the answer: what a detach costs depends on
   * whether the attachment is inherited and whether its needs have answers, and an imperative
   * confirm dialog can carry neither that branch nor a capture of it.
   */
  const [confirmDrop, setConfirmDrop] = useState<string | null>(() => initialConfirmDrop ?? null);
  /**
   * `compose.callout`/`compose.dismiss` — the agent's ambient critique cards for this block. The
   * dismissal set is owned by the composer root (`dismissedCritiqueIds`/`onDismissCritique`) and
   * shared with the Agent tab's "Watching as you edit" panel, same as the prototype's own single
   * `dismissed` state (`trail-composer.jsx:159`) — no schema representation, so no other seam.
   */
  const composeCritique = (critique ?? []).filter((c) => c.block === 'compose' && !dismissedCritiqueIds.includes(c.id));
  /**
   * `compose.add-row#2` — detached in THIS session, restorable until publish. An array, not the
   * single undo it replaced: detaching twice used to discard the first undo silently.
   */
  const [dropped, setDropped] = useState<{ index: number; aspect: AspectRef }[]>(() => (initialDropped ?? []).map((aspect, index) => ({ index, aspect })));
  /**
   * `compose.just-attached` — attachments made in THIS editing session. Session
   * state, not schema state: the merged read cannot distinguish "attached a moment
   * ago" from "attached last week", and the chip's whole point is that the author
   * has not published it yet.
   */
  const [justAttached, setJustAttached] = useState<string[]>([]);
  /** `onDraftAspect`'s most recent failure (`draftAspectCollisionKeepsTheDraft`) — see the
   * dialog's own `error` prop. Cleared on a fresh open and on every new submit attempt. */
  const [draftAspectError, setDraftAspectError] = useState<string | null>(null);
  /** True for the duration of an in-flight `onDraftAspect` call. */
  const [draftAspectSubmitting, setDraftAspectSubmitting] = useState(false);
  const aspectCatalogFetch = useAttachableAspectCatalog(apiClients, definition.tenant_id);

  /**
   * The workspace registry the rows are read against. The caller's `aspectCatalog` when it
   * has arrived, otherwise whatever the attach picker's own lazy fetch has cached — one
   * source, so the picker and the rows can never disagree about what is resolvable.
   * `null` means NOT LOADED, which is deliberately not "empty": every resolvability
   * reading below is silent while it holds.
   */
  const resolvedCatalog = aspectCatalog ?? aspectCatalogFetch.catalog;
  const catalogStatus = aspectCatalogFetch.status;
  const catalogByName = useMemo(() => new Map((resolvedCatalog ?? []).map((row) => [row.system_name, row])), [resolvedCatalog]);

  // Same re-baseline rule as every other editable section (spec
  // `staleEditSurfacesConflictNotClobber`): never in response to a conflict.
  //
  // Compared against a ref rather than the dependency alone, because an effect also fires on
  // MOUNT — where nothing has changed. That was harmless while every one of these started empty,
  // and a silent wipe of `initialConfirmDrop` / `initialDropped` once they did not (the same
  // defect `openTypeRef` records for `initialGhosts`).
  const baselineRef = useRef(`${definition.system_name}\0${definition.version_id}`);
  useEffect(() => {
    const baseline = `${definition.system_name}\0${definition.version_id}`;
    if (baselineRef.current === baseline) return;
    baselineRef.current = baseline;
    setRows(definition.aspects ?? []);
    setPinDrafts({});
    setConfirmDrop(null);
    setDropped([]);
    setJustAttached([]);
    setConfiguring(null);
    setAnsweredHere({});
    setNeedError({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.system_name, definition.version_id]);

  const aspects = editable ? rows : definition.aspects ?? [];

  /** The one path every mutation (attach, detach, undo, pin/float) funnels through. */
  function commit(next: AspectRef[]) {
    // is_inherited/inherited_from_schema (C1) and collectionKind/evaluation/sealed (C3)
    // are provenance stamped by the server on read — never authored. Strip before the
    // REPLACE-semantics write so a mutation built from `rows` (seeded from the merged
    // GET response) can't round-trip them.
    onSaveField?.({
      aspects: next.map(({ is_inherited, inherited_from_schema, collectionKind, evaluation, sealed, ...rest }) => rest),
    });
  }

  /**
   * The needs of one attachment as this surface currently knows them: what the page read
   * (`GET /schemas/:id/needs`), with anything answered in this session laid over it. An
   * answer written here always originates on THIS schema, so its `inheritedFrom` clears —
   * the row must not keep crediting the ancestor whose answer was just superseded.
   */
  function needsFor(systemName: string): SchemaNeed[] {
    const written = answeredHere[systemName];
    const declared = needsByAspect[systemName] ?? [];
    if (!written) return declared;
    return declared.map((need) => {
      const answer = written[need.key];
      return answer ? { ...need, at: answer.at, answer: answer.answer || null, inheritedFrom: null } : need;
    });
  }

  /**
   * The sheet hands back its whole config; this splits it by which store owns each half.
   *
   * `answers` / `answeredBy` belong to the ANSWER STORE (`schema_aspect_need_answers`,
   * C4), which `GET /schemas/:id/needs` resolves nearest-ancestor-first and which is the
   * only thing the publish gate reads. Writing them into `AspectRef.config` as well would
   * put two answers to one need in two places with no rule for which wins, so the config
   * this surface persists carries neither — it is the answers-store write, below, that
   * records them.
   *
   * Everything else is the per-attachment config (C6) and rides the ordinary schema write.
   */
  /**
   * The sheet's view of one attachment's settings: the persisted per-attachment config (C6)
   * plus the answers store's current reading of its needs. The sheet holds both halves in one
   * object; `persistAttachmentConfig` and `writeNeedAnswers` put them back in the two places
   * that actually own them.
   */
  function attachmentConfigOf(aspect: AspectRef): AttachmentConfig {
    const config = aspect.config ?? {};
    const answers: Record<string, string> = {};
    const answeredBy: Record<string, NeedAnswerAt> = {};
    needsFor(aspect.systemName).forEach((need) => {
      answeredBy[need.key] = need.at;
      if (need.at === 'type' && need.answer) answers[need.key] = need.answer;
    });
    return {
      confirmed: config.confirmed,
      startAs: config.startAs,
      targets: config.targets,
      wiring: config.wiring,
      options: config.options,
      weight: config.weight === undefined ? undefined : String(config.weight),
      pin: pinDrafts[aspect.systemName]?.pin ?? (aspect.versionPin === undefined ? 'float' : 'pin'),
      pinnedVersion: pinDrafts[aspect.systemName]?.pinnedVersion ?? (aspect.versionPin === undefined ? undefined : String(aspect.versionPin)),
      answers,
      answeredBy,
    };
  }

  function persistAttachmentConfig(aspect: AspectRef, next: AttachmentConfig) {
    const parsedWeight = next.weight === undefined || next.weight.trim() === '' ? NaN : Number(next.weight);
    const config: AspectAttachmentConfig = {
      confirmed: next.confirmed,
      startAs: next.startAs,
      targets: next.targets,
      wiring: next.wiring,
      options: next.options,
      weight: Number.isFinite(parsedWeight) ? parsedWeight : undefined,
    };
    // "" floats to the latest published version; a parseable positive integer pins it.
    // Anything else keeps the reference floating rather than silently coercing to NaN.
    const parsedPin = next.pin === 'pin' ? Number((next.pinnedVersion ?? '').trim()) : NaN;
    const versionPin = Number.isInteger(parsedPin) && parsedPin > 0 ? parsedPin : undefined;
    setPinDrafts((prev) => ({ ...prev, [aspect.systemName]: { pin: next.pin, pinnedVersion: next.pinnedVersion } }));
    setRows((prev) => {
      const nextRows = prev.map((row) => (row.systemName === aspect.systemName ? { ...row, config, versionPin } : row));
      commit(nextRows);
      return nextRows;
    });
  }

  /** What refused the write, said in the terms of the thing that refused it. */
  function needErrorMessage(outcome: string): string {
    switch (outcome) {
      case 'sealed':
        return t('studio.typeEditor.aspects.needWrite.sealed');
      case 'forbidden':
        return t('studio.typeEditor.aspects.needWrite.forbidden');
      case 'notFound':
        return t('studio.typeEditor.aspects.needWrite.notFound');
      case 'unknownNeed':
        return t('studio.typeEditor.aspects.needWrite.unknownNeed');
      default:
        return t('studio.typeEditor.aspects.needWrite.failed');
    }
  }

  /**
   * Sends every need whose answer or answerer actually moved. One PUT per changed need,
   * because the route is per-need — and `sphere_type_need_answered` fires on the 2xx, not
   * on the change, so a refused write is never counted as an answer given.
   */
  async function writeNeedAnswers(aspect: AspectRef, next: AttachmentConfig) {
    const client = apiClients?.schemaClient;
    if (!client) {
      // `persistAttachmentConfig` has already run, so the answer is on screen as chosen while
      // nothing was sent. Returning bare left the author looking at a settled-looking sheet
      // backed by no write at all — the same silence the 5xx branch below exists to close.
      console.error(
        '[SphereTypeComposer] answering a need failed',
        { schemaId: definition.schema_id, aspectSystemName: aspect.systemName, needKey: null },
        new Error('no schemaClient on the ApiClients context')
      );
      setNeedError((prev) => ({ ...prev, [aspect.systemName]: needErrorMessage('failed') }));
      return;
    }
    // Every refusal in this run, named by the need it belongs to. One banner cannot say
    // "the perspective was refused" by holding the last message written to it: a success
    // later in the loop used to clear an earlier 403, so an author who changed two needs
    // and had one refused was shown nothing at all and told nothing was wrong.
    const failures: string[] = [];
    let attempted = 0;
    for (const need of needsFor(aspect.systemName)) {
      const at: NeedAnswerAt = next.answeredBy?.[need.key] ?? need.at;
      // A need handed to each sphere carries no type-level answer: the sheet clears it
      // when the answerer changes, and sending the stale pick would re-answer it here.
      const answer = at === 'type' ? next.answers?.[need.key] ?? need.answer ?? '' : '';
      if (at === need.at && answer === (need.answer ?? '')) continue;
      // An empty answer is not an answer. Moving the answerer BACK to `type` on a need nobody
      // has answered yet fails the skip test above (`at !== need.at`) while carrying no value,
      // and the route stores whatever it is handed — which would mark the need answered with
      // nothing in it, so the sheet reads it as settled while `!!need.answer` still reads it as
      // open. The answerer alone is not a writable fact here; it is written with an answer.
      if (at === 'type' && answer === '') continue;
      attempted += 1;
      const refuse = (reason: string) =>
        failures.push(
          t('studio.typeEditor.aspects.needWrite.perNeed', {
            noun: needCopy(need.key, 'noun', t),
            reason,
          })
        );
      try {
        const outcome = await answerAspectNeed(client, definition.schema_id, aspect.systemName, need.key, {
          answer,
          answeredAt: at,
        });
        if (outcome.outcome !== 'answered') {
          refuse(needErrorMessage(outcome.outcome));
          continue;
        }
        setAnsweredHere((prev) => ({
          ...prev,
          [aspect.systemName]: { ...(prev[aspect.systemName] ?? {}), [need.key]: { answer, at } },
        }));
        // Spec § Analytics: `sphere_type_need_answered`.
        logAnalyticsEvent('sphere_type_need_answered', undefined, {
          schemaId: definition.schema_id,
          aspectSystemName: aspect.systemName,
          needKey: need.key,
          answeredAt: at,
        });
      } catch (error) {
        // `answerAspectNeed` classifies 400/403/404/409 itself and rethrows everything else
        // — a 5xx, a dropped connection, an unparseable body. That is the one class of
        // failure nobody designed a message for, so it is the one that most needs a record.
        console.error(
          '[SphereTypeComposer] answering a need failed',
          { schemaId: definition.schema_id, aspectSystemName: aspect.systemName, needKey: need.key },
          error
        );
        refuse(needErrorMessage('failed'));
      }
    }
    // Only a run that actually wrote may speak for this aspect's banner. A sheet change that
    // touched no need must not clear a refusal the author has not dealt with yet — and it
    // speaks for THIS aspect only, never for whichever sheet happens to be open when it lands.
    if (attempted === 0) return;
    setNeedError((prev) => {
      const next = { ...prev };
      if (failures.length > 0) next[aspect.systemName] = failures.join(' ');
      else delete next[aspect.systemName];
      return next;
    });
  }

  /**
   * Need writes are serialized per composer, not fired in parallel. Each PUT is
   * last-write-wins on the server, so two overlapping changes to the same need would leave
   * the record holding whichever RESPONSE returned last — not the answer the author chose
   * last. Chaining makes request order the order of the author's edits.
   */
  const needWriteQueue = useRef<Promise<void>>(Promise.resolve());

  function handleSheetChange(aspect: AspectRef, next: AttachmentConfig) {
    persistAttachmentConfig(aspect, next);
    needWriteQueue.current = needWriteQueue.current.catch(() => undefined).then(() => writeNeedAnswers(aspect, next));
  }

  function openAttach() {
    onPickerOpenChange(true);
    aspectCatalogFetch.ensureLoaded();
  }

  function handleAttach(systemName: string) {
    setRows((prev) => {
      if (prev.some((aspect) => aspect.systemName === systemName)) return prev;
      const next = [...prev, { systemName }];
      commit(next);
      return next;
    });
    setJustAttached((prev) => (prev.includes(systemName) ? prev : [...prev, systemName]));
    onPickerOpenChange(false);
  }

  /** `compose.draft-a-new-aspect` — opens the dialog, lazily loading the catalog it searches
   * for near-duplicates exactly like `openAttach` does for the attach picker. */
  function openDraftAspect() {
    setDraftAspectError(null);
    onDraftingAspectChange(true);
    aspectCatalogFetch.ensureLoaded();
  }

  /** `compose.use-this` on a duplicate hit — attaches the existing aspect instead of drafting
   * a new one, reusing the real attach path rather than a second one. */
  function handleUseExistingAspect(systemName: string) {
    handleAttach(systemName);
    onDraftingAspectChange(false);
  }

  function handleDetach(systemName: string) {
    setConfirmDrop(systemName);
  }

  /** The confirm's `Detach it` — only ever reachable for an attachment this type owns. */
  function performDetach(systemName: string) {
    const index = rows.findIndex((candidate) => candidate.systemName === systemName);
    if (index < 0) return;
    const aspect = rows[index];
    setRows((prev) => {
      const next = prev.filter((candidate) => candidate.systemName !== systemName);
      commit(next);
      return next;
    });
    setDropped((prev) => [...prev, { index, aspect }]);
    // The open sheet and the lever draft both describe a row that no longer exists. Left
    // behind, a re-attach of the same aspect would mount with its sheet already open, on
    // `Pin` with the old number while the fresh reference floats; a put-back re-seeds the
    // lever from the restored ref's own `versionPin`.
    if (configuring === systemName) setConfiguring(null);
    setPinDrafts((prev) => {
      if (!(systemName in prev)) return prev;
      const { [systemName]: _dropped, ...rest } = prev;
      return rest;
    });
    setConfirmDrop(null);
  }

  /**
   * `compose.put-them-back` — restores every attachment detached this session at the place it
   * held, ascending so an earlier index is not shifted by a later insert. Its settings come back
   * with it: the ref was kept whole, not rebuilt from its system name.
   */
  function handlePutThemBack() {
    if (dropped.length === 0) return;
    setRows((prev) => {
      const next = [...prev];
      [...dropped].sort((a, b) => a.index - b.index).forEach(({ index, aspect }) => next.splice(Math.min(index, next.length), 0, aspect));
      commit(next);
      return next;
    });
    setDropped([]);
  }

  // Memoized: this feeds the `onPickerStateChange` effect below, and a fresh array identity on
  // every render (from an unmemoized `.filter()`) would re-fire that effect every render while
  // the picker is open — an update loop, since the effect's own `onPickerStateChange` call is
  // what causes the next render.
  const availableToAttach = useMemo(
    () => (resolvedCatalog ?? []).filter((row) => !aspects.some((aspect) => aspect.systemName === row.system_name)),
    [resolvedCatalog, aspects]
  );

  /**
   * `sel.kind === "pick"` — reports this section's picker render inputs to the composer root,
   * which renders `PickerShellMolecule` itself as the rail's `pick` selection body (EAINT-928
   * Phase 1). Replaces the earlier portal (`pickerPortalNode`): this section still owns the
   * catalog fetch and `handleAttach`; the root now owns the markup instead of hosting a target
   * div for this section to portal into. `null` while closed, so the root never renders a stale
   * item list from the last time the picker was open.
   */
  useEffect(() => {
    if (!onPickerStateChange) return;
    if (!pickerOpen) {
      onPickerStateChange(null);
      return;
    }
    onPickerStateChange({
      items: availableToAttach,
      loading: catalogStatus === 'loading',
      errorMessage: catalogStatus === 'error' ? t('studio.typeEditor.aspects.attach.error') : null,
      scopeLabelCount: availableToAttach.length,
      onPick: handleAttach,
      onCancel: () => onPickerOpenChange(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, availableToAttach, catalogStatus, onPickerStateChange]);

  /**
   * `sel.kind === "start"` — reports the "Draft a new aspect" dialog's render inputs to the
   * composer root, which renders `NewAspectDialogMolecule` itself as the rail's `start`
   * selection body (EAINT-933 Phase 3.2, same no-portal shape as `pick`). This section still
   * owns the catalog and the create/use-existing wiring; `null` while closed or while the host
   * supplies no `onDraftAspect` (mirrors the add-row button's own guard).
   */
  /**
   * `onCreate` — resolves the aspect `onDraftAspect` created (`blank`/`extend`) by attaching it
   * through the same path `handleAttach` already uses and opening its configuration sheet
   * (`draftBlankAspectCreatesAttachesAndOpensIt`, `draftExtendAspectSetsParentAspect`). `undefined`
   * (the `describe` route, forwarded to the live agent thread instead of writing anything) just
   * closes the dialog. A rejection — most often `AspectSystemNameCollisionError` — surfaces
   * inline and leaves the dialog open with the draft intact (`draftAspectCollisionKeepsTheDraft`);
   * it never reaches `onDraftingAspectChange(false)`.
   */
  async function handleCreateAspect(draft: NewAspectDraft) {
    if (!onDraftAspect) return;
    setDraftAspectError(null);
    setDraftAspectSubmitting(true);
    try {
      const created = await onDraftAspect(draft);
      if (created) {
        handleAttach(created);
        setConfiguring(created);
      }
      onDraftingAspectChange(false);
    } catch (err) {
      setDraftAspectError(
        err instanceof AspectSystemNameCollisionError
          ? t('studio.typeEditor.aspects.newDialog.errors.collision', { name: draft.name })
          : t('studio.typeEditor.aspects.newDialog.errors.failed')
      );
    } finally {
      setDraftAspectSubmitting(false);
    }
  }

  useEffect(() => {
    if (!onDraftAspectStateChange) return;
    if (!draftingAspect || !onDraftAspect) {
      onDraftAspectStateChange(null);
      return;
    }
    onDraftAspectStateChange({
      aspectCatalog: resolvedCatalog,
      onCreate: handleCreateAspect,
      onUseExisting: handleUseExistingAspect,
      error: draftAspectError,
      submitting: draftAspectSubmitting,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftingAspect, resolvedCatalog, onDraftAspect, onDraftAspectStateChange, draftAspectError, draftAspectSubmitting]);

  const confirmAspect = confirmDrop ? aspects.find((a) => a.systemName === confirmDrop) ?? null : null;
  /** An inherited attachment is dropped where it was attached, never here (C1 provenance). */
  const confirmInheritedFrom = confirmAspect?.is_inherited
    ? resolveAncestorName(confirmAspect.inherited_from_schema, lineage, localize, t) ?? t('studio.typeEditor.row.unknownAncestor')
    : null;

  /**
   * `compose.p-label`..`compose.on-the-aspect` (`trail-composer.jsx:1380-1430`, `.t-propsum`):
   * what a sphere of this type carries, read in one place — every row is an aspect's property,
   * an ancestor's, or this type's own, and none of it is authored here. `OwnPropertiesSection`,
   * the standalone editable section this replaces, computed the same three groups
   * (`aspectContributedProperties` / `inheritedOwnProperties` / `declaredProperties`); this
   * reads all three read-only, with the `compose.*` datum ids the contract's merged summary
   * declares. The prototype is explicit that the third ("legacy" — `compose.legacy`,
   * declared-directly-on-the-type) group is read-only too (`trail-composer.jsx:537`, "a
   * directly-declared row is legacy and read-only"): the rail contract drops the `props` block
   * entirely rather than keep it as a second, still-editable place to declare a property — a
   * type composes an aspect for that now (see the Phase 5 ADR). A legacy row's own editor
   * (`PropertyEditorMolecule`, the add/delete/undo affordances) went with `OwnPropertiesSection`.
   */
  const propSummaryAspectRows = aspectContributedProperties(definition);
  const propSummaryInheritedRows = inheritedOwnProperties(definition);
  const propSummaryLegacyRows = declaredProperties(definition);
  const propRowsTotal = propSummaryAspectRows.length + propSummaryInheritedRows.length + propSummaryLegacyRows.length;
  const propSummaryAspectLabelOf = (systemName: string): string =>
    aspectDisplayName((definition.aspects ?? []).find((aspect) => aspect.systemName === systemName) ?? { systemName }, catalogByName);
  const propSummaryGroups: RowGroup[] = [
    {
      id: 'aspect',
      label: t('studio.typeEditor.aspects.propertySummary.aspectLabel'),
      note: t('studio.typeEditor.aspects.propertySummary.aspectNote'),
      count: propSummaryAspectRows.length,
      datums: MERGED_PROPERTY_SUMMARY_GROUP_DATUMS,
      children: propSummaryAspectRows.map((property) =>
        propertyRow(
          property,
          t,
          localize,
          {
            // Opening the aspect's OWN definition is out of scope (D7,
            // `NestedAspectSheet`); "On the aspect" instead expands that
            // attachment's own row in THIS section — the same expansion its
            // chevron opens — so the property's contributing aspect is at
            // least one click away rather than a dead end.
            onOpenAspect: editable ? () => setConfiguring(property.aspect_system_name ?? null) : undefined,
            aspectLabel: propSummaryAspectLabelOf(property.aspect_system_name ?? ''),
            datums: MERGED_PROPERTY_SUMMARY_ROW_DATUMS,
          },
          lineage
        )
      ),
    },
    {
      id: 'inherited',
      label: t('studio.typeEditor.aspects.propertySummary.inheritedLabel'),
      note: t('studio.typeEditor.aspects.propertySummary.inheritedNote'),
      count: propSummaryInheritedRows.length,
      datums: MERGED_PROPERTY_SUMMARY_GROUP_DATUMS,
      children: propSummaryInheritedRows.map((property) => propertyRow(property, t, localize, { datums: MERGED_PROPERTY_SUMMARY_ROW_DATUMS }, lineage)),
    },
    {
      id: 'legacy',
      label: t('studio.typeEditor.aspects.propertySummary.legacyLabel', {
        typeName: localize(definition.localization_name_key, definition.name),
      }),
      note: t('studio.typeEditor.aspects.propertySummary.legacyNote'),
      count: propSummaryLegacyRows.length,
      datums: MERGED_PROPERTY_SUMMARY_GROUP_DATUMS,
      children: propSummaryLegacyRows.map((property) => propertyRow(property, t, localize, { datums: MERGED_PROPERTY_SUMMARY_ROW_DATUMS }, lineage)),
    },
  ];

  return (
    <div data-testid="sphere-type-aspects">
      {/*
        The contract's `compose > list` (`type-composer.jsx:1179`, `.t-rows.t-rows-tight`): one
        bordered list whose rows are divided, not a run of free-floating groups. The empty note
        sits INSIDE the list, where the prototype puts it — an empty list is still the list.
      */}
      <div className="sphere-type-editor__aspect-list" data-container="aspects > list" data-testid="sphere-type-aspects-list">
        {aspects.length === 0 ? (
          /*
           * `compose.nothing-composed-spheres-of` (`trail-composer.jsx:1263-1264`) —
           * the contract's empty reading is a NOTE, not an icon panel: read-only says
           * spheres of this type carry only what its ancestors bring; editable says
           * to attach or draft an aspect, since a property with no aspect to hold it
           * can't be declared here.
           */
          <p
            className="sphere-type-editor__list-empty"
            data-testid="sphere-type-aspects-empty"
            data-datum="compose.nothing-composed-spheres-of"
            data-kind="note"
          >
            {editable
              ? t('studio.typeEditor.aspects.emptyEditable')
              : t('studio.typeEditor.aspects.emptyReadOnly', {
                  name: localize(definition.localization_name_key, definition.name),
                })}
          </p>
        ) : (
          aspects.map((aspect, index) => {
            const contributed = definition.properties.filter((p) => p.aspect_system_name === aspect.systemName);
            return (
              <div
                key={aspect.systemName}
                className="sphere-type-editor__aspect-group"
                data-container="aspects > list > each(aspects) > list-item"
                data-testid={`sphere-type-aspect-${aspect.systemName}`}
              >
                <AspectDefSummaryMolecule
                  className="sphere-type-editor__aspect-header"
                  label={aspectDisplayName(aspect, catalogByName)}
                  glyphDatum="compose.tile"
                  labelDatum="compose.cfg-label"
                  meta={aspectRowMeta(
                    aspect,
                    contributed,
                    needsFor(aspect.systemName),
                    justAttached.includes(aspect.systemName),
                    lineage,
                    catalogByName.get(aspect.systemName),
                    resolvedCatalog !== null,
                    localize,
                    t
                  )}
                  needs={needsFor(aspect.systemName).map((need) => ({
                    key: need.key,
                    answer: need.answer ? needAnswerLabel(need.key, need.answer, definition, t) : t('studio.typeEditor.aspects.needs.unanswered'),
                    atWord: t(need.at === 'record' ? 'studio.typeEditor.aspects.needs.atRecord' : 'studio.typeEditor.aspects.needs.atType'),
                  }))}
                  expandLabel={t('studio.typeEditor.aspects.expand')}
                  collapseLabel={t('studio.typeEditor.aspects.collapse')}
                  data-testid={`aspect-def-summary-${aspect.systemName}`}
                  trailing={
                    editable ? (
                      <>
                        {/*
                        The attachment sheet opens from the row it configures — the answers
                        only this composition site can give (needs, arc wiring, targets) are
                        not row content, and a modal would put them outside the section they
                        belong to. The prototype opens it by clicking the row and draws a
                        chevron at the row's end (`type-composer.jsx:1274`); a whole row is
                        not an affordance a keyboard user can tab to, so the chevron IS the
                        press here. It carries no contract datum — the row's click never did —
                        and `compose.button` is the definition's, on the row's strip.
                      */}
                        <ButtonAtom
                          variant="ghost"
                          shape="icon-square"
                          size="small"
                          icon={configuring === aspect.systemName ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                          aria-label={t(configuring === aspect.systemName ? 'studio.typeEditor.aspects.closeConfigure' : 'studio.typeEditor.aspects.configure')}
                          aria-expanded={configuring === aspect.systemName}
                          onClick={() => setConfiguring(configuring === aspect.systemName ? null : aspect.systemName)}
                          data-testid={`aspect-configure-${aspect.systemName}`}
                        />
                      </>
                    ) : undefined
                  }
                >
                  {contributed.length === 0 ? (
                    <EmptyStateAtom className="sphere-type-editor__empty" icon="pi-inbox" title={t('studio.typeEditor.aspects.noContributedProperties')} />
                  ) : (
                    <div className="sphere-type-editor__aspect-properties">
                      {contributed.map((property) => propertyRow(property, t, localize, {}, lineage))}
                    </div>
                  )}
                </AspectDefSummaryMolecule>
                {/* Outside the `configuring` gate on purpose: the write is queued, so a refusal
                  can land after the author has closed this sheet. It belongs to the aspect it
                  refused, and it stays on that aspect's row until that aspect writes again. */}
                {editable && needError[aspect.systemName] && (
                  <MessageAtom severity="error" text={needError[aspect.systemName]} data-testid={`aspect-need-error-${aspect.systemName}`} />
                )}
                {/*
                `sel.kind === "attach"` (`type-attachment.jsx`, contract `side` datums) — the
                sheet's markup mounts where `InspectorRailOrganism` mounted the rail's `attach`
                selection target, not inline beneath the row (EAINT-928 Phase 1, same portal
                shape as the `pick` picker above): only the fields, needs and onChange/onRemove
                wiring stay here. `configuringPortalNode` is null for one render after
                `configuring` names this aspect (the target div mounts before its ref reaches
                here) and, in any host that never renders a rail, stays null forever — the sheet
                then does not fall back to an inline mount the contract no longer has.
              */}
                {editable &&
                  configuring === aspect.systemName &&
                  configuringPortalNode &&
                  createPortal(
                    <AspectAttachmentSheetMolecule
                      aspectName={aspectDisplayName(aspect, catalogByName)}
                      typeName={localize(definition.localization_name_key, definition.name)}
                      version={aspect.versionPin === undefined ? undefined : `v${aspect.versionPin}`}
                      definition={attachmentDefinitionOf(catalogByName.get(aspect.systemName))}
                      config={attachmentConfigOf(aspect)}
                      needs={needsFor(aspect.systemName).map<AttachmentNeedRow>((need) => ({
                        key: need.key,
                        at: need.at,
                        answer: need.answer ?? undefined,
                        options: needOptionsFor(need.key, definition, t),
                      }))}
                      evaluated={!!aspect.evaluation}
                      inheritedFrom={aspect.is_inherited ? resolveAncestorName(aspect.inherited_from_schema, lineage, localize, t) ?? undefined : undefined}
                      review={reviewOfAttachment(aspect, needsFor(aspect.systemName), resolvedCatalog !== null && !catalogByName.get(aspect.systemName), t)}
                      onChange={(next) => handleSheetChange(aspect, next)}
                      onClose={() => setConfiguring(null)}
                      onRemove={() => handleDetach(aspect.systemName)}
                      data-testid={`aspect-sheet-${aspect.systemName}`}
                    />,
                    configuringPortalNode
                  )}
              </div>
            );
          })
        )}
      </div>
      {/*
        `type-composer.jsx:1303-1305` — the hint has a read-only branch. Without
        it a platform type simply has no attach control and no explanation of
        why, which reads as a missing feature rather than a definition boundary.
      */}
      {!editable && (
        <p
          className="sphere-type-editor__section-note"
          data-testid="sphere-type-aspects-platform-hint"
          data-datum="compose.the-aspects-on-a-platform-type-are-part-"
          data-kind="hint"
        >
          {t('studio.typeEditor.aspects.platformHint')}
        </p>
      )}
      {editable && (
        <div className="sphere-type-editor__aspect-attach" data-datum="compose.add-row" data-kind="add-row">
          <ButtonAtom
            variant="secondary"
            size="small"
            icon="pi pi-plus"
            label={t('studio.typeEditor.aspects.attach.button')}
            onClick={openAttach}
            data-testid="aspect-attach-button"
            data-datum="compose.attach-aspect"
            data-kind="button"
            data-open={pickerOpen ? '1' : undefined}
            aria-expanded={pickerOpen}
          />
          {/*
            `compose.draft-a-new-aspect` — the prototype's second add-row button, opening
            `NewAspectDialogMolecule` rather than the deep, out-of-scope `NestedAspectSheet`
            (D7: opening an aspect's own definition editor stays out of this spec). Hidden, not
            greyed, while the host supplies no `onDraftAspect`: there is no backend to draft
            against yet, and a control whose submit discards its input is a defect.
          */}
          {onDraftAspect && (
            <ButtonAtom
              variant="secondary"
              size="small"
              icon="pi pi-sparkles"
              label={t('studio.typeEditor.aspects.draftNew.button')}
              onClick={openDraftAspect}
              data-testid="aspect-draft-new-button"
              data-datum="compose.draft-a-new-aspect"
              data-kind="button"
            />
          )}
          {/*
            `type-composer.jsx:1303-1305` — the hint sits in the add row in BOTH
            modes, BESIDE the button (`.t-addrow` is one inline row), and says a
            different thing in each. The editable sentence is the one that explains
            where an attachment's answers land, which is the question the attach
            button raises.
          */}
          <span className="sphere-type-editor__aspect-attach-hint" data-testid="sphere-type-aspects-attach-hint">
            {t('studio.typeEditor.aspects.attachHint')}
          </span>
          {/*
            `sel.kind === "pick"` (`type-rail/trail-composer.jsx:825-828`) — the picker's body
            renders at the composer root, which reads it from `onPickerStateChange` above and
            mounts `PickerShellMolecule` directly as the rail's `pick` selection body (EAINT-928
            Phase 1): no portal, since the markup itself lives at the root now, not just its
            target.
          */}
        </div>
      )}
      {/*
        `sel.kind === "start"` — the "Draft a new aspect" dialog's body renders at the composer
        root, which reads it from `onDraftAspectStateChange` above and mounts
        `NewAspectDialogMolecule` directly as the rail's `start` selection body (EAINT-933 Phase
        3.2): no portal and no own modal scrim, the same shape `pick` already uses.
      */}
      {/*
        `compose.p-label`..`compose.on-the-aspect` (`trail-composer.jsx:1380-1430`, `.t-propsum`):
        what a sphere of this type carries, read in one place, below the attach row — all three
        groups (from a composed aspect, inherited from an ancestor, or declared directly on this
        type before properties became aspects). Read-only by construction: an aspect's or
        ancestor's property is changed where it is declared, and a legacy one is changed by
        composing an aspect for it (`OwnPropertiesSection` and the `own-properties` section id it
        owned are gone — plan Phase 3a); this list offers no "add".
      */}
      <MergedPropertySummaryMolecule
        data-testid="sphere-type-property-summary"
        headingText={t('studio.typeEditor.aspects.propertySummary.heading', {
          typeName: localize(definition.localization_name_key, definition.name),
        })}
        totalCount={propRowsTotal}
        hint={t('studio.typeEditor.aspects.propertySummary.hint')}
        groups={propSummaryGroups}
        emptyMessage={t('studio.typeEditor.aspects.propertySummary.empty')}
        emptyDatum="compose.nothing-yet-attach-an-aspect-and-its-pro"
        headingDatum="compose.properties-a-sphere-of"
        headingTextDatum="compose.properties-a-sphere-of#2"
        countDatum="compose.count"
        hintDatum="compose.read-here-changed-on-the-aspect-that-hol"
      />
      {/*
        `compose.callout`/`compose.dismiss` (`trail-composer.jsx:1432-1437`, `.t-crit`) — the
        agent's ambient critique for this block, one card per undismissed item. Advisory only
        (spec § Agentic UX): the card reads and comments, it never writes; acting on it goes
        through the existing ghost flow, not through this card.
      */}
      {composeCritique.map((c) => (
        <div key={c.id} data-container='compose > each(crit.filter(c => c.block === "compose"))'>
          <MessageAtom
            severity={c.severity}
            text={<span>{c.text}</span>}
            dismissible
            dismissVariant="text"
            onDismiss={() => onDismissCritique(c.id)}
            data-testid={`aspects-critique-${c.id}`}
            datum="compose.callout"
            dismissDatum="compose.dismiss"
          />
        </div>
      ))}
      {/*
        `type-composer.jsx:1307-1333` — the drop confirm is a callout INSIDE the section, after the
        add row (contract order 1, against the add row's 0). It asks in place because the question
        has two different answers: an inherited attachment cannot be dropped here at all, and the
        sentence names where it can be. A modal could carry neither branch nor a rendered state.
      */}
      {editable && confirmAspect && (
        <InlineConfirmMolecule
          tone="danger"
          testId="aspects-drop-confirm"
          datum="compose.callout#2"
          icon="pi-link"
          titleId="sphere-type-drop-confirm-title"
          title={t('studio.typeEditor.aspects.detach.confirmTitle', {
            name: aspectDisplayName(confirmAspect, catalogByName),
            typeName: localize(definition.localization_name_key, definition.name),
          })}
          titleTestId="aspects-drop-confirm-title"
          titleDatum="compose.detach"
          body={
            confirmInheritedFrom
              ? t('studio.typeEditor.aspects.detach.inheritedBody', {
                  schema: confirmInheritedFrom,
                  typeName: localize(definition.localization_name_key, definition.name),
                })
              : t(
                  needsFor(confirmAspect.systemName).length > 0
                    ? 'studio.typeEditor.aspects.detach.ownBodyWithAnswers'
                    : 'studio.typeEditor.aspects.detach.ownBody',
                  { typeName: localize(definition.localization_name_key, definition.name) }
                )
          }
          bodyTestId="aspects-drop-confirm-body"
          bodyDatum="compose.attached-this-so"
          actions={[
            // No `Detach it` on an inherited attachment, and no route from here to the type that
            // owns it either — the sentence above does that work rather than a button that goes
            // nowhere (the prototype's own note at `type-composer.jsx:1328`).
            ...(!confirmInheritedFrom
              ? [
                  {
                    label: t('studio.typeEditor.aspects.detach.confirmAccept'),
                    onClick: () => performDetach(confirmAspect.systemName),
                    variant: 'danger' as const,
                    'data-testid': 'aspects-drop-confirm-accept',
                    'data-datum': 'compose.detach-it',
                  },
                ]
              : []),
            {
              label: t(confirmInheritedFrom ? 'studio.typeEditor.aspects.detach.close' : 'studio.typeEditor.aspects.detach.keepIt'),
              onClick: () => setConfirmDrop(null),
              variant: 'text' as const,
              'data-testid': 'aspects-drop-confirm-dismiss',
              'data-datum': 'compose.close',
            },
          ]}
        />
      )}
      {/*
        `type-composer.jsx:1336-1349` — a detach is undoable until publish, and the row says so in
        the same breath as naming what went. `compose.add-row#2` was unwaived (mapping.json,
        2026-09-12): the wrapper carries the datum so the notice strip is accounted for, even
        though — like `compose.add-row` itself — it is a grouping `<div>`, not an actionable
        element on its own; `Put them back` below is the actionable child.
      */}
      {editable && dropped.length > 0 && (
        <div
          className="sphere-type-editor__detached-row"
          data-testid="aspects-detached-row"
          data-datum="compose.add-row#2"
          data-kind="add-row"
        >
          <span className="sphere-type-editor__detached-note" data-testid="aspects-detached-note" data-datum="compose.detached-here" data-kind="hint">
            {t('studio.typeEditor.aspects.detach.undoNotice', {
              names: dropped.map(({ aspect }) => aspectDisplayName(aspect, catalogByName)).join(', '),
            })}
          </span>
          <ButtonAtom
            variant="text"
            size="small"
            icon="pi pi-replay"
            label={t('studio.typeEditor.aspects.detach.undo')}
            onClick={handlePutThemBack}
            data-testid="aspects-put-them-back"
            data-datum="compose.put-them-back"
            data-kind="button"
          />
        </div>
      )}
    </div>
  );
}

function BusinessRulesSection({
  definition,
  lineage,
  t,
  localize,
  locked,
  onSaveField,
  onOpenSection,
  ruleEditTarget,
  onRuleEditTargetChange,
  ruleEditPortalNode,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  onOpenSection?: (sectionId: string) => void;
  /**
   * `sel.kind === "rule"` — the rule whose editor the rail currently holds: `null` (closed),
   * `'new'` (authoring a brand-new rule), or the rule being edited
   * (`SphereTypeComposerOrganism`'s own state, lifted the same way `configuringAspect` was —
   * EAINT-928 Phase 1). This section still owns `rows`, `saveFromSheet` and the add/edit/remove
   * wiring; only which rule's sheet is open, and where its markup mounts, live above it. The
   * head's "needs fixing" caption button sets this directly (no nonce indirection needed once
   * the target is a real value the root can just assign).
   */
  ruleEditTarget: BusinessRule | 'new' | null;
  onRuleEditTargetChange: (target: BusinessRule | 'new' | null) => void;
  /** The rail's portal target for the rule editor once `ruleEditTarget` is set — same seam as
   * `configuringPortalNode`. */
  ruleEditPortalNode: HTMLDivElement | null;
}) {
  const onOpenTranslations = onOpenSection ? () => onOpenSection('languages') : undefined;
  const editable = !locked && !!onSaveField;
  const [rows, setRows] = useState<BusinessRule[]>(() => definition.businessRules ?? []);

  // Same re-baseline rule as every other editable section: never in response to
  // a conflict, which must leave in-progress local edits alone.
  useEffect(() => {
    setRows(definition.businessRules ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.system_name, definition.version_id]);

  const rules: BusinessRule[] = editable ? rows : definition.businessRules ?? [];

  /*
   * The prototype leads the section with what a business rule IS, because the
   * distinction is not obvious: a constraint on ONE property belongs on the
   * aspect that holds it, so it travels with it. Only cross-property statements
   * live here (`type-composer.jsx:1556`). That lead sentence is the shell's
   * `bodyText` (rendered above every section by `EditorBlockMolecule`,
   * wired to `rules.constraints-spanning-more-than-one-prope`) — this section
   * used to also render it inline via `businessRules.sectionIntro`, the exact
   * same string, as a second unannotated copy (a PAR-6 extra-structure defect).
   */

  function commitRows(next: BusinessRule[]) {
    setRows(next);
    onSaveField?.({ businessRules: next });
  }

  /** Every field commit funnels through here — REPLACE always carries the full own array (EAINT-895 Phase 3c). */
  function saveFromSheet(patch: RuleSheetPatch) {
    if (ruleEditTarget === 'new') {
      commitRows([
        ...rows,
        {
          ...patch,
          id: `rule_${rows.length + 1}_${Date.now()}`,
          messageKey: '',
        } as BusinessRule,
      ]);
    } else if (ruleEditTarget) {
      const id = ruleEditTarget.id;
      commitRows(rows.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)));
    }
    onRuleEditTargetChange(null);
  }

  /*
    The add row, in the shape the contract gives it (`type-composer.jsx:1610-1618`): a
    wrapper (`rules.add-row`) holding the button (`rules.add-a-rule`) and the hint beside it.
  */
  const ruleAddRow = (
    <div className="sphere-type-editor__props-addrow" data-datum="rules.add-row" data-kind="add-row">
      <ButtonAtom
        variant="secondary"
        size="small"
        icon="pi pi-plus"
        label={t('studio.typeEditor.businessRules.addRule')}
        onClick={() => onRuleEditTargetChange('new')}
        data-testid="business-rules-add"
        data-datum="rules.add-a-rule"
        data-kind="button"
      />
      <span className="sphere-type-editor__props-addrow-hint" data-datum="rules.write-it-as-a-statement-that-must-stay-t" data-kind="hint">
        {t('studio.typeEditor.businessRules.addRuleHint')}
      </span>
    </div>
  );

  /*
    `sel.kind === "rule"` (`type-rule.jsx`, contract `side` datums) — the editor's markup
    mounts where `InspectorRailOrganism` mounted the rail's `rule` selection target, not IN
    PLACE beneath the row it edits (EAINT-928 Phase 1, same portal shape as `AspectsSection`'s
    `attach` rehost): only the fields and onSave/onRemove wiring stay here.
    `ruleEditPortalNode` is null for one render after `ruleEditTarget` is set (the target div
    mounts before its ref reaches here) and, in any host that never renders a rail, stays null
    forever — the sheet then does not fall back to an inline mount the contract no longer has.
  */
  const isEditing = (rule: BusinessRule) => ruleEditTarget !== null && ruleEditTarget !== 'new' && ruleEditTarget.id === rule.id;

  const mode: TypeFieldRowMode = editable ? 'edit' : locked ? 'locked' : 'disabled';

  return (
    <div data-testid="sphere-type-business-rules">
      {/*
        `rules > list` (`type-composer.jsx:1559`, `.t-rows.t-rows-tight`): one bordered list of
        divided rows. The empty note lives inside it (`:1605-1607`) — a NOTE, not an
        EmptyStateAtom that replaces the section, the defect the own-properties list carried
        until Phase 2 slice 18: swapping the section for an empty state takes the add row with
        it, so a type with no rules could not declare its first one.
      */}
      <div className="sphere-type-editor__rule-list" data-container="business-rules > list" data-testid="business-rules-list">
        {rules.map((rule) => (
          <div
            key={rule.id}
            className="sphere-type-editor__rule-item"
            data-container="business-rules > list > each(rules) > list-item"
            data-testid={`business-rules-item-${rule.id}`}
          >
            <TypeRuleRowMolecule
              rule={rule}
              mode={mode}
              problemCount={blockingProblemCount(rule, definition.properties ?? [])}
              inheritedFromName={rule.is_inherited ? resolveAncestorName(rule.inherited_from_schema, lineage, localize, t) ?? undefined : undefined}
              // An inherited rule is the ancestor type's to change; editing it here would
              // fork it silently, so the row opens nothing (`type-composer.jsx:1594`,
              // `readOnly || r.inherited`).
              onEdit={editable && !rule.is_inherited ? () => onRuleEditTargetChange(rule) : undefined}
              isOpen={isEditing(rule)}
              onOpenTranslations={onOpenTranslations}
            />
          </div>
        ))}
        {rules.length === 0 && (
          <p
            className="sphere-type-editor__list-empty"
            data-testid="business-rules-empty"
            data-datum="rules.no-cross-property-rules-a-constraint-on-"
            data-kind="note"
          >
            {t('studio.typeEditor.businessRules.emptyExplained')}
          </p>
        )}
      </div>
      {editable && ruleAddRow}
      {editable &&
        ruleEditTarget !== null &&
        ruleEditPortalNode &&
        createPortal(
          <RuleSheetMolecule
            visible
            rule={ruleEditTarget === 'new' ? null : ruleEditTarget}
            properties={definition.properties ?? []}
            onHide={() => onRuleEditTargetChange(null)}
            onSave={saveFromSheet}
            onRemove={() => {
              if (ruleEditTarget && ruleEditTarget !== 'new') {
                const id = ruleEditTarget.id;
                commitRows(rows.filter((rule) => rule.id !== id));
              }
              onRuleEditTargetChange(null);
            }}
          />,
          ruleEditPortalNode
        )}
    </div>
  );
}

/** Positively describes a relationship's target when `target_filter` constrains `type` by equals/in; otherwise names it as constrained rather than fabricating specificity `describeAssigneeFilters` doesn't offer here. */
/**
 * Names the permitted target types positively when `target_filter` says so, and
 * otherwise reports that the target is constrained rather than guessing.
 *
 * Reads EVERY `type` filter, not just the first: a filter set may narrow the
 * target across several entries, and naming only one would overstate what the
 * relationship accepts. Legacy entries carry a bare `value`/`values` with no
 * `operator`; those are treated as the equivalent positive constraint, since
 * dropping them silently reported "constrained" for a target that is in fact
 * named.
 */
function describeRelationshipTarget(targetFilter: UnifiedFilters | undefined, t: (key: string) => string): string {
  const typeFilters = (targetFilter?.propertyFilters ?? []).filter((filter) => filter.property === 'type');

  const named = typeFilters.flatMap((filter) => {
    if (filter.operator === 'equals' || filter.operator === undefined) {
      return typeof filter.value === 'string' ? [filter.value] : [];
    }
    if (filter.operator === 'in') {
      return Array.isArray(filter.values) ? filter.values.map(String) : [];
    }
    return [];
  });

  const unique = [...new Set(named)];
  return unique.length > 0 ? unique.join(t('studio.typeEditor.businessRules.valueSeparator')) : t('studio.typeEditor.relationships.constrainedTarget');
}

/**
 * One relationship row — CLOSED summary line, for both declared and inherited
 * relationships alike. The contract (`type-relationship.jsx`, `rels > list >
 * each(relRows) > list-item:x`) never renders a relationship as an
 * always-editable inline row: `r.name`/`→`/the target readback (`rels.r-name`
 * `rels.x` `rels.window-reltargetlabel-r-scope`) are unconditional, and
 * editing happens in `RelationshipSheetMolecule`, opened by acting on the row.
 * `onEdit` is only ever supplied for a declared row in `mode="edit"` — an
 * inherited relationship is never editable here, so it never gets one.
 */
function relationshipListRow(
  relationship: SchemaRelationship,
  t: (key: string, params?: Record<string, unknown>) => string,
  localize: Localize,
  lineage: LineageResult | null,
  onEdit?: () => void,
  onAdjust?: () => void,
  /** Opens Languages for a declared relationship's name, as the type name's chip does. */
  onOpenTranslations?: () => void
): React.ReactElement {
  const name = localize(relationship.label_key, relationship.label || relationship.name);
  const targetLabel = describeRelationshipTarget(relationship.target_filter, t);
  /*
    What this type changed about the relationship it inherited, read off the merged row:
    `fetchAndMergeDefinitions` collapses ancestor and child by `system_name` and stamps the
    ancestor's declaration as `adjusted_from`, so the diff is available without a second read.
    An unadjusted inherited row has no base and the list is empty, which is what hides the chip.
  */
  const adjustments = relAdjustments(relationship);
  /*
    `rels.r-from` is the ancestor's NAME alone (`{r.from}`, `type-composer.jsx:1382`),
    not a sentence about it: the row already says it is inherited (`data-inherited`),
    and an "Inherited from …" tag wide enough to say so twice pushes the target —
    the thing the row is for — off the meta line at 1440. The glyph carries the
    direction; the name is what a reader wants to find in the index.
  */
  const provenanceTag = relationship.is_inherited ? (
    <TagAtom
      tone="info"
      size="small"
      icon="pi-arrow-up-right"
      label={resolveAncestorName(relationship.inherited_from_schema, lineage, localize, t) ?? t('studio.typeEditor.row.unknownAncestor')}
      className="type-relationship-row__provenance"
      data-datum="rels.r-from"
      data-kind="chip"
    />
  ) : (
    /*
      Translation coverage on a declared relationship's name (`type-composer.jsx:1387`,
      `LocChip`) — the same chip a declared property and the type name carry. An
      inherited row carries its provenance instead: the name is the ancestor's to
      translate. An `Own` tag here would only restate `data-inherited`.
    */
    onOpenTranslations && (
      <TranslationCoverageChipAtom
        done={0}
        total={name.trim() ? 1 : 0}
        label={t('studio.typeEditor.relationships.translations', { name })}
        onClick={onOpenTranslations}
        data-datum="rels.chip"
        data-testid={`type-relationship-translations-${relationship.system_name}`}
      />
    )
  );

  return (
    <div
      key={relationship.system_name}
      data-testid={`type-relationship-row-${relationship.system_name}`}
      data-container="list > each(rows) > list-item"
      className="type-relationship-row"
    >
      <span className="type-relationship-row__name" data-datum="rels.r-name" data-kind="label">
        {name}
      </span>
      {/*
        `{r.cardinality} → <b>target</b>` on one muted line (`t-line-meta`,
        `type-composer.jsx:1373`). The cardinality is the row's, not the sheet's: five
        relationships should read as five shapes without opening any of them.
      */}
      <span className="type-relationship-row__meta" data-datum="rels.x" data-kind="text">
        {t(`studio.typeEditor.relationships.cardinality.${relationship.cardinality}`)} {t('studio.typeEditor.relationships.arrow')}{' '}
        <b className="type-relationship-row__target" data-datum="rels.window-reltargetlabel-r-scope" data-kind="strong">
          {targetLabel}
        </b>
      </span>
      {relationship.required && <TagAtom tone="outline" size="small" label={t('studio.typeEditor.row.required')} data-datum="rels.req" data-kind="chip" />}
      {/*
        The adjusted mark, before the provenance chip (contract order 4, ahead of
        `rels.r-from` at 5). `title` names WHAT was adjusted — the count alone says
        the inherited declaration no longer stands here without saying in which way,
        and the sheet is a click away rather than the only place to find out.
      */}
      {relationship.is_inherited && adjustments.length > 0 && (
        <span
          className="type-relationship-row__adjusted"
          data-datum="rels.adjusted"
          data-kind="text"
          title={t('studio.typeEditor.relationships.adjustedHereTitle', {
            fields: adjustments.map((a) => t(`studio.typeEditor.relationships.adjustment.${a}`)).join(', '),
          })}
        >
          {t('studio.typeEditor.relationships.adjustedCount', { count: adjustments.length })}
        </span>
      )}
      {provenanceTag}
      {onAdjust && (
        <ButtonAtom
          variant="text"
          size="small"
          shape="icon-square"
          icon="pi pi-sliders-h"
          className="type-relationship-row__icon-btn"
          aria-label={t('studio.typeEditor.relationships.adjustRowAria', { name })}
          data-testid={`type-relationship-adjust-${relationship.system_name}`}
          data-datum="rels.button"
          data-kind="button"
          onClick={onAdjust}
        />
      )}
      {/*
        A declared row's way in. The prototype's row is clickable, but a whole row is not an
        affordance a keyboard user can find by tabbing past the rows, and a row that is itself
        a button cannot hold the buttons it does (the chip, adjust, this) — so the row is a
        container and the contract's explicit press (`rels.button#2`, beside the inherited
        row's adjust) is the one way in.
      */}
      {onEdit && (
        <ButtonAtom
          variant="text"
          size="small"
          shape="icon-square"
          icon="pi pi-pencil"
          className="type-relationship-row__icon-btn"
          aria-label={t('studio.typeEditor.relationships.editRowAria', { name })}
          data-testid={`type-relationship-edit-${relationship.system_name}`}
          data-datum="rels.button#2"
          data-kind="button"
          onClick={onEdit}
        />
      )}
    </div>
  );
}

function RelationshipsSection({
  definition,
  lineage,
  t,
  localize,
  locked,
  onSaveField,
  onOpenSection,
  editingTarget,
  onEditingTargetChange,
  editingPortalNode,
  onRequestCloseEditing,
  onEditingDirtyChange,
  overrideTarget,
  onOverrideTargetChange,
  overridePortalNode,
  onRequestCloseOverrideEditing,
  onOverrideEditingDirtyChange,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  onOpenSection?: (sectionId: string) => void;
  /**
   * `sel.kind === "relationship"` — the relationship whose sheet the rail currently holds:
   * `null` (closed), `'new'` (authoring a brand-new relationship), or the declared row being
   * edited (`SphereTypeComposerOrganism`'s own state, lifted the same way `ruleEditTarget` was —
   * EAINT-933 Phase 3.2). This section still owns `rows` and the sheet's save wiring; only which
   * relationship's sheet is open, and where its markup mounts, live above it.
   */
  editingTarget: SchemaRelationship | 'new' | null;
  onEditingTargetChange: (target: SchemaRelationship | 'new' | null) => void;
  /** The rail's portal target for the relationship sheet once `editingTarget` is set — same seam
   * as `ruleEditPortalNode`. */
  editingPortalNode: HTMLDivElement | null;
  /**
   * The sheet's Cancel button and its own Escape/dismiss both close through this instead of
   * `onEditingTargetChange(null)` directly, so the composer's dirty guard (EAINT-933 Phase 3.3,
   * extended from the `start` kind to `relationship`) sees every close attempt, not just the rail
   * head's. Opening still goes through `onEditingTargetChange` — only closing is guarded.
   */
  onRequestCloseEditing: () => void;
  /** Reports the open sheet's dirtiness up to the composer, which owns the confirm. */
  onEditingDirtyChange: (dirty: boolean) => void;
  /**
   * `sel.kind === "relOverride"` — the INHERITED row whose override sheet the rail currently
   * holds, `null` closed. Lifted the same way `editingTarget` was, for the same reason: this
   * section still owns `rows` and `saveAdjustment`; only which row's override sheet is open, and
   * where its markup mounts, live above it.
   */
  overrideTarget: SchemaRelationship | null;
  onOverrideTargetChange: (target: SchemaRelationship | null) => void;
  /** The rail's portal target for the override sheet once `overrideTarget` is set — same seam as
   * `editingPortalNode`. */
  overridePortalNode: HTMLDivElement | null;
  /**
   * The override sheet's Cancel/dismiss both close through this instead of
   * `onOverrideTargetChange(null)` directly, so the composer's dirty guard (EAINT-933 Phase 3.3,
   * extended from `relationship` to `relOverride`) sees every close attempt, not just the rail
   * head's. Same seam as `onRequestCloseEditing`.
   */
  onRequestCloseOverrideEditing: () => void;
  /** Reports the open override sheet's dirtiness up to the composer, which owns the confirm. */
  onOverrideEditingDirtyChange: (dirty: boolean) => void;
}) {
  const onOpenTranslations = onOpenSection ? () => onOpenSection('languages') : undefined;
  const editable = !locked && !!onSaveField;
  const inherited = inheritedRelationships(definition);
  const apiClients = useApiClientsOrNull();
  const typeName = localize(definition.localization_name_key, definition.name);

  const [rows, setRows] = useState<SchemaRelationship[]>(() => declaredRelationships(definition));

  /*
    The sheet's "What can be on the other end" section is the platform's applicability
    editor, and that editor picks families/types from the org's catalog. The composer owns
    the fetch — a molecule that fetches its own catalog can't be composed anywhere else —
    but only while the sheet is OPEN: `null` apiClients skips the request, so reading the
    relationships list never pulls the org-wide type catalog it has no use for.
  */
  const {
    schemas: orgTypeSchemas,
    sources: typeSources,
    approachSources,
  } = useOrgAvailableTypes(editingTarget === null && overrideTarget === null ? null : apiClients);

  useEffect(() => {
    setRows(declaredRelationships(definition));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.system_name, definition.version_id]);

  const declared = editable ? rows : declaredRelationships(definition);

  /*
    The add row (`rels > add-row`): a wrapper (`rels.add-row`) holding the button
    (`rels.add-a-relationship`) and the hint beside it, which states the one thing
    declaring a relationship commits every subtype to. The contract puts that
    sentence HERE, next to the affordance that causes it — it used to sit under
    the inherited group, where it read as a fact about what arrived rather than
    as a consequence of what you are about to add.
  */
  const addRow = editable ? (
    <div className="sphere-type-editor__rels-addrow" data-datum="rels.add-row" data-kind="add-row">
      <ButtonAtom
        variant="secondary"
        size="small"
        icon="pi pi-plus"
        label={t('studio.typeEditor.relationships.addRelationship')}
        className="sphere-type-editor__rels-add-btn"
        onClick={() => onEditingTargetChange('new')}
        data-testid="relationships-add"
        data-datum="rels.add-a-relationship"
        data-kind="button"
      />
      <span className="sphere-type-editor__rels-addrow-hint" data-datum="rels.arrives-on-every-subtype-and-can-t-be-re" data-kind="hint">
        {t('studio.typeEditor.relationships.arrivesOnEverySubtype')}
      </span>
    </div>
  ) : null;

  if (declared.length === 0 && inherited.length === 0) {
    /*
      Not an `EmptyStateAtom`: the contract states this as a NOTE inside the
      list (`rels > list`, `relRows=none`), and the section keeps its add row —
      "no relationships" is a starting point in edit mode, not a dead end. The
      atom replaced the whole section, add row included, so an empty type had
      no way to declare its first relationship.
    */
    return (
      <div data-testid="sphere-type-relationships">
        <div className="sphere-type-editor__rel-list" data-container="relationships > list" data-testid="relationships-list">
          <p className="sphere-type-editor__list-empty" data-datum="rels.no-relationships-spheres-of-this-type-st" data-kind="note">
            {t('studio.typeEditor.relationships.empty')}
          </p>
        </div>
        {addRow}
        {editable &&
          editingTarget !== null &&
          editingPortalNode &&
          createPortal(
            <RelationshipSheetMolecule
              visible
              railMode
              relationship={null}
              typeName={typeName}
              onHide={onRequestCloseEditing}
              onDirtyChange={onEditingDirtyChange}
              onSave={saveFromSheet}
              familyTypeSchemas={orgTypeSchemas}
              sources={typeSources}
              approachSources={approachSources}
            />,
            editingPortalNode
          )}
      </div>
    );
  }

  function commitRows(next: SchemaRelationship[]) {
    setRows(next);
    onSaveField?.({ relationships: next });
  }

  /*
    Saving an adjustment writes a full declaration into this type's OWN
    `relationships[]` — the same REPLACE array a declared relationship lives in. The
    merge collapses it back onto the ancestor's by `system_name` on the next read, so
    the row returns as inherited-and-adjusted. There is no separate patch store: a
    patch beside the array would be invisible to graph-service's target validation,
    which reads `target_filter` off the merged relationship, and the narrowing a
    subtype authored would never actually be enforced.
  */
  function saveAdjustment(base: SchemaRelationship, patch: RelationshipOverridePatch | null) {
    const systemName = base.system_name;
    const declared = rows.some((r) => r.system_name === systemName);
    if (patch === null) {
      // Nothing adjusted: drop this type's declaration so the row reads as the ancestor's again.
      if (declared) commitRows(rows.filter((r) => r.system_name !== systemName));
    } else {
      const applied = relApplyOverride(base.adjusted_from ?? base, patch, relBaseTargetIds(base));
      commitRows(declared ? rows.map((r) => (r.system_name === systemName ? applied : r)) : [...rows, applied]);
    }
    onOverrideTargetChange(null);
  }

  function saveFromSheet(patch: RelationshipSheetPatch) {
    if (editingTarget === 'new') {
      commitRows([
        ...rows,
        {
          ...patch,
          system_name: `relationship_${rows.length + 1}_${Date.now()}`,
          rules: {},
          label: patch.name,
          label_key: '',
          is_inherited: false,
          inherited_from_schema: null,
        },
      ]);
    } else if (editingTarget) {
      const systemName = editingTarget.system_name;
      commitRows(rows.map((relationship) => (relationship.system_name === systemName ? { ...relationship, ...patch } : relationship)));
    }
    onEditingTargetChange(null);
  }

  /*
    ONE flat list (`rels > list > each(relRows) > list-item`, `type-composer.jsx:1369`):
    this type's own relationships first, then what the chain brings, each inherited
    row saying so itself (`data-inherited`, `rels.r-from`). Relationships are additive
    on inheritance — a subtype gets these plus its own — so a reader wants the whole
    set a sphere of this type will carry, in one sequence. The Declared / Inherited
    split Own properties uses is that block's contract (`t-propgroup`); it is not this
    one's, and grouping is a binding axis.
  */
  return (
    <div data-testid="sphere-type-relationships">
      <div className="sphere-type-editor__rel-list" data-container="relationships > list" data-testid="relationships-list">
        {declared.map((relationship) =>
          relationshipListRow(
            relationship,
            t,
            localize,
            lineage,
            editable ? () => onEditingTargetChange(relationship) : undefined,
            undefined,
            onOpenTranslations
          )
        )}
        {inherited.map((relationship) =>
          relationshipListRow(
            relationship,
            t,
            localize,
            lineage,
            undefined,
            // An inherited relationship is never EDITED here — it can only be
            // adjusted, and only in the one direction that keeps the ancestor's
            // promise true. That is the override sheet, not the declare sheet.
            editable ? () => onOverrideTargetChange(relationship) : undefined
          )
        )}
      </div>
      {addRow}
      {editable &&
        editingTarget !== null &&
        editingPortalNode &&
        createPortal(
          <RelationshipSheetMolecule
            visible
            railMode
            relationship={editingTarget === 'new' ? null : editingTarget}
            typeName={typeName}
            onHide={onRequestCloseEditing}
            onDirtyChange={onEditingDirtyChange}
            onSave={saveFromSheet}
            onRemove={
              editingTarget && editingTarget !== 'new'
                ? () => {
                    const systemName = editingTarget.system_name;
                    commitRows(rows.filter((relationship) => relationship.system_name !== systemName));
                    onEditingTargetChange(null);
                  }
                : undefined
            }
            familyTypeSchemas={orgTypeSchemas}
            sources={typeSources}
            approachSources={approachSources}
          />,
          editingPortalNode
        )}
      {overrideTarget &&
        overridePortalNode &&
        createPortal(
          <RelationshipOverrideSheetMolecule
            visible
            railMode
            base={overrideTarget.adjusted_from ?? overrideTarget}
            from={resolveAncestorName(overrideTarget.inherited_from_schema, lineage, localize, t) ?? t('studio.typeEditor.row.unknownAncestor')}
            typeName={typeName}
            override={relOverrideFromRow(overrideTarget)}
            onHide={onRequestCloseOverrideEditing}
            onDirtyChange={onOverrideEditingDirtyChange}
            onSave={(patch) => saveAdjustment(overrideTarget, patch)}
            familyTypeSchemas={orgTypeSchemas}
            sources={typeSources}
            approachSources={approachSources}
          />,
          overridePortalNode
        )}
    </div>
  );
}

function PerspectivesSection({
  definition,
  lineage,
  t,
  localize,
  locked,
  onSaveField,
  openTarget,
  onOpenTargetChange,
  editingPortalNode,
  initialConfirmDeletePerspective,
  initialDraftingPerspective,
  initialAskSuppressPerspective,
  initialDeletedPerspectives,
  initialEdits,
  initialDraft,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  /**
   * `sel.kind === "perspective"` — the LIVE row whose editor the rail currently holds, `null`
   * closed (`SphereTypeComposerOrganism`'s own state, lifted the same way `overrideTarget` was —
   * EAINT-933 Phase 3.2, `persp > list > each(live) > <Row> > list-item > <PerspEditor>` rebinds
   * to `side`, Waivers section 2026-09-15). This section still owns `rows`/`edits` and every
   * other lever the row's open body exposes; only which row is open, and where its editor
   * mounts, lives above it. The *Not on this type* group never opens (a dropped row's `canOpen`
   * is always false), so it keeps comparing against this same value with no portal of its own.
   */
  openTarget: SchemaPerspective | null;
  onOpenTargetChange: (target: SchemaPerspective | null) => void;
  /** The rail's portal target for the open row's editor once `openTarget` is set — same seam as
   * `overridePortalNode`. */
  editingPortalNode: HTMLDivElement | null;
  /** The perspective whose delete confirm stands open on mount, by `system_name`. */
  initialConfirmDeletePerspective?: string;
  /** True when the declare form stands open on mount — the add row's other way in. */
  initialDraftingPerspective?: boolean;
  /** The inherited perspective whose SUPPRESS callout stands open on mount, by `system_name`. */
  initialAskSuppressPerspective?: string;
  /** Declarations already deleted this session on mount — the undo state, by `system_name`. */
  initialDeletedPerspectives?: string[];
  /**
   * This session's per-row field overlay on mount, keyed by `system_name` — the only seam
   * that renders a row's "changed" state (the `was …` note and the per-field revert) without
   * a live click sequence to produce it.
   */
  initialEdits?: Record<string, Partial<SchemaPerspective>>;
  /** The declare form's own overlay on mount — seeds `draft` so its fields read as changed against `BLANK_PERSPECTIVE`. */
  initialDraft?: Partial<SchemaPerspective>;
}) {
  const editable = !locked && !!onSaveField;
  const typeName = localize(definition.localization_name_key, definition.name);

  const [rows, setRows] = useState<SchemaPerspective[]>(() => declaredPerspectives(definition));
  const openSystemName = openTarget?.system_name ?? null;
  const [confirmSystemName, setConfirmSystemName] = useState<string | null>(initialConfirmDeletePerspective ?? null);
  /*
    This session's field overlay, keyed by `system_name`. The merged read is the BASELINE and
    is never mutated: the contract's editor states what each field would inherit and offers a
    per-field revert (`type-perspectives.jsx:82-96`), which is only answerable while the
    baseline still exists beside the change.
  */
  const [edits, setEdits] = useState<Record<string, Partial<SchemaPerspective>>>(initialEdits ?? {});
  const [drafting, setDrafting] = useState<boolean>(initialDraftingPerspective ?? false);
  const [draft, setDraft] = useState<Partial<SchemaPerspective>>(initialDraft ?? {});
  /*
    The suppress ASK, and the reason typed into it. Suppression is the one lever a global
    answers to and it beats everything else, so the contract makes it a stated act with a
    reason rather than a row button that removes something silently
    (`type-perspectives.jsx:379-401`).
  */
  const [askSuppress, setAskSuppress] = useState<string | null>(initialAskSuppressPerspective ?? null);
  const [suppressWhy, setSuppressWhy] = useState('');
  /*
    Declarations this session deleted, kept in the list with a `deleted` chip and an undo
    rather than vanishing (`type-perspectives.jsx:195-199, 309-313`). A perspective is the
    permission surface of every sphere of this type and of every subtype, so a delete with no
    way back is the one destructive act on this surface that must not be silent (ux-craft I4).
  */
  const [deletedNames, setDeletedNames] = useState<string[]>(initialDeletedPerspectives ?? []);

  // Re-baseline on a different type or a new version only — never on a conflict,
  // which must leave in-progress local edits alone. The `initial*` seams are mount
  // state, so this effect must not run on mount (Phase 2 slice 18: an effect that
  // re-baselined on mount cleared them before the first paint).
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setRows(declaredPerspectives(definition));
    onOpenTargetChange(null);
    setConfirmSystemName(null);
    setEdits({});
    setDrafting(false);
    setDraft({});
    setAskSuppress(null);
    setSuppressWhy('');
    setDeletedNames([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.system_name, definition.version_id]);

  const declared = editable ? rows : declaredPerspectives(definition);
  const inherited = inheritedPerspectives(definition);

  const guidanceBySystemName = new Map((definition.structural_guidance?.perspectives ?? []).map((entry) => [entry.perspectiveSystemName, entry] as const));
  const mode: TypeFieldRowMode = editable ? 'edit' : locked ? 'locked' : 'disabled';

  /*
    What actually persists is the session's declarations MINUS the ones deleted and not
    undone. The delete is held in session state rather than applied to `rows`, because the row
    has to stay in the list to carry its `deleted` chip and its undo — a delete that removes
    the row outright leaves nothing to undo from.
  */
  function saveDeclarations(nextRows: SchemaPerspective[], nextDeleted: string[]) {
    setRows(nextRows);
    setDeletedNames(nextDeleted);
    onSaveField?.({ perspectives: nextRows.filter((perspective) => !nextDeleted.includes(perspective.system_name)) });
  }

  function commitRows(next: SchemaPerspective[]) {
    saveDeclarations(next, deletedNames);
  }

  /*
    The editor's field vocabulary → the `SchemaPerspective` fields it overlays. Declared once
    here rather than inside each handler: the same map answers an edit and a revert, and a
    revert that used a different key would silently keep the change it claimed to drop.
  */
  const EDITOR_FIELD: Record<PerspectiveEditorField, keyof SchemaPerspective> = {
    name: 'label',
    description: 'description',
    cardinality: 'cardinality',
    filter: 'assignee_filter',
    permissions: 'permissions',
    required: 'required',
  };

  /** Baseline + this session's overlay — what the engine would produce, and what is persisted. */
  const withEdits = (perspective: SchemaPerspective): SchemaPerspective => ({
    ...perspective,
    ...(edits[perspective.system_name] ?? {}),
  });

  function editAt(systemName: string, field: PerspectiveEditorField, value: unknown) {
    setEdits((prev) => {
      const next = { ...prev, [systemName]: { ...(prev[systemName] ?? {}), [EDITOR_FIELD[field]]: value } };
      onSaveField?.({
        perspectives: declaredPerspectives(definition)
          .filter((p) => !deletedNames.includes(p.system_name))
          .map((p) => ({ ...p, ...(next[p.system_name] ?? {}) })),
      });
      return next;
    });
  }

  /** `null` drops the whole overlay — the follow-the-ancestor-again lever, one click for the row. */
  function revertAt(systemName: string, field: PerspectiveEditorField | null) {
    setEdits((prev) => {
      const next = { ...prev };
      if (field === null) {
        delete next[systemName];
      } else {
        const row = { ...(next[systemName] ?? {}) };
        delete row[EDITOR_FIELD[field]];
        if (Object.keys(row).length) next[systemName] = row;
        else delete next[systemName];
      }
      onSaveField?.({
        perspectives: declaredPerspectives(definition)
          .filter((p) => !deletedNames.includes(p.system_name))
          .map((p) => ({ ...p, ...(next[p.system_name] ?? {}) })),
      });
      return next;
    });
  }

  /*
    ONE list, in the order the engine resolves them (`type-perspectives.jsx:341`) — not the
    declared/inherited split this section carried through Phase 1. Each row's chips already
    say where the perspective came from; splitting the list into two groups says it a second
    time, and hides the fact that a redeclaration OVERLAYS the ancestor's row rather than
    adding a second one beside it.
  */
  const liveAll = [...inherited, ...declared];

  /*
    A declaration deleted THIS SESSION stops being live and is listed where every other removal
    is listed (`type-perspectives.jsx:187-202`: `dropped: del ? "deleted" : r.dropped`, then
    `live = merged.filter(r => !r.dropped)`). Leaving it in the live list would read as still
    arriving, which is the one thing it no longer does. It is the only drop this surface authors
    that has not reached the server, so the undo beside it is free — nothing downstream has seen it.
  */
  const sessionDeleted = liveAll.filter((perspective) => deletedNames.includes(perspective.system_name));
  const live = liveAll.filter((perspective) => !deletedNames.includes(perspective.system_name));

  /*
    The permission words this type's perspectives actually grant, resolved server-side from the
    SpiceDB schema and observed across the merged read. The design system restates no second
    list of its own — a second list is how the two came to disagree. It is an OBSERVED
    vocabulary, not a declared one: a word no perspective on this chain grants cannot be picked
    here yet, which is the read-shape dependency recorded in the plan.
  */
  // Explicit collator rather than the default sort: these are permission names shown to a
  // person, so the order should be the locale's, not UTF-16 code-unit order (S2871).
  const permissionVocabulary = [...new Set(live.flatMap((perspective) => perspective.permissions ?? []))].sort((a, b) => a.localeCompare(b));

  /*
    The kinds of thing that may hold a standing, read from the descriptors the schema read
    already resolves for every perspective (`structural_guidance`), so the picker offers the
    vocabulary the merge itself uses rather than a hard-coded type list.
  */
  const assigneeKinds: PerspectiveAssigneeKind[] = [
    ...new Map(
      (definition.structural_guidance?.perspectives ?? [])
        .flatMap((entry) => entry.validAssignees)
        .map((assignee) => [`${assignee.property}:${assignee.value}`, { property: assignee.property, value: assignee.value, label: assignee.value }] as const)
    ).values(),
  ];

  const confirmTarget = declared.find((perspective) => perspective.system_name === confirmSystemName) ?? null;

  /*
    The perspectives an ancestor declares but this type does NOT receive, and the three levers
    that decide it — surfaced by the merged read as siblings of `perspectives` (EAINT-897).
    Before this existed the merge simply discarded them, so a person reading the ancestor came
    here looking for a perspective and found no trace of it, let alone why.
  */
  const gone = definition.droppedPerspectives ?? [];
  /** The group as it renders: the drops the merge reports, then this session's own deletions. */
  const goneRows = [...gone, ...sessionDeleted];
  const inheritance = definition.perspectiveInheritance;
  const lockOn = inheritance?.lock === true;
  const suppressed = inheritance?.suppress ?? [];
  const keepsOwnAllowList = Array.isArray(inheritance?.allowList);
  const hasParent = !!definition.parent_schema_id;
  const cutByLock = gone.filter((perspective) => perspective.dropped === 'lock').length;
  const askTarget = [...live, ...gone].find((perspective) => perspective.system_name === askSuppress) ?? null;

  /** Why a row is not on this type, in the words the person reading the ancestor needs. */
  const dropReasons = (perspective: SchemaPerspective): string[] => {
    if (!perspective.dropped) return [];
    const from = resolveAncestorName(perspective.inherited_from_schema, lineage, localize, t);
    return [t(`studio.typeEditor.perspectives.dropReason.${perspective.dropped}`, { type: typeName, from })];
  };

  /** The three levers are set-only fields of their own — never part of the `perspectives` array. */
  function setSuppressList(next: string[]) {
    onSaveField?.({ suppressPerspectives: next.length ? next : null });
  }

  /*
    The accepted list IN FORCE right now: this type's own if it keeps one, otherwise the
    nearest ancestor's, otherwise no list at all — which the merge reads as "everything
    inherited is accepted" (`fetchAndMergeDefinitions.ts:391-395`).

    When no list is in force it has to be MATERIALISED from what currently arrives before a
    single name can be refused. The contract's own handler just toggles membership of a
    possibly-empty list (`type-perspectives.jsx:226-229`), which on a type that keeps no list
    would turn "accept everything" into "accept only this one" — refusing one perspective by
    silently refusing every other. The materialised baseline is every inherited non-global
    perspective the type receives, plus the ones it suppresses: suppression is a different
    lever with its own undo, and a name dropped by it is still ACCEPTED.
  */
  const acceptedNames: string[] =
    inheritance?.allowList ??
    inheritance?.inheritedAllowList ??
    [...live, ...gone.filter((perspective) => perspective.dropped === 'suppress')]
      .filter((perspective) => perspective.is_inherited && !perspective.global)
      .map((perspective) => perspective.system_name);

  /** An accepted list is authored as a whole; `inheritPerspectives` is REPLACE, never a patch. */
  function setAcceptedList(next: string[]) {
    onSaveField?.({ inheritPerspectives: [...new Set(next)] });
  }

  return (
    <div data-testid="sphere-type-perspectives">
      {/*
        `persp > list` (`type-perspectives.jsx:342`, `.p-list`): one bordered list of divided
        rows. The empty answer inside it is a NOTE, not an `EmptyStateAtom` that replaces the
        section — the same defect the own-properties and business-rules lists carried until
        Phase 2 slices 18 and 19: swapping the section for an empty state takes the add row
        with it, so a type with no perspectives could not declare its first one.
      */}
      <div className="sphere-type-editor__persp-list" data-container="perspectives > list" data-testid="perspectives-list">
        {live.length === 0 ? (
          <p className="sphere-type-editor__list-empty" data-testid="perspectives-empty">
            {t('studio.typeEditor.perspectives.empty')}
          </p>
        ) : (
          live.map((perspective) => {
            const isOwn = !perspective.is_inherited;
            return (
              <TypePerspectiveRowMolecule
                key={perspective.system_name}
                container="perspectives > list > each(live)"
                perspective={withEdits(perspective)}
                guidance={guidanceBySystemName.get(perspective.system_name)}
                inheritedFromName={resolveAncestorName(perspective.inherited_from_schema, lineage, localize, t)}
                mode={isOwn ? mode : locked ? 'locked' : 'disabled'}
                typeName={typeName}
                isOpen={openSystemName === perspective.system_name}
                onToggle={() => onOpenTargetChange(openSystemName === perspective.system_name ? null : perspective)}
                editorPortalNode={editingPortalNode}
                /*
                A platform-owned perspective and an inherited one are not this type's to
                delete: the first is the permission floor, the second belongs to the type
                that declared it — removing that one is a SUPPRESSION, a different act with
                no persistence on the read yet (see the plan's perspectives dependency).
              */
                onDelete={editable && isOwn ? () => setConfirmSystemName(perspective.system_name) : undefined}
                /*
                A declaration deleted this session stays in the list and says so, with the one
                act left on it. It is the only drop this surface authors that has not reached
                the server yet, so the undo is free — nothing downstream has seen it.
              */
                /*
                An inherited perspective is not this type's to delete — removing it is a
                SUPPRESSION, a different act with a different consequence (it reaches every
                subtype, and it is the only lever a global answers to), so it asks for a
                reason first. A platform-owned row is the permission floor and answers to
                neither.
              */
                onSuppress={
                  editable && !isOwn && perspective.is_system !== true
                    ? () => {
                        setAskSuppress(perspective.system_name);
                        setSuppressWhy('');
                      }
                    : undefined
                }
                /*
                Refusing is the ACCEPTED-LIST lever and it only answers for an inherited,
                non-global row: a global reaches every descendant whatever the list says
                (`fetchAndMergeDefinitions.ts:391`), so offering it here would be an act that
                does nothing. Suppression is the lever there, and it sits beside this one.
              */
                onRefuse={
                  editable && !isOwn && !perspective.global && perspective.is_system !== true
                    ? () => setAcceptedList(acceptedNames.filter((name) => name !== perspective.system_name))
                    : undefined
                }
                base={perspective}
                edits={edits[perspective.system_name]}
                permissionVocabulary={permissionVocabulary}
                assigneeKinds={assigneeKinds}
                onEdit={editable ? (field, value) => editAt(perspective.system_name, field, value) : undefined}
                onRevert={editable ? (field) => revertAt(perspective.system_name, field) : undefined}
              />
            );
          })
        )}
      </div>

      {/*
        The add row, in the shape the contract gives it (`type-perspectives.jsx:344-350`): the
        dashed row IS the affordance and the hint sits inside it — the same
        wrapper-is-the-affordance reading settled for the compose block (Phase 2 slice 8).
      */}
      {editable && !drafting && (
        <div className="sphere-type-editor__props-addrow" data-datum="persp.add-row" data-kind="add-row">
          <ButtonAtom
            variant="secondary"
            size="small"
            icon="pi pi-plus"
            label={t('studio.typeEditor.perspectives.addPerspective')}
            onClick={() => {
              setDraft({});
              setDrafting(true);
              onOpenTargetChange(null);
            }}
            data-testid="perspectives-add"
            data-datum="persp.declare-a-perspective"
            data-kind="button"
          />
          <span className="sphere-type-editor__props-addrow-hint" data-datum="persp.a-new-one-reaches-every-subtype-of" data-kind="hint">
            {t('studio.typeEditor.perspectives.addPerspectiveHint', { name: typeName })}
          </span>
        </div>
      )}

      {/*
        A new declaration is authored in the SAME editor an existing one opens into
        (`type-perspectives.jsx:352-378`) — one surface for one shape, so nothing is
        configurable in the creation form that is not editable afterwards.
      */}
      {editable && drafting && (
        <PerspectiveEditorMolecule
          variant="declare"
          base={BLANK_PERSPECTIVE}
          edits={draft}
          typeName={typeName}
          permissionVocabulary={permissionVocabulary}
          assigneeKinds={assigneeKinds}
          parity={PERSPECTIVE_DECLARE_PARITY}
          onEdit={(field, value) => setDraft((prev) => ({ ...prev, [EDITOR_FIELD[field]]: value }))}
          onRevert={(field) =>
            setDraft((prev) => {
              if (field === null) return {};
              const next = { ...prev };
              delete next[EDITOR_FIELD[field]];
              return next;
            })
          }
          onDeclare={() => {
            const declaredRow = { ...BLANK_PERSPECTIVE, ...draft } as SchemaPerspective;
            declaredRow.name = declaredRow.label;
            declaredRow.system_name = perspectiveSystemName(definition.system_name, declaredRow.label);
            commitRows([...rows, declaredRow]);
            setDrafting(false);
            setDraft({});
          }}
          onCancel={() => {
            setDrafting(false);
            setDraft({});
          }}
        />
      )}

      {/*
        `type-perspectives.jsx:404-422` — the delete confirm is a callout INSIDE the section,
        not a modal, and it names what is lost: every subtype, and the access the perspective
        grants. A modal over the list hides the row being talked about.
      */}
      {confirmTarget && (
        <InlineConfirmMolecule
          tone="danger"
          icon="pi-trash"
          testId="perspectives-delete-confirm"
          datum="persp.callout#2"
          titleId="sphere-type-persp-confirm-title"
          title={t('studio.typeEditor.perspectives.remove.confirmTitle', {
            name: localize(confirmTarget.label_key, confirmTarget.label || confirmTarget.name),
          })}
          titleTestId="perspectives-delete-confirm-title"
          titleDatum="persp.delete"
          body={t('studio.typeEditor.perspectives.remove.confirmMessage', { type: typeName })}
          bodyTestId="perspectives-delete-confirm-body"
          bodyDatum="persp.declares-it-so-deleting-removes-it-from-"
          actions={[
            {
              label: t('studio.typeEditor.perspectives.remove.confirmAccept'),
              onClick: () => {
                saveDeclarations(rows, [...deletedNames, confirmTarget.system_name]);
                onOpenTargetChange(null);
                setConfirmSystemName(null);
              },
              variant: 'danger',
              'data-testid': 'perspectives-delete-confirm-accept',
              'data-datum': 'persp.delete-it',
            },
            {
              label: t('studio.typeEditor.perspectives.remove.keepIt'),
              onClick: () => setConfirmSystemName(null),
              variant: 'text',
              'data-testid': 'perspectives-delete-confirm-dismiss',
              'data-datum': 'persp.keep-it',
            },
          ]}
        />
      )}

      {/*
        `type-perspectives.jsx:379-401` — the suppress ASK. Suppression applies last and beats
        everything, including the accepted list and a global's own reach, so it states where the
        perspective arrives from and takes a reason before it happens. The reason is ours, not
        the platform's: `suppressPerspectives` stores system_names only, and the note under the
        ask says so rather than letting a person believe the sentence ships with the type.
      */}
      {editable && askTarget && (
        <InlineConfirmMolecule
          tone="warning"
          icon="pi-minus-circle"
          testId="perspectives-suppress-ask"
          datum="persp.callout"
          titleId="sphere-type-persp-suppress-title"
          title={t('studio.typeEditor.perspectives.suppress.title', {
            name: localize(askTarget.label_key, askTarget.label || askTarget.name),
            type: typeName,
          })}
          titleTestId="perspectives-suppress-title"
          titleDatum="persp.suppress"
          body={t(askTarget.global ? 'studio.typeEditor.perspectives.suppress.bodyGlobal' : 'studio.typeEditor.perspectives.suppress.body', {
            from: resolveAncestorName(askTarget.inherited_from_schema, lineage, localize, t),
          })}
          bodyTestId="perspectives-suppress-body"
          bodyDatum="persp.it-arrives-from"
          actions={[
            {
              label: t('studio.typeEditor.perspectives.suppress.accept'),
              onClick: () => {
                setSuppressList([...suppressed, askTarget.system_name]);
                setAskSuppress(null);
                setSuppressWhy('');
              },
              variant: 'primary',
              disabled: !suppressWhy.trim(),
              'data-testid': 'perspectives-suppress-accept',
              'data-datum': 'persp.suppress-it',
            },
            {
              label: t('studio.typeEditor.perspectives.suppress.cancel'),
              onClick: () => setAskSuppress(null),
              variant: 'text',
              'data-testid': 'perspectives-suppress-cancel',
              'data-datum': 'persp.cancel#2',
            },
          ]}
        >
          {/* Every input carries an associated label, even where the contract shows only a
              placeholder — a placeholder disappears the moment anyone types (ux-craft F1). */}
          <label className="sphere-type-editor__field-label" htmlFor="sphere-type-persp-suppress-why">
            {t('studio.typeEditor.perspectives.suppress.whyLabel')}
          </label>
          <InputAtom
            id="sphere-type-persp-suppress-why"
            placeholder={t('studio.typeEditor.perspectives.suppress.whyPlaceholder')}
            value={suppressWhy}
            onChange={(event) => setSuppressWhy(event.target.value)}
            data-testid="perspectives-suppress-why"
            data-datum="persp.e-g-a-vendor-hangs-under-nothing"
            data-kind="input"
          />
          <p
            className="sphere-type-editor__section-note"
            data-testid="perspectives-suppress-note"
            data-datum="persp.the-reason-is-kept-with-the-type-for-who"
            data-kind="hint"
          >
            {t('studio.typeEditor.perspectives.suppress.reasonNote')}
          </p>
        </InlineConfirmMolecule>
      )}

      {/*
        `type-perspectives.jsx:424-434` — *Not on this type*. These perspectives exist upstream
        and do not reach this type; showing them is the whole point of surfacing the drops on
        the read (EAINT-897). A person reading the ancestor comes here looking for them, and an
        empty space is indistinguishable from "the ancestor never had one".
      */}
      {goneRows.length > 0 && (
        <div className="sphere-type-editor__persp-gone" data-testid="perspectives-gone">
          <p className="sphere-type-editor__persp-gone-head">
            <i className="pi pi-minus-circle" aria-hidden="true" data-datum="persp.chip#2" data-kind="chip" />
            <span data-datum="persp.not-on-this-type" data-kind="heading">
              <b data-datum="persp.not-on-this-type#2" data-kind="strong">
                {t('studio.typeEditor.perspectives.gone.title')}
              </b>
            </span>
            <span className="sphere-type-editor__props-addrow-hint" data-datum="persp.shown-because-they-exist-upstream-a-pers" data-kind="hint">
              {t('studio.typeEditor.perspectives.gone.hint')}
            </span>
          </p>
          <div className="sphere-type-editor__persp-list" data-container="perspectives > list#2" data-testid="perspectives-gone-list">
            {goneRows.map((perspective) => (
              <TypePerspectiveRowMolecule
                key={perspective.system_name}
                perspective={perspective}
                container="perspectives > list#2 > each(gone)"
                guidance={guidanceBySystemName.get(perspective.system_name)}
                inheritedFromName={resolveAncestorName(perspective.inherited_from_schema, lineage, localize, t)}
                /*
                Dropped is not locked: a field overlay recorded before (or independent of) the
                drop stays visible and revertable, same as the live list — the contract's global
                `readOnly` gates the editor, not this row's own group (`type-perspectives.jsx:325`
                passes the same page-level `readOnly` to every `<Row>`, live or gone).
              */
                mode={mode}
                typeName={typeName}
                isOpen={openSystemName === perspective.system_name}
                onToggle={() => onOpenTargetChange(openSystemName === perspective.system_name ? null : perspective)}
                base={perspective}
                edits={edits[perspective.system_name]}
                permissionVocabulary={permissionVocabulary}
                assigneeKinds={assigneeKinds}
                onEdit={editable ? (field, value) => editAt(perspective.system_name, field, value) : undefined}
                onRevert={editable ? (field) => revertAt(perspective.system_name, field) : undefined}
                parity={PERSPECTIVE_GONE_ROW_PARITY}
                editorParity={PERSPECTIVE_GONE_ROW_EDITOR_PARITY}
                /*
                A dropped row this type ALSO redeclares still reads as overridden here — the
                redeclaration is not undone by the drop, it just no longer reaches anything.
              */
                overriddenHere={declared.some((own) => own.system_name === perspective.system_name)}
                dropped={deletedNames.includes(perspective.system_name) ? 'deleted' : perspective.dropped}
                reasons={deletedNames.includes(perspective.system_name) ? [t('studio.typeEditor.perspectives.dropReason.deleted')] : dropReasons(perspective)}
                /* The undo for a deletion this session authored — free, because nothing downstream has it. */
                onUndoDelete={
                  editable && deletedNames.includes(perspective.system_name)
                    ? () =>
                        saveDeclarations(
                          rows,
                          deletedNames.filter((name) => name !== perspective.system_name)
                        )
                    : undefined
                }
                /* The undo for the ask above — the only drop this type authored itself. */
                onUnsuppress={
                  editable && perspective.dropped === 'suppress'
                    ? () => setSuppressList(suppressed.filter((name) => name !== perspective.system_name))
                    : undefined
                }
                /*
                The undo for `Refuse it`. Only a row the ACCEPTED LIST dropped can be put back
                by it — a lock-dropped row answers to the policy lever below, and a suppressed
                one to `Stop suppressing it` beside this.
              */
                onAccept={editable && perspective.dropped === 'allow' ? () => setAcceptedList([...acceptedNames, perspective.system_name]) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/*
        `type-perspectives.jsx:437-450` — the lock is the biggest of the five levers, so it
        states its consequence and the count it would cut BEFORE anyone flips it, not after.
      */}
      <div className="sphere-type-editor__persp-policy" data-testid="perspectives-policy" data-locked={lockOn ? '1' : undefined}>
        <i className={lockOn ? 'pi pi-shield' : 'pi pi-sitemap'} aria-hidden="true" />
        {lockOn ? (
          <span>
            <b data-datum="persp.this-type-replaces-the-chain" data-kind="strong">
              {t('studio.typeEditor.perspectives.policy.lockedTitle')}
            </b>{' '}
            {t('studio.typeEditor.perspectives.policy.lockedBody', { count: cutByLock })}
          </span>
        ) : (
          <span>
            <b data-datum="persp.the-chain-flows-through" data-kind="strong">
              {t('studio.typeEditor.perspectives.policy.openTitle')}
            </b>{' '}
            {hasParent
              ? t('studio.typeEditor.perspectives.policy.openBody', {
                  parent: resolveAncestorName(definition.parent_schema_id, lineage, localize, t),
                })
              : t('studio.typeEditor.perspectives.policy.openBodyRoot')}
          </span>
        )}
        {editable && (
          <ButtonAtom
            variant="text"
            size="small"
            label={t(lockOn ? 'studio.typeEditor.perspectives.policy.unlock' : 'studio.typeEditor.perspectives.policy.lock')}
            onClick={() => onSaveField?.({ overridePerspectiveInheritance: lockOn ? null : true })}
            data-testid="perspectives-policy-toggle"
            data-datum="persp.let-the-chain-through"
            data-kind="button"
          />
        )}
      </div>

      {/*
        `type-perspectives.jsx:455-459` — an accepted list is not necessarily this type's own:
        the engine falls back to the nearest ancestor's, so "no list here" does not mean
        "everything arrives". Rendered only where it is true — a type with a parent and no list
        of its own — so it stays a fact rather than boilerplate.
      */}
      {!keepsOwnAllowList && hasParent && (
        <p
          className="sphere-type-editor__section-note"
          data-testid="perspectives-allowlist-note"
          data-datum="persp.this-type-keeps-no-accepted-list-of-its-"
          data-kind="note"
        >
          {t('studio.typeEditor.perspectives.allowListNote', { name: typeName })}
        </p>
      )}

      {/*
        `type-perspectives.jsx:462-465` — default contexts are REPLACE semantics, not merge.
        Saying so at the foot of the block stops someone expecting the layering lead above it
        to apply to them too.
      */}
      <p
        className="sphere-type-editor__section-note"
        data-testid="perspectives-default-context-note"
        data-datum="persp.which-view-a-perspective-lands-on-isn-t-"
        data-kind="note"
      >
        {t('studio.typeEditor.perspectives.layeredNote')}
      </p>
    </div>
  );
}

/**
 * Which of the arcs block's three views is showing. `following` and `declared`
 * are the two real answers a type can give; `picking` is `declared` with the
 * template picker open. Exposed as an organism prop so a caller (and a story)
 * can open the block on a given view — the block's own affordances move between
 * them from there.
 */
export type ArcsView = 'following' | 'declared' | 'picking';

/**
 * What an arc template's own applicability gate admits, in the one sentence the
 * picker and the "won't deploy here" chip need. Deliberately narrow: it names
 * the sphere types a `type` property filter allows, says "any sphere" when the
 * template declares no gate at all, and otherwise declines to paraphrase rather
 * than inventing a description of a filter it cannot read. A wrong paraphrase
 * here would be worse than none — the author would act on it.
 */
function describeArcApplicability(applicability: Record<string, unknown> | undefined, t: (key: string, params?: Record<string, unknown>) => string): string {
  if (!applicability || Object.keys(applicability).length === 0) {
    return t('studio.typeEditor.arcs.appliesToAnything');
  }
  const propertyFilters = (applicability as { propertyFilters?: unknown }).propertyFilters;
  const typeValues = Array.isArray(propertyFilters)
    ? propertyFilters.flatMap((filter) => {
        const expression = filter as { property?: string; value?: unknown; values?: unknown };
        if (expression?.property !== 'type') return [];
        if (Array.isArray(expression.values)) return expression.values.map(String);
        return expression.value === undefined || expression.value === null ? [] : [String(expression.value)];
      })
    : [];
  return typeValues.length ? t('studio.typeEditor.arcs.appliesToTypes', { types: typeValues.join(', ') }) : t('studio.typeEditor.arcs.appliesToConditions');
}

/**
 * The arcs a sphere of this type starts running (`type-composer.jsx:582-701`).
 *
 * The one list in the composer that REPLACES its ancestors' rather than adding to
 * it, which is why the block's first job is to say which of the two answers is in
 * force: FOLLOWING the chain (whatever an ancestor declares arrives here, now and
 * later), or this type's OWN list (an explicit set, `[]` included — "no arc at
 * all" is a real answer and not the same as following). Declaring an own list and
 * going back to following are both real writes: `defaultArcs: [...]` and
 * `defaultArcs: null` respectively.
 */
function ArcsSection({
  definition,
  lineage,
  t,
  localize,
  locked,
  onSaveField,
  arcTemplates,
  initialView,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
  arcTemplates: ArcTemplateOption[];
  initialView?: ArcsView;
}) {
  const editable = !locked && !!onSaveField;
  const inherited = (definition.defaultArcs ?? []).some((arc) => arc.is_inherited);
  const declaresOwn = Array.isArray(definition.defaultArcs) && !inherited;

  const [rows, setRows] = useState<ArcDefault[]>(() => definition.defaultArcs ?? []);
  const [view, setView] = useState<ArcsView>(() => initialView ?? (declaresOwn ? 'declared' : 'following'));

  useEffect(() => {
    setRows(definition.defaultArcs ?? []);
    setView(initialView ?? (Array.isArray(definition.defaultArcs) && !inherited ? 'declared' : 'following'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.system_name, definition.version_id]);

  const parentName = resolveAncestorName(definition.parent_schema_id, lineage, localize, t) ?? t('studio.typeEditor.arcs.theChain');

  const logAnalyticsEvent = useAnalytics();

  const templateBySystemName = useMemo(() => new Map(arcTemplates.map((template) => [template.systemName, template])), [arcTemplates]);

  /**
   * Whether an arc template's OWN applicability gate admits spheres of this type
   * — the same evaluator the deploy path runs (`validateApplicability`), asked
   * here as a discovery question. A template with no applicability at all is
   * universal; one this client cannot see is assumed to fit, because "unknown"
   * must not read as "won't deploy".
   */
  const fitsThisType = React.useCallback(
    (systemName: string) => {
      const template = templateBySystemName.get(systemName);
      if (!template || template.applicability === undefined) return true;
      return validateApplicability(template.applicability as never, {
        sphere: { properties: { type: definition.system_name } },
      }).valid;
    },
    [templateBySystemName, definition.system_name]
  );

  const describeGate = React.useCallback(
    (systemName: string) => describeArcApplicability(templateBySystemName.get(systemName)?.applicability, t),
    [templateBySystemName, t]
  );

  const displayNameOf = (systemName: string) => templateBySystemName.get(systemName)?.name || systemName;
  const stagesOf = (systemName: string): string[] => {
    const stages = templateBySystemName.get(systemName)?.definition?.stages;
    return Array.isArray(stages) ? stages.map((stage) => t(stage.localizationKey, { defaultValue: stage.fallbackName })) : [];
  };

  function commit(next: ArcDefault[]) {
    setRows(next);
    onSaveField?.({ defaultArcs: next });
  }

  /** Declare this type's own list, seeded from whatever it currently follows. */
  function startOwnArcs() {
    setView('declared');
    commit(
      (definition.defaultArcs ?? []).map((arc) => ({
        templateSystemName: arc.templateSystemName,
        isPrimary: !!arc.isPrimary,
      }))
    );
  }

  /** Stop declaring an own list — `null` removes the key, so the chain applies again. */
  function followChainAgain() {
    setView('following');
    setRows(definition.defaultArcs ?? []);
    onSaveField?.({ defaultArcs: null });
  }

  const legacyArc = definition.arcTemplateSystemName;
  if (!Array.isArray(definition.defaultArcs) && legacyArc) {
    // Legacy single-arc field — not part of the `defaultArcs` REPLACE array (B1–B8
    // never widen `update-schema` to accept `arcTemplateSystemName`), so it never
    // renders editable regardless of the section's overall edit state.
    return (
      <div data-testid="sphere-type-arcs">
        <TypeFieldRowMolecule
          data-testid="type-arc-row-legacy"
          mode={locked ? 'locked' : 'disabled'}
          controls={[
            {
              kind: 'text',
              value: legacyArc,
              ariaLabel: t('studio.typeEditor.arcs.nameAria'),
              grow: true,
            },
          ]}
          badges={[{ label: t('studio.typeEditor.arcs.legacy'), tone: 'neutral' }]}
        />
      </div>
    );
  }

  if (view === 'following') {
    const followed = definition.defaultArcs ?? [];
    return (
      <div data-testid="sphere-type-arcs">
        <div
          className="sphere-type-editor__arcs-summary"
          data-datum="arcs.and-whatever"
          data-kind="text"
          data-on={followed.length ? '1' : undefined}
          data-testid="arcs-following-summary"
        >
          <i className={`pi ${followed.length ? 'pi-sitemap' : 'pi-minus'}`} aria-hidden="true" />
          <span className="sphere-type-editor__arcs-summary-text" data-datum="arcs.and-whatever#2" data-kind="label">
            {followed.length ? (
              <>
                <strong data-datum="arcs.following" data-kind="strong">
                  {t('studio.typeEditor.arcs.followingLead', { parent: parentName })}
                </strong>{' '}
                {t('studio.typeEditor.arcs.followingRest', {
                  arcs: followed.map((arc) => displayNameOf(arc.templateSystemName)).join(', '),
                  parent: parentName,
                })}
              </>
            ) : (
              <>
                <strong data-datum="arcs.no-arcs" data-kind="strong">
                  {t('studio.typeEditor.arcs.emptyTitle')}
                </strong>{' '}
                {t('studio.typeEditor.arcs.emptyExplained')}
              </>
            )}
          </span>
          {editable && (
            <ButtonAtom
              variant="text"
              size="small"
              label={followed.length ? t('studio.typeEditor.arcs.setOwn') : t('studio.typeEditor.arcs.addArc')}
              onClick={startOwnArcs}
              data-testid="arcs-declare-own"
              data-datum="arcs.set-this-type-s-own"
              data-kind="button"
            />
          )}
        </div>
      </div>
    );
  }

  const mode: 'edit' | 'disabled' | 'locked' = editable ? 'edit' : locked ? 'locked' : 'disabled';
  const misfit = rows.find((arc) => !fitsThisType(arc.templateSystemName));
  const unlisted = arcTemplates.filter((template) => !rows.some((arc) => arc.templateSystemName === template.systemName));

  return (
    <div data-testid="sphere-type-arcs">
      <div className="sphere-type-editor__arcs-rows" data-container="arcs > list">
        {rows.map((arc, index) => (
          <ArcRowMolecule
            key={arc.templateSystemName || index}
            data-testid={`type-arc-row-${arc.templateSystemName}`}
            templateSystemName={arc.templateSystemName}
            displayName={displayNameOf(arc.templateSystemName)}
            stages={stagesOf(arc.templateSystemName)}
            isPrimary={!!arc.isPrimary}
            fits={fitsThisType(arc.templateSystemName)}
            appliesTo={describeGate(arc.templateSystemName)}
            mode={mode}
            onMakePrimary={
              arc.isPrimary
                ? undefined
                : // Choosing a primary CLEARS the others in the same payload — the
                  // engine resolves exactly one, so the write has to say which.
                  () => commit(rows.map((row, i) => ({ ...row, isPrimary: i === index })))
            }
            onRemove={() => commit(rows.filter((_, i) => i !== index))}
          />
        ))}
        {/*
          `type-composer.jsx:635-636`. An EMPTY declared list is not the same as
          having none: it overrides the chain, so spheres start with no arc at
          all. Saying so is the difference between "nothing set" and "set to
          nothing", which look identical and mean opposite things.
        */}
        {!rows.length && (
          <p
            className="sphere-type-editor__section-note"
            data-testid="arcs-empty-list-note"
            data-datum="arcs.this-type-declares-an-empty-list-which-i"
            data-kind="note"
          >
            {t('studio.typeEditor.arcs.emptyListNotFollowing', { parent: parentName })}
          </p>
        )}
      </div>

      {/*
        `:643`. Several arcs with no primary is a real ambiguity — at runtime one
        has to answer for the sphere — so it is surfaced where it is created.
      */}
      {rows.length > 1 && !rows.some((arc) => arc.isPrimary) && (
        <MessageAtom severity="warn" data-testid="arcs-no-primary-warning" text={<p>{t('studio.typeEditor.arcs.noPrimaryWarning')}</p>} />
      )}
      {/*
        `:647-655`. An arc whose own gate excludes this type may still be named —
        it simply never deploys, and nothing at runtime says so. That silence is
        the reason this is stated at authoring time.
      */}
      {misfit && (
        <MessageAtom
          severity="warn"
          data-testid="arcs-applicability-warning"
          text={
            <p>
              {t('studio.typeEditor.arcs.gateWarning', {
                says: describeGate(misfit.templateSystemName),
                type: localize(definition.localization_name_key, definition.name),
              })}
            </p>
          }
        />
      )}

      {editable && view === 'picking' && (
        <div className="sphere-type-editor__arcs-picker" data-datum="arcs.picker" data-kind="picker">
          <div className="sphere-type-editor__arcs-picker-head">
            <strong data-datum="arcs.which-arc" data-kind="strong">
              {t('studio.typeEditor.arcs.whichArc')}
            </strong>
            <ButtonAtom
              variant="text"
              size="small"
              label={t('studio.typeEditor.arcs.cancel')}
              onClick={() => setView('declared')}
              data-testid="arcs-picker-cancel"
              data-datum="arcs.cancel"
              data-kind="button"
            />
          </div>
          <PickerShellMolecule<ArcTemplateOption>
            items={unlisted}
            getSearchText={(template) => `${template.name} ${template.systemName} ${template.category ?? ''}`}
            getRowKey={(template) => template.systemName}
            renderRow={(template) => (
              /*
                A template whose own gate excludes this type is LISTED — hiding it
                would make an author hunt for something they can see exists — but
                it is not choosable here (spec § Edge Cases: "listed, not
                selectable, with the reason; deploy remains the authority"). This
                departs from the prototype, which leaves the row live and states
                afterwards that the arc never runs: naming an arc that can never
                deploy is a dead write the author has no way to notice. The reason
                is on the row itself, beside the disabled control — the "outside
                its gate" chip and the "applies to <says>" line — so the row never
                refuses without saying why. The picker's closing note keeps stating
                the model (a template's gate is the authority; a named arc outside
                it never runs), which stays true of arcs that arrive by inheritance
                or from an ancestor's list — those still raise the misfit warning
                above.
              */
              <div
                className="sphere-type-editor__arcs-picker-row"
                data-testid={`arcs-picker-option-${template.systemName}`}
                data-outside-gate={fitsThisType(template.systemName) ? undefined : '1'}
              >
                <span className="sphere-type-editor__arcs-picker-glyph" data-datum="arcs.tile" data-kind="tile">
                  <i className="pi pi-sitemap" aria-hidden="true" />
                </span>
                <span className="sphere-type-editor__arcs-picker-main">
                  <span className="sphere-type-editor__arcs-picker-name" data-datum="arcs.label" data-kind="label">
                    <strong data-datum="arcs.a-name#2" data-kind="strong">
                      {template.name}
                    </strong>
                    {template.category && <TagAtom size="small" tone="outline" label={template.category} data-datum="arcs.a-category" data-kind="pill" />}
                    {!fitsThisType(template.systemName) && (
                      <TagAtom
                        size="small"
                        tone="warn"
                        label={t('studio.typeEditor.arcs.outsideItsGate')}
                        data-datum="arcs.outside-its-gate"
                        data-kind="chip"
                      />
                    )}
                  </span>
                  <span className="sphere-type-editor__arcs-picker-stages" data-datum="arcs.no-stages-defined-yet#2" data-kind="text">
                    {stagesOf(template.systemName).join(' → ') || t('studio.typeEditor.arcs.noStages')}
                  </span>
                  <span className="sphere-type-editor__arcs-picker-gate" data-datum="arcs.applies-to" data-kind="text">
                    {t('studio.typeEditor.arcs.appliesTo', { says: describeGate(template.systemName) })}
                  </span>
                </span>
              </div>
            )}
            // The shell owns the option and its activation: the row above is content, and
            // a template outside its own gate is `aria-disabled` rather than clickable —
            // listed and readable, with the reason inside the row. The shell's own
            // `role="option"` wrapper is therefore the actionable element `arcs.button#2`
            // names, not the content row.
            getOptionDatumId={() => 'arcs.button#2'}
            getOptionKind={() => 'button'}
            getOptionContainer={() => 'each(templates)'}
            isItemDisabled={(template) => !fitsThisType(template.systemName)}
            onPickItem={(template) => {
              setView('declared');
              commit([...rows, { templateSystemName: template.systemName, isPrimary: !rows.length }]);
              // Spec § Analytics: `sphere_type_arc_picked`. `applicable` is read from
              // the same gate that disables the row, so it is true at this call site by
              // construction; it is emitted rather than assumed so the event stays
              // honest if the picker ever admits an out-of-gate arc (the prototype's
              // own behaviour, which this implementation deliberately tightened).
              logAnalyticsEvent('sphere_type_arc_picked', undefined, {
                schemaId: definition.schema_id,
                templateSystemName: template.systemName,
                applicable: fitsThisType(template.systemName),
              });
            }}
            scopeLabel={t('studio.typeEditor.arcs.pickerScope', { count: unlisted.length })}
            noMatchesMessage={t('studio.typeEditor.arcs.pickerNoMatches')}
            searchPlaceholder={t('studio.typeEditor.arcs.pickerSearchPlaceholder')}
            cancelLabel={t('studio.typeEditor.arcs.cancel')}
          />
          <p className="sphere-type-editor__section-note" data-datum="arcs.an-arc-template-decides-for-itself-which" data-kind="note">
            {t('studio.typeEditor.arcs.pickerNote')}
          </p>
        </div>
      )}

      {editable && view !== 'picking' && (
        <div className="sphere-type-editor__arcs-addrow" data-datum="arcs.add-row" data-kind="add-row">
          <ButtonAtom
            variant="secondary"
            size="small"
            icon="pi pi-plus"
            label={t('studio.typeEditor.arcs.addArc')}
            onClick={() => setView('picking')}
            data-testid="arcs-add"
            data-datum="arcs.add-an-arc"
            data-kind="button"
          />
          <ButtonAtom
            variant="text"
            size="small"
            icon="pi pi-replay"
            label={t('studio.typeEditor.arcs.followAgain', { parent: parentName })}
            onClick={followChainAgain}
            data-testid="arcs-follow-again"
            data-datum="arcs.follow"
            data-kind="button"
          />
        </div>
      )}
    </div>
  );
}

/**
 * `trail-composer.jsx:727-768` (`health` container). Health is derived, not authored — the
 * block only ever explains what drives it and points back at what's composed. The
 * prototype's driver list reads from `t.healthDrivers`, computed from the type's attached
 * aspects' status-factor config; this type has no status-factor read wired yet (same gap
 * Languages' per-locale table has — Phase 3 content), so the skeleton renders the
 * `drivers=[]` branch verbatim rather than fabricate a driver row with no data behind it.
 */
function HealthSection({
  t,
  onOpenSection,
}: {
  t: (key: string) => string;
  /** `Change what's composed` walks to Aspects, the block whose contents actually drive health. */
  onOpenSection?: (sectionId: string) => void;
}) {
  return (
    <div className="sphere-type-editor__health" data-testid="sphere-type-health">
      <p className="editor-block__body-text" data-datum="health.nothing-composed-here-drives-health" data-kind="note">
        {t('studio.typeEditor.composer.health.nothingDrivesStatus')}
      </p>
      <p className="editor-block__body-text" data-datum="health.nothing-composed-yet" data-kind="note">
        {t('studio.typeEditor.composer.health.nothingComposedYet')}
      </p>
      <p className="editor-block__body-text" data-datum="health.nothing-here-is-switched-on-an-aspect-dr" data-kind="note">
        {t('studio.typeEditor.composer.health.explainer')}
      </p>
      {onOpenSection && (
        <ButtonAtom
          variant="text"
          size="small"
          label={t('studio.typeEditor.composer.health.changeWhatsComposed')}
          onClick={() => onOpenSection('aspects')}
          data-testid="sphere-type-health-change-composed"
          data-datum="health.change-what-s-composed"
          data-kind="button"
        />
      )}
    </div>
  );
}

/**
 * `trail-composer.jsx:772-815` (`present` container). Presentation names which context the
 * type's aspects render into: the type's own default panel (inherited from `parent` and
 * replaced, never merged, by an override), then the per-perspective panels that take priority
 * over the default when a perspective matches — the first one in the list wins.
 */
function PresentationSection({
  definition,
  lineage,
  t,
  localize,
  locked,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
}) {
  const readOnly = locked;
  const parentName = definition.parent_schema_id ? resolveAncestorName(definition.parent_schema_id, lineage, localize, t) : null;
  const perspectives = (definition.perspectives ?? []).slice(0, 3);

  return (
    <div className="sphere-type-editor__presentation" data-testid="sphere-type-presentation">
      {parentName ? (
        <div className="sphere-type-editor__presentation-row t-row">
          <i className="pi pi-image" aria-hidden="true" data-datum="present.default-panel" data-kind="icon" />
          <span className="t-row-name" data-datum="present.default-panel#2" data-kind="label">
            {t('studio.typeEditor.composer.presentation.defaultPanel')}
          </span>
          <span className="t-inherit" data-datum="present.panel" data-kind="note">
            <i className="pi pi-lock" aria-hidden="true" />
            {t('studio.typeEditor.composer.presentation.panelFrom', { parent: parentName })}
          </span>
          <span className="t-replaces" data-datum="present.replaces-doesn-t-merge" data-kind="note">
            {t('studio.typeEditor.composer.presentation.replacesNotMerges')}
          </span>
        </div>
      ) : (
        <p className="editor-block__body-text" data-datum="present.no-parent-so-nothing-is-inherited" data-kind="note">
          {readOnly ? t('studio.typeEditor.composer.presentation.noParentReadOnly') : t('studio.typeEditor.composer.presentation.noParentEditable')}
        </p>
      )}
      {!readOnly && (
        <ButtonAtom
          variant="secondary"
          size="small"
          label={parentName ? t('studio.typeEditor.composer.presentation.overrideForThisType') : t('studio.typeEditor.composer.presentation.setDefaultPanel')}
          data-testid="sphere-type-presentation-override"
          data-datum="present.override-for-this-type"
          data-kind="button"
        />
      )}

      <div className="sphere-type-editor__presentation-perspectives" data-datum="present.text" data-kind="text">
        <div className="sphere-type-editor__presentation-persp-head" data-datum="present.per-perspective-panels" data-kind="heading">
          <i className="pi pi-user-edit" aria-hidden="true" />
          <strong data-datum="present.per-perspective-panels#2" data-kind="strong">
            {t('studio.typeEditor.composer.presentation.perPerspectivePanels')}
          </strong>
          <span className="a-hint" data-datum="present.the-first-matching-perspective-wins-and-" data-kind="hint">
            {t('studio.typeEditor.composer.presentation.firstMatchWins')}
          </span>
        </div>
        <div className="t-rows">
          {perspectives.map((p, i) => (
            <div key={p.system_name} className="t-row">
              <span className="t-row-name" data-datum="present.p-name" data-kind="label">
                {localize(p.label_key, p.label)}
              </span>
              <span className="a-hint" data-datum="present.no-panel-of-its-own-falls-back-to-the-de" data-kind="hint">
                {i === 0 ? t('studio.typeEditor.composer.presentation.noPanelFallsBack') : t('studio.typeEditor.composer.presentation.noPanel')}
              </span>
              {!readOnly && (
                <ButtonAtom
                  variant="text"
                  size="small"
                  icon="pi pi-plus"
                  label={t('studio.typeEditor.composer.presentation.givePanel')}
                  data-datum="present.button"
                  data-kind="button"
                />
              )}
            </div>
          ))}
          {!perspectives.length && (
            <p className="editor-block__body-text" data-datum="present.no-perspectives-yet-so-there-s-nothing-t" data-kind="note">
              {t('studio.typeEditor.composer.presentation.noPerspectivesYet')}
            </p>
          )}
        </div>
        <p className="editor-block__body-text" data-datum="present.order-matters-reorder-the-perspectives-a" data-kind="note">
          {t('studio.typeEditor.composer.presentation.orderMatters')}
        </p>
      </div>

      <SectionsOnSpherePage definition={definition} lineage={lineage} t={t} localize={localize} />
    </div>
  );
}

/**
 * Read-only list of the merged `definition.sections` (EAINT-1011 SC7): which groups the sphere
 * page shows and in what order, where each came from, and how many visible fields each holds.
 * Authoring lives in the schema definition, not here, so the block has no edit affordance.
 */
function SectionsOnSpherePage({
  definition,
  lineage,
  t,
  localize,
}: Readonly<{
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
}>) {
  const sections = definition.sections ?? [];
  const sectionNames = new Set(sections.map((section) => section.system_name));
  // The same rows and order the Sphere Editor lays out: the header renders its own fields, and the
  // implicit Details group sits at DEFAULT_SECTION_ORDER among the sections.
  const sectionedProperties = filterHiddenPropertiesExcluding(definition.properties ?? [], HEADER_RENDERED_PROPERTY_NAMES);
  const groups = [
    ...sections.map((section) => ({ key: section.system_name, section })),
    ...(sections.length > 0 ? [{ key: DEFAULT_SECTION_KEY, section: null }] : []),
  ].sort(compareSectionGroups);
  const countFor = (sectionName: string) =>
    sectionedProperties.filter((property) =>
      sectionName === DEFAULT_SECTION_KEY ? !property.section || !sectionNames.has(property.section) : property.section === sectionName
    ).length;

  return (
    <div className="sphere-type-editor__presentation-sections" data-testid="sphere-type-presentation-sections">
      <div className="sphere-type-editor__presentation-persp-head" data-datum="present.sections-title" data-kind="heading">
        <i className="pi pi-objects-column" aria-hidden="true" />
        <strong data-datum="present.sections-title#2" data-kind="strong">
          {t('studio.typeEditor.composer.presentation.sections.title')}
        </strong>
      </div>
      <div className="t-rows">
        {groups.map(({ key, section }) => {
          if (!section) {
            return (
              <div key={key} className="t-row" data-testid="sphere-type-presentation-section-details">
                <span className="t-row-name" data-datum="present.sections-details" data-kind="label">
                  {t('studio.typeEditor.composer.presentation.sections.details')}
                </span>
                <span className="a-hint" data-datum="present.sections-count" data-kind="hint">
                  {t('studio.typeEditor.composer.presentation.sections.fieldCount', { count: countFor(key) })}
                </span>
              </div>
            );
          }
          const adjustedHere = !!definition.schema_id && section.adjusted_by_schema === definition.schema_id;
          const fromName = !adjustedHere && section.is_inherited ? resolveAncestorName(section.inherited_from_schema, lineage, localize, t) : undefined;
          return (
            <div key={section.system_name} className="t-row" data-testid={`sphere-type-presentation-section-${section.system_name}`}>
              <span className="t-row-name" data-datum="present.sections-name" data-kind="label">
                {localize(section.label_key, section.label ?? section.system_name)}
              </span>
              {section.dock && (
                <span className="a-hint" data-datum="present.sections-own-tab" data-kind="tag">
                  {t('studio.typeEditor.composer.presentation.sections.ownTab')}
                </span>
              )}
              {adjustedHere && (
                <span className="t-inherit" data-datum="present.sections-provenance" data-kind="note">
                  {t('studio.typeEditor.composer.presentation.sections.adjustedHere')}
                </span>
              )}
              {fromName && (
                <span className="t-inherit" data-datum="present.sections-provenance" data-kind="note">
                  <i className="pi pi-lock" aria-hidden="true" />
                  {t('studio.typeEditor.composer.presentation.sections.from', { parent: fromName })}
                </span>
              )}
              <span className="a-hint" data-datum="present.sections-count" data-kind="hint">
                {t('studio.typeEditor.composer.presentation.sections.fieldCount', { count: countFor(section.system_name) })}
              </span>
            </div>
          );
        })}
        {sections.length === 0 && (
          <p className="editor-block__body-text" data-datum="present.sections-empty" data-kind="note">
            {t('studio.typeEditor.composer.presentation.sections.empty')}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * `type-composer.jsx:1676-1682` (`languages` container, rows 0-3) — the summary that replaces
 * the per-locale-string list a subtype gets: what it covers (own strings plus everything
 * inherited) and where to go add more of it. The per-locale table itself (`s-label` / `text` /
 * `button`, `each(locStrings)`) needs a real localizable-strings read this type doesn't have
 * yet — Phase 3 content, not this phase's skeleton.
 */
function LanguagesSection({
  t,
  onOpenSection,
}: {
  t: (key: string) => string;
  /** `See what's composed` walks to Aspects, where the strings this type inherits are attached. */
  onOpenSection?: (sectionId: string) => void;
}) {
  return (
    <div className="sphere-type-editor__languages" data-testid="sphere-type-languages">
      <p className="editor-block__body-text" data-datum="languages.everything-the" data-kind="note">
        <Trans
          i18nKey="studio.typeEditor.composer.languages.body"
          components={{
            type: <strong data-datum="languages.type" data-kind="strong" />,
            inherited: <strong data-datum="languages.inherited" data-kind="strong" />,
          }}
        />
      </p>
      {onOpenSection && (
        <ButtonAtom
          variant="ghost"
          size="small"
          className="sphere-type-editor__languages-composed-toggle"
          label={t('studio.typeEditor.composer.languages.seeWhatsComposed')}
          onClick={() => onOpenSection('aspects')}
          data-testid="sphere-type-languages-see-composed"
          data-datum="languages.see-what-s-composed"
          data-kind="button"
          data-variant="quiet"
        />
      )}
    </div>
  );
}

/**
 * `type-composer.jsx:1686-1712` (`guide` — Agent guidance, 8 datums). Own-type-only, freeform
 * text an agent reads before it touches a sphere of this type — never inherited, because a
 * subtype means something its parent doesn't (`guide.own-type-only-guidance-is-never-inherite`,
 * always rendered regardless of content). Read from `agent_guidance.notes`: the real
 * `SchemaAgentGuidance` shape is richer (structured `what`/`intendedUse`/`distinguishFrom`/
 * `notFor`/`examples` alongside the freeform `notes`), but the contract's `t.guidance` is a
 * single textarea, and `notes` is the field that shape maps onto without inventing a UI the
 * design doesn't show yet — the structured fields are Phase 3 content, not this skeleton's job.
 * The ghost state (`ghost=ghost` — an agent-drafted guidance proposal, `guide.card`/
 * `guide.textarea`/`guide.proposed`/`guide.edit`/`guide.keep`) is the prototype's own inline
 * `<Ghost>` wrapper (`trail-composer.jsx:51-62,1688-1691`) around a single editable textarea —
 * a different, simpler shape than the generic per-field `ghosts`/`GhostBarMolecule` mechanism
 * every other section uses (that mechanism renders N field rows plus a bottom summary bar; guide
 * only ever proposes one field, inline, with its own Edit/Keep pair). So this section renders it
 * itself, and `renderSectionCard` skips `guide` when composing the generic rows/bar so a pending
 * guide proposal doesn't show two Keep actions.
 * `writing` is local, ephemeral UI state — no field commits until `onSaveField` grows a
 * `guidance` write path (Phase 3), matching how `AdvancedSection`'s toggles hold local state
 * before their own commit lands.
 */
function GuideSection({
  definition,
  locked,
  t,
  ghost,
  onGhostFieldEdit,
  onKeepGhost,
}: {
  definition: Schema;
  locked: boolean;
  t: (key: string, params?: Record<string, unknown>) => string;
  ghost?: PendingGhost;
  onGhostFieldEdit?: (ghostId: string, key: string, value: unknown) => void;
  onKeepGhost?: (ghostId: string) => void;
}) {
  const [writing, setWriting] = useState(false);
  const guidanceText = definition.agent_guidance?.notes;
  const ghostTextareaRef = useRef<HTMLTextAreaElement>(null);

  if (ghost) {
    const draftValue = (ghostEffectiveValue(ghost, 'notes') as string) ?? '';
    return (
      <div className="sphere-type-editor__guide" data-testid="sphere-type-guide">
        <div className="sphere-type-editor__guide-ghost" data-testid="sphere-type-guide-ghost" data-datum="guide.card" data-kind="card">
          <TextareaAtom
            ref={ghostTextareaRef}
            className="sphere-type-editor__guide-textarea"
            data-testid="sphere-type-guide-ghost-textarea"
            data-datum="guide.textarea"
            data-kind="textarea"
            rows={3}
            value={draftValue}
            onChange={(e) => onGhostFieldEdit?.(ghost.id, 'notes', e.target.value)}
            aria-label={t('studio.typeEditor.section.agentGuidance')}
          />
          <div className="sphere-type-editor__guide-ghost-actions">
            <TagAtom
              tone="accent"
              size="small"
              icon="pi-sparkles"
              label={t('studio.typeEditor.composer.guide.ghostDrafted')}
              data-testid="sphere-type-guide-ghost-mark"
              data-datum="guide.proposed"
              data-kind="chip"
            />
            <ButtonAtom
              variant="text"
              size="small"
              label={t('studio.typeEditor.composer.ghost.edit')}
              onClick={() => ghostTextareaRef.current?.focus()}
              data-testid="sphere-type-guide-ghost-edit"
              data-datum="guide.edit"
              data-kind="button"
            />
            <ButtonAtom
              variant="primary"
              size="small"
              label={t('studio.typeEditor.composer.ghost.keep')}
              onClick={() => onKeepGhost?.(ghost.id)}
              data-testid="sphere-type-guide-ghost-keep"
              data-datum="guide.keep"
              data-kind="button"
            />
          </div>
        </div>
        <p className="editor-block__body-text" data-datum="guide.own-type-only-guidance-is-never-inherite" data-kind="note">
          {t('studio.typeEditor.composer.guide.ownTypeOnly')}
        </p>
      </div>
    );
  }

  return (
    <div className="sphere-type-editor__guide" data-testid="sphere-type-guide">
      {guidanceText ? (
        <TextareaAtom
          className="sphere-type-editor__guide-textarea"
          data-testid="sphere-type-guide-textarea"
          data-datum="guide.textarea#2"
          data-kind="textarea"
          rows={3}
          defaultValue={guidanceText}
          readOnly={locked}
          aria-label={t('studio.typeEditor.section.agentGuidance')}
        />
      ) : locked ? (
        <p className="editor-block__body-text" data-datum="guide.no-guidance-authored-platform-types-desc" data-kind="note">
          {t('studio.typeEditor.composer.guide.noGuidanceReadOnly')}
        </p>
      ) : (
        <div className="sphere-type-editor__guide-empty" data-testid="sphere-type-guide-empty">
          <p className="editor-block__body-text" data-datum="guide.no-guidance-authored-yet-without-it-othe" data-kind="note">
            {t('studio.typeEditor.composer.guide.noGuidanceEdit')}
          </p>
          <ButtonAtom
            variant="primary"
            size="small"
            icon="pi pi-pencil"
            label={t('studio.typeEditor.composer.guide.writeGuidance')}
            onClick={() => setWriting(true)}
            data-testid="sphere-type-guide-write"
            data-datum="guide.write-guidance"
            data-kind="button"
          />
        </div>
      )}
      {writing && !guidanceText && !locked && (
        <TextareaAtom
          className="sphere-type-editor__guide-textarea"
          data-testid="sphere-type-guide-writing"
          data-datum="guide.what-another-agent-should-know-before-it"
          data-kind="textarea"
          rows={3}
          autoFocus
          placeholder={t('studio.typeEditor.composer.guide.writingPlaceholder')}
          aria-label={t('studio.typeEditor.composer.guide.writingPlaceholder')}
        />
      )}
      <p className="editor-block__body-text" data-datum="guide.own-type-only-guidance-is-never-inherite" data-kind="note">
        {t('studio.typeEditor.composer.guide.ownTypeOnly')}
      </p>
    </div>
  );
}

/**
 * `type-composer.jsx:1729-1786` (`adv` — 26 datums, entirely inline). The three policy
 * toggles are live (Phase 4): `update-schema` accepts `attachmentsEnabled`,
 * `defaultPrivate` and `conversationsEnabled` as scalar, child-wins writes since C6/C7,
 * so each row is a real `mode="edit"` control whose value is held locally and committed
 * on change — the composer's one write seam, `onSaveField`. Each hint states what the
 * CURRENT setting means, both ways, as the contract's own ternaries do: a hint that only
 * described the On case would read as false the moment the toggle was switched off.
 * Membership config's row is the contract's own conditional (`t.membership=yes`): it does
 * not render at all when unset, never a fallback "not configured" line — that fallback
 * text does not exist in the design.
 */
function AdvancedSection({
  definition,
  lineage,
  t,
  localize,
  locked,
  onSaveField,
}: {
  definition: Schema;
  lineage: LineageResult | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  localize: Localize;
  locked: boolean;
  onSaveField?: (fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>;
}) {
  const editable = !locked && !!onSaveField;
  const fieldMode = editable ? 'edit' : 'disabled';
  // `type-composer.jsx:1778` — "inherited from {t.parent || 'the platform'}": the pill names
  // where the config comes from, and a root type's comes from the platform.
  const parent = lineage?.ancestors[0] ?? null;
  const inheritedFromName = parent ? localize(parent.nameKey, parent.name) : t('studio.typeEditor.advanced.thePlatform');

  // `attachments` is a config OBJECT server-side; the composer writes only its
  // `enabled` scalar (`allowedTypes` is untouched by this route), so the row holds
  // the scalar. Absent means the chain never set it — the platform default is on.
  const [attachments, setAttachments] = useState(() => definition.attachments?.enabled !== false);
  const [defaultPrivate, setDefaultPrivate] = useState(() => definition.default_private === true);
  const [conversations, setConversations] = useState(() => definition.conversations_enabled !== false);

  return (
    <div data-testid="sphere-type-advanced">
      {/* `adv > list` (`type-composer.jsx:1733`): the three mutable settings, as one list of rows. */}
      <div className="sphere-type-editor__adv-list" data-container="advanced > list" data-testid="sphere-type-advanced-list">
        <TypeFieldRowMolecule
          data-testid="sphere-type-advanced-attachments"
          datum="adv.attachments"
          labelDatum="adv.attachments#2"
          hintPlacement="inline"
          label={t('studio.typeEditor.advanced.attachments')}
          hint={t(attachments ? 'studio.typeEditor.advanced.attachmentsHint' : 'studio.typeEditor.advanced.attachmentsOffHint')}
          hintDatum="adv.files-can-be-attached-to-a-sphere-they-i"
          mode={fieldMode}
          controls={[
            {
              kind: 'switch',
              checked: attachments,
              ariaLabel: t('studio.typeEditor.advanced.attachments'),
              onChange: setAttachments,
              onCommit: () => onSaveField?.({ attachmentsEnabled: !attachments }),
            },
          ]}
        />
        <TypeFieldRowMolecule
          data-testid="sphere-type-advanced-new-spheres-private"
          datum="adv.new-spheres-start-private"
          labelDatum="adv.new-spheres-start-private#2"
          hintPlacement="inline"
          label={t('studio.typeEditor.advanced.newSpheresStartPrivate')}
          hint={t(defaultPrivate ? 'studio.typeEditor.advanced.newSpheresStartPrivateHint' : 'studio.typeEditor.advanced.newSpheresStartPrivateOffHint')}
          hintDatum="adv.only-people-holding-a-perspective-on-the"
          mode={fieldMode}
          controls={[
            {
              kind: 'switch',
              checked: defaultPrivate,
              ariaLabel: t('studio.typeEditor.advanced.newSpheresStartPrivate'),
              onChange: setDefaultPrivate,
              onCommit: () => onSaveField?.({ defaultPrivate: !defaultPrivate }),
            },
          ]}
        />
        <TypeFieldRowMolecule
          data-testid="sphere-type-advanced-conversations"
          datum="adv.conversations"
          labelDatum="adv.conversations#2"
          hintPlacement="inline"
          label={t('studio.typeEditor.advanced.conversations')}
          hint={t('studio.typeEditor.advanced.conversationsExplained')}
          hintDatum="adv.whether-spheres-of-this-type-carry-a-com"
          mode={fieldMode}
          controls={[
            {
              kind: 'switch',
              checked: conversations,
              ariaLabel: t('studio.typeEditor.advanced.conversations'),
              onChange: setConversations,
              onCommit: () => onSaveField?.({ conversationsEnabled: !conversations }),
            },
          ]}
        />
      </div>

      {/*
        `type-composer.jsx:1760-1786`. Refusing without saying why is how a
        reader concludes the app is broken rather than that the platform owns
        this. These are grouped and labelled with the reason, not scattered
        among the editable rows.
      */}
      <div className="sphere-type-editor__shown-not-editable" data-testid="sphere-type-advanced-locked-group" data-datum="adv.callout" data-kind="callout">
        <p className="sphere-type-editor__shown-not-editable-head" data-datum="adv.shown-not-editable" data-kind="heading">
          <strong data-datum="adv.shown-not-editable#2" data-kind="strong">
            {t('studio.typeEditor.advanced.shownNotEditable')}
          </strong>{' '}
          <span data-datum="adv.these-change-inheritance-or-permission-t" data-kind="hint">
            {t('studio.typeEditor.advanced.shownNotEditableWhy')}
          </span>
        </p>
        <TypeFieldRowMolecule
          data-testid="sphere-type-advanced-subject-relation"
          datum="adv.subject-relation"
          kind="field"
          labelDatum="adv.subject-relation#2"
          label={t('studio.typeEditor.advanced.subjectRelation')}
          hintPlacement="inline"
          hint={t('studio.typeEditor.advanced.subjectRelationExplained')}
          hintDatum="adv.the-relation-that-makes-someone-the-subj"
          // The reading is a pill at the row's far edge (`type-composer.jsx:1772`, `.t-inherit`),
          // after the hint — `trailing` renders after the main line, which keeps the contract's
          // order (label #1, hint #2, note #3) in the DOM.
          trailing={
            <TagAtom
              tone="outline"
              size="small"
              label={
                definition.subject_relation ? t('studio.typeEditor.advanced.subjectRelationMember') : t('studio.typeEditor.advanced.subjectRelationNotSet')
              }
              data-datum="adv.member"
              data-kind="note"
            />
          }
        />
        {!!definition.membershipConfig && (
          <TypeFieldRowMolecule
            data-testid="sphere-type-advanced-membership-config"
            datum="adv.membership-config"
            kind="field"
            labelDatum="adv.membership-config#2"
            label={t('studio.typeEditor.advanced.membershipConfig')}
            hintPlacement="inline"
            hint={t('studio.typeEditor.advanced.membershipConfigHint')}
            hintDatum="adv.how-assignment-to-this-role-like-type-is"
            trailing={
              <TagAtom
                tone="outline"
                size="small"
                label={t('studio.typeEditor.advanced.membershipConfigInheritedFrom', { name: inheritedFromName })}
                data-datum="adv.inherited-from"
                data-kind="note"
              />
            }
          />
        )}
        <TypeFieldRowMolecule
          data-testid="sphere-type-advanced-sealing"
          datum="adv.sealing"
          kind="icon"
          leading={<i className="pi pi-lock" aria-hidden="true" />}
          labelDatum="adv.sealing#2"
          label={t('studio.typeEditor.advanced.sealing')}
          hintPlacement="inline"
          hint={t('studio.typeEditor.advanced.sealingExplained')}
          hintDatum="adv.set-by-migration-only-deliberately-not-c"
          trailing={
            <TagAtom
              tone="outline"
              size="small"
              icon="pi-lock"
              label={definition.sealed ? t('studio.typeEditor.advanced.sealedNote') : t('studio.typeEditor.advanced.notSealedNote')}
              data-datum="adv.sealed"
              data-kind="note"
            />
          }
        />
      </div>
    </div>
  );
}

function sectionBody(
  sectionId: string,
  definition: Schema,
  lineage: LineageResult | null,
  t: (key: string, params?: Record<string, unknown>) => string,
  localize: Localize,
  locked: boolean,
  onSaveField: ((fields: UpdateSphereTypeFields) => Promise<UpdateSphereTypeOutcome | 'locked' | 'error'>) | undefined,
  conflict: boolean,
  needsByAspect: Record<string, SchemaAspectNeeds['needs']>,
  onOpenSection: (sectionId: string) => void,
  arcTemplates: ArcTemplateOption[],
  arcsInitialView: ArcsView | undefined,
  aspectsInitialConfirmDrop: string | undefined,
  aspectsInitialDropped: AspectRef[] | undefined,
  aspectsInitialConfiguring: string | undefined,
  aspectCatalog: AspectRow[] | null | undefined,
  onDraftAspect: ((draft: NewAspectDraft) => Promise<string | undefined>) | undefined,
  pickerOpen: boolean,
  onPickerOpenChange: (open: boolean) => void,
  onPickerStateChange: (state: AspectPickerRenderState | null) => void,
  configuringAspect: string | null,
  onConfiguringAspectChange: (systemName: string | null) => void,
  configuringPortalNode: HTMLDivElement | null,
  draftingAspect: boolean,
  onDraftingAspectChange: (open: boolean) => void,
  onDraftAspectStateChange: (state: DraftAspectRenderState | null) => void,
  ruleEditTarget: BusinessRule | 'new' | null,
  onRuleEditTargetChange: (target: BusinessRule | 'new' | null) => void,
  ruleEditPortalNode: HTMLDivElement | null,
  relationshipEditTarget: SchemaRelationship | 'new' | null,
  onRelationshipEditTargetChange: (target: SchemaRelationship | 'new' | null) => void,
  relationshipEditPortalNode: HTMLDivElement | null,
  onRequestCloseRelationshipEditing: () => void,
  onRelationshipEditingDirtyChange: (dirty: boolean) => void,
  overrideEditTarget: SchemaRelationship | null,
  onOverrideEditTargetChange: (target: SchemaRelationship | null) => void,
  overrideEditPortalNode: HTMLDivElement | null,
  onRequestCloseOverrideEditing: () => void,
  onOverrideEditingDirtyChange: (dirty: boolean) => void,
  perspectiveEditTarget: SchemaPerspective | null,
  onPerspectiveEditTargetChange: (target: SchemaPerspective | null) => void,
  perspectiveEditPortalNode: HTMLDivElement | null,
  perspInitialDrafting: boolean | undefined,
  perspInitialAskSuppress: string | undefined,
  perspInitialConfirmDelete: string | undefined,
  perspInitialDeleted: string[] | undefined,
  perspInitialEdits: Record<string, Partial<SchemaPerspective>> | undefined,
  perspInitialDraft: Partial<SchemaPerspective> | undefined,
  parentOptions: ParentOption[] | undefined,
  onMoveParent: ((option: ParentOption) => void) | undefined,
  critique: ComposerTypeCritique[] | undefined,
  dismissedCritiqueIds: string[],
  onDismissCritique: (id: string) => void,
  guideGhost: PendingGhost | undefined,
  onGuideGhostFieldEdit: (ghostId: string, key: string, value: unknown) => void,
  onKeepGuideGhost: (ghostId: string) => void
): React.ReactNode {
  switch (sectionId) {
    case 'identity':
      return (
        <IdentitySection
          definition={definition}
          t={t}
          localize={localize}
          locked={locked}
          onSaveField={onSaveField}
          conflict={conflict}
          onOpenSection={onOpenSection}
        />
      );
    case 'placement':
      return (
        <PlacementSection
          definition={definition}
          lineage={lineage}
          t={t}
          localize={localize}
          locked={locked}
          parentOptions={parentOptions}
          onMoveParent={onMoveParent}
        />
      );
    case 'aspects':
      return (
        <AspectsSection
          definition={definition}
          lineage={lineage}
          t={t}
          localize={localize}
          locked={locked}
          onSaveField={onSaveField}
          needsByAspect={needsByAspect}
          aspectCatalog={aspectCatalog}
          onDraftAspect={onDraftAspect}
          initialConfirmDrop={aspectsInitialConfirmDrop}
          initialDropped={aspectsInitialDropped}
          pickerOpen={pickerOpen}
          onPickerOpenChange={onPickerOpenChange}
          onPickerStateChange={onPickerStateChange}
          configuringAspect={configuringAspect}
          onConfiguringAspectChange={onConfiguringAspectChange}
          configuringPortalNode={configuringPortalNode}
          draftingAspect={draftingAspect}
          onDraftingAspectChange={onDraftingAspectChange}
          onDraftAspectStateChange={onDraftAspectStateChange}
          critique={critique}
          dismissedCritiqueIds={dismissedCritiqueIds}
          onDismissCritique={onDismissCritique}
        />
      );
    case 'business-rules':
      return (
        <BusinessRulesSection
          definition={definition}
          lineage={lineage}
          t={t}
          localize={localize}
          locked={locked}
          onSaveField={onSaveField}
          onOpenSection={onOpenSection}
          ruleEditTarget={ruleEditTarget}
          onRuleEditTargetChange={onRuleEditTargetChange}
          ruleEditPortalNode={ruleEditPortalNode}
        />
      );
    case 'relationships':
      return (
        <RelationshipsSection
          definition={definition}
          lineage={lineage}
          t={t}
          localize={localize}
          locked={locked}
          onSaveField={onSaveField}
          onOpenSection={onOpenSection}
          editingTarget={relationshipEditTarget}
          onEditingTargetChange={onRelationshipEditTargetChange}
          editingPortalNode={relationshipEditPortalNode}
          onRequestCloseEditing={onRequestCloseRelationshipEditing}
          onEditingDirtyChange={onRelationshipEditingDirtyChange}
          overrideTarget={overrideEditTarget}
          onOverrideTargetChange={onOverrideEditTargetChange}
          overridePortalNode={overrideEditPortalNode}
          onRequestCloseOverrideEditing={onRequestCloseOverrideEditing}
          onOverrideEditingDirtyChange={onOverrideEditingDirtyChange}
        />
      );
    case 'perspectives':
      return (
        <PerspectivesSection
          definition={definition}
          lineage={lineage}
          t={t}
          localize={localize}
          locked={locked}
          onSaveField={onSaveField}
          openTarget={perspectiveEditTarget}
          onOpenTargetChange={onPerspectiveEditTargetChange}
          editingPortalNode={perspectiveEditPortalNode}
          initialDraftingPerspective={perspInitialDrafting}
          initialAskSuppressPerspective={perspInitialAskSuppress}
          initialDeletedPerspectives={perspInitialDeleted}
          initialConfirmDeletePerspective={perspInitialConfirmDelete}
          initialEdits={perspInitialEdits}
          initialDraft={perspInitialDraft}
        />
      );
    case 'arcs':
      return (
        <ArcsSection
          definition={definition}
          lineage={lineage}
          t={t}
          localize={localize}
          locked={locked}
          onSaveField={onSaveField}
          arcTemplates={arcTemplates}
          initialView={arcsInitialView}
        />
      );
    case 'health':
      return <HealthSection t={t} onOpenSection={onOpenSection} />;
    case 'presentation':
      return <PresentationSection definition={definition} lineage={lineage} t={t} localize={localize} locked={locked} />;
    case 'languages':
      return <LanguagesSection t={t} onOpenSection={onOpenSection} />;
    case 'guide':
      return (
        <GuideSection
          definition={definition}
          locked={locked}
          t={t}
          ghost={guideGhost}
          onGhostFieldEdit={onGuideGhostFieldEdit}
          onKeepGhost={onKeepGuideGhost}
        />
      );
    case 'advanced':
      return <AdvancedSection definition={definition} lineage={lineage} t={t} localize={localize} locked={locked} onSaveField={onSaveField} />;
    default:
      return <p className="sphere-type-editor__placeholder">{t('studio.typeEditor.placeholder.notBuilt')}</p>;
  }
}

const SphereTypeComposerOrganism: React.FC<SphereTypeComposerOrganismProps> = ({
  status,
  definition,
  lineage,
  onSectionView,
  onBack,
  onExtendInstead,
  onInheritedClick,
  onSaveField,
  saving = false,
  conflict = false,
  saveError = false,
  gate = null,
  gateStatus = 'idle',
  needsByAspect = {},
  aspectCatalog = null,
  onDraftAspect,
  publishing = false,
  onPublish,
  onSaveDraft,
  onOpenSphere,
  initialSideTab,
  initialAgentItems,
  savingDraft = false,
  agentEndpoint,
  agentGetToken,
  agentTarget,
  arcTemplates = EMPTY_ARC_TEMPLATES,
  arcsInitialView,
  initialGhosts,
  aspectsInitialConfirmDrop,
  aspectsInitialDropped,
  aspectsInitialConfiguring,
  rulesInitialOpen,
  relsInitialAdjust,
  perspInitialOpen,
  perspInitialDrafting,
  perspInitialAskSuppress,
  perspInitialConfirmDelete,
  perspInitialDeleted,
  perspInitialEdits,
  perspInitialDraft,
  parentOptions,
  onMoveParent,
  critique,
}) => {
  const { t, i18n } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const [ghosts, setGhosts] = useState<PendingGhost[]>(() =>
    (initialGhosts ?? [])
      .filter((proposal) => {
        const reason = rejectedProposalReason(proposal);
        if (reason) console.error('[SphereTypeComposer] dropped an initial ghost:', reason, proposal);
        return !reason;
      })
      .map((proposal) => ({
        id: nextGhostId(),
        sectionId: proposal.sectionId,
        rationale: proposal.rationale,
        fields: proposal.fields,
        edited: {},
        provenance: proposal.provenance,
        status: 'pending' as const,
      }))
  );
  // How many of this type's proposals have been kept in this session — the numerator of the index
  // rail's "N of M proposed blocks kept." A kept ghost leaves `ghosts`, so the count it was part
  // of has to be remembered separately or the note would report the remaining ones as the total.
  const [keptGhostCount, setKeptGhostCount] = useState(0);
  // Toggle state for the header's Agent button `aria-pressed` (spec `railOpen`,
  // `type-composer.jsx:75,825`) — the prototype itself never gates the rail's mount on this
  // flag, only the button's own pressed look, so this does the same rather than inventing a
  // collapse behaviour the wireframe doesn't have.
  const [agentRailOpen, setAgentRailOpen] = useState(!!agentTarget);
  // What the rail's own probe found (`InspectorRailOrganism` -> `AgentThreadOrganism`).
  // Optimistic until it reports, like the rail itself. The header's Agent button and the rail's
  // Agent tab were gated on DIFFERENT facts — `agentTarget` here, `agentTarget && agentAvailable`
  // there — so a confirmed-unavailable service produced an enabled button in the header with no
  // tab to open: `focusAgentInput()` returns early on this very flag, and the toggle's only other
  // effect is its own pressed look. One signal, both ends: an agent affordance hides rather than
  // greys, and it hides at the entrance as well as the destination.
  const [agentAvailable, setAgentAvailable] = useState(true);

  /**
   * `compose.callout`/`side.watching-as-you-edit` — one dismissal set shared by every surface
   * that reads `critique` (the per-block callout AND the Agent tab's aggregate "Watching as you
   * edit" panel), same as the prototype's own single `dismissed` array (`trail-composer.jsx:159`).
   * A dismissed card stays dismissed for the session; no schema representation, so no other seam.
   */
  const [dismissedCritiqueIds, setDismissedCritiqueIds] = useState<string[]>([]);
  const handleDismissCritique = React.useCallback((id: string) => {
    setDismissedCritiqueIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  }, []);
  const visibleCritique = useMemo(
    () => (critique ?? []).filter((c) => !dismissedCritiqueIds.includes(c.id)),
    [critique, dismissedCritiqueIds]
  );
  /**
   * `compose.callout` is an agent affordance like the Agent tab and the header's Agent button:
   * with the agent confirmed unavailable it is HIDDEN, not greyed (spec § Agentic UX). A host
   * may still hand over a `critique` array it computed earlier; the rail already withholds the
   * "Watching as you edit" panel behind `agentAvailable`, so the per-block cards read the same
   * signal rather than a different one.
   */
  const sectionCritique = agentAvailable ? critique : undefined;

  /**
   * `sel.kind === "rule"` — the rule whose editor the rail currently holds: `null` (closed),
   * `'new'`, or the rule itself. Lifted the same way `configuringAspect` was (EAINT-928 Phase
   * 1): `BusinessRulesSection` still owns `rows` and the sheet's save/remove wiring — only
   * which rule's sheet is open, and where its markup mounts, live above it.
   */
  const [ruleEditTarget, setRuleEditTarget] = useState<BusinessRule | 'new' | null>(
    () => (definition?.businessRules ?? []).find((rule) => rule.id === rulesInitialOpen) ?? null
  );
  /** The rail's portal target for the rule editor once `ruleEditTarget` is set — same seam as
   * `configuringPortalNode`. */
  const [ruleEditPortalNode, setRuleEditPortalNode] = useState<HTMLDivElement | null>(null);

  /**
   * `sel.kind === "relationship"` — the relationship whose sheet the rail currently holds:
   * `null` (closed), `'new'` (authoring a brand-new relationship), or the declared row being
   * edited. Lifted the same way `ruleEditTarget` was (EAINT-933 Phase 3.2): `RelationshipsSection`
   * still owns `rows` and the sheet's save wiring — only which relationship's sheet is open, and
   * where its markup mounts, live above it.
   */
  const [relationshipEditTarget, setRelationshipEditTarget] = useState<SchemaRelationship | 'new' | null>(null);
  /** The rail's portal target for the relationship sheet once `relationshipEditTarget` is set —
   * same seam as `ruleEditPortalNode`. */
  const [relationshipEditPortalNode, setRelationshipEditPortalNode] = useState<HTMLDivElement | null>(null);
  /** Whether the open relationship sheet has anything changed worth losing, reported by
   * `RelationshipSheetMolecule` itself (EAINT-933 Phase 3.3, extended from the `start` kind to
   * `relationship`). Reset whenever a fresh sheet opens, dirty or not. */
  const [relationshipDirty, setRelationshipDirty] = useState(false);
  /** Showing the inline "discard these changes?" confirm in place of the sheet — same seam as
   * `confirmingDiscardStart`. */
  const [confirmingDiscardRelationship, setConfirmingDiscardRelationship] = useState(false);
  // A fresh open (a new draft, or switching to a different row's sheet) must never inherit a
  // stale confirm/dirty flag from the PREVIOUS sheet.
  React.useEffect(() => {
    if (relationshipEditTarget !== null) {
      setConfirmingDiscardRelationship(false);
      setRelationshipDirty(false);
    }
  }, [relationshipEditTarget]);
  /** The actual discard, run only once the inline confirm is accepted. */
  const discardRelationshipDraft = React.useCallback(() => {
    setConfirmingDiscardRelationship(false);
    setRelationshipDirty(false);
    setRelationshipEditTarget(null);
  }, []);
  /**
   * Every path that can close the relationship sheet without saving — its own Cancel/dismiss
   * (wired through `RelationshipsSection`'s `onRequestCloseEditing`), the rail head's close
   * button and Escape (both reach here via `handleCloseRailSelection`) — funnels through this so
   * a changed sheet is never discarded without asking. An unchanged sheet closes straight away.
   */
  const handleRequestCloseRelationship = React.useCallback(() => {
    if (relationshipDirty) {
      setConfirmingDiscardRelationship(true);
      return;
    }
    setRelationshipEditTarget(null);
  }, [relationshipDirty]);

  /**
   * `sel.kind === "relOverride"` — the INHERITED relationship whose override sheet the rail
   * currently holds, `null` closed. Lifted the same way `relationshipEditTarget` was, now that
   * the rebinding for `rels > dialog#2` has landed (Waivers section, 2026-09-15): the override
   * sheet's own `rels.dialog#3`/`#4` datums surface under `side`, the same as the add sheet's.
   */
  const [overrideEditTarget, setOverrideEditTarget] = useState<SchemaRelationship | null>(
    () => (relsInitialAdjust && definition ? inheritedRelationships(definition).find((r) => r.system_name === relsInitialAdjust) ?? null : null)
  );
  /** The rail's portal target for the override sheet once `overrideEditTarget` is set — same
   * seam as `relationshipEditPortalNode`. */
  const [overrideEditPortalNode, setOverrideEditPortalNode] = useState<HTMLDivElement | null>(null);
  /** Whether the open override sheet has anything adjusted worth losing, reported by
   * `RelationshipOverrideSheetMolecule` itself (EAINT-933 Phase 3.3, extended from the
   * `relationship` kind to `relOverride`). Reset whenever a fresh sheet opens, dirty or not. */
  const [overrideDirty, setOverrideDirty] = useState(false);
  /** Showing the inline "discard these changes?" confirm in place of the override sheet — same
   * seam as `confirmingDiscardRelationship`. */
  const [confirmingDiscardOverride, setConfirmingDiscardOverride] = useState(false);
  // A fresh open (adjusting a different inherited row) must never inherit a stale
  // confirm/dirty flag from the PREVIOUS sheet.
  React.useEffect(() => {
    if (overrideEditTarget !== null) {
      setConfirmingDiscardOverride(false);
      setOverrideDirty(false);
    }
  }, [overrideEditTarget]);
  /** The actual discard, run only once the inline confirm is accepted. */
  const discardOverrideDraft = React.useCallback(() => {
    setConfirmingDiscardOverride(false);
    setOverrideDirty(false);
    setOverrideEditTarget(null);
  }, []);
  /**
   * Every path that can close the override sheet without saving — its own Cancel/dismiss (wired
   * through `RelationshipsSection`'s `onRequestCloseOverrideEditing`), the rail head's close
   * button and Escape (both reach here via `handleCloseRailSelection`) — funnels through this so
   * an adjusted sheet is never discarded without asking. An unadjusted sheet closes straight away.
   */
  const handleRequestCloseOverride = React.useCallback(() => {
    if (overrideDirty) {
      setConfirmingDiscardOverride(true);
      return;
    }
    setOverrideEditTarget(null);
  }, [overrideDirty]);

  /**
   * `sel.kind === "perspective"` — the LIVE perspective row whose editor the rail currently
   * holds, `null` closed. Lifted the same way `overrideEditTarget` was (EAINT-933 Phase 3.2):
   * `PerspectivesSection` still owns `rows`/`edits` and every lever the open body exposes — only
   * which row is open, and where its editor mounts, lives above it (`persp > list > each(live) >
   * <Row> > list-item > <PerspEditor>` rebinds to `side`, Waivers section 2026-09-15).
   */
  const [perspectiveEditTarget, setPerspectiveEditTarget] = useState<SchemaPerspective | null>(
    () =>
      (perspInitialOpen && definition
        ? [...inheritedPerspectives(definition), ...declaredPerspectives(definition)].find(
            (perspective) => perspective.system_name === perspInitialOpen
          ) ?? null
        : null)
  );
  /** The rail's portal target for the open row's editor once `perspectiveEditTarget` is set —
   * same seam as `overrideEditPortalNode`. */
  const [perspectiveEditPortalNode, setPerspectiveEditPortalNode] = useState<HTMLDivElement | null>(null);

  const handleTypeEditProposal = React.useCallback(
    (proposal: ComposerAgentTypeEditProposal, provenance?: ComposerGhostProvenance) => {
      const rejectedReason = rejectedProposalReason(proposal);
      if (rejectedReason) {
        console.error('[SphereTypeComposer] dropped an agent proposal:', rejectedReason, {
          schemaId: definition?.schema_id,
          sectionId: proposal.sectionId,
          fields: Object.keys(proposal.fields),
        });
        return;
      }
      setGhosts((prev) => [
        ...prev,
        { id: nextGhostId(), sectionId: proposal.sectionId, rationale: proposal.rationale, fields: proposal.fields, edited: {}, provenance, status: 'pending' },
      ]);
      // Spec § Analytics: `sphere_type_agent_proposed`. A proposal is counted when it
      // LANDS as a ghost, not when the agent emits it — a ghost the composer never
      // rendered was never put to anyone.
      logAnalyticsEvent('sphere_type_agent_proposed', undefined, {
        schemaId: definition?.schema_id,
        sectionId: proposal.sectionId,
        fields: Object.keys(proposal.fields).join(','),
        fieldCount: Object.keys(proposal.fields).length,
      });
    },
    [logAnalyticsEvent, definition?.schema_id]
  );

  const handleGhostFieldEdit = React.useCallback((ghostId: string, key: string, value: unknown) => {
    setGhosts((prev) => prev.map((ghost) => (ghost.id === ghostId ? { ...ghost, edited: { ...ghost.edited, [key]: value } } : ghost)));
  }, []);

  const handleDiscardGhost = React.useCallback((ghostId: string) => {
    setGhosts((prev) => prev.filter((ghost) => ghost.id !== ghostId));
  }, []);

  /** Guards `keptGhostAppliesExactlyOnce` synchronously — `setGhosts`'s re-render (which disables
   * the Keep button) is batched and may not have committed before a second click dispatches in
   * the same tick, so the actual dedup has to be a plain ref, not React state. */
  const keepingGhostIdsRef = React.useRef<Set<string>>(new Set());
  /** The queue as of the latest render. Keep resolves its ghost here rather than from the
   * closure it was created in, so a batch started from an older snapshot cannot re-save a
   * ghost that has already left the queue. */
  const ghostsRef = React.useRef(ghosts);
  ghostsRef.current = ghosts;

  /** Applies exactly once per Keep click. On `'conflict'`/`'locked'` the ghost is put back to
   * `'pending'` rather than discarded — a failed save must never silently lose what the user was
   * trying to apply. Resolves to the save outcome, or `undefined` when nothing was attempted. */
  const handleKeepGhost = React.useCallback(
    async (ghostId: string): Promise<UpdateSphereTypeOutcome | 'locked' | 'error' | undefined> => {
      if (keepingGhostIdsRef.current.has(ghostId)) return undefined;
      const ghost = ghostsRef.current.find((candidate) => candidate.id === ghostId);
      if (!ghost || !onSaveField) return undefined;
      keepingGhostIdsRef.current.add(ghostId);
      setGhosts((prev) => prev.map((candidate) => (candidate.id === ghostId ? { ...candidate, status: 'keeping' } : candidate)));
      try {
        const outcome = await onSaveField({ ...ghost.fields, ...ghost.edited } as UpdateSphereTypeFields);
        if (outcome === 'saved') {
          setGhosts((prev) => prev.filter((candidate) => candidate.id !== ghostId));
          setKeptGhostCount((kept) => kept + 1);
          // Spec § Analytics: `sphere_type_agent_ghost_kept` — on the write landing, not on
          // the click, so a refused Keep (`conflict`/`locked`, which puts the ghost back)
          // is never counted as accepted. `edited` says whether the person changed the
          // agent's proposal before keeping it, which is the signal worth having.
          logAnalyticsEvent('sphere_type_agent_ghost_kept', undefined, {
            schemaId: definition?.schema_id,
            sectionId: ghost.sectionId,
            fields: Object.keys({ ...ghost.fields, ...ghost.edited }).join(','),
            editedBeforeKeep: Object.keys(ghost.edited).length > 0,
          });
        } else {
          setGhosts((prev) => prev.map((candidate) => (candidate.id === ghostId ? { ...candidate, status: 'pending' } : candidate)));
        }
        return outcome;
      } finally {
        keepingGhostIdsRef.current.delete(ghostId);
      }
    },
    [onSaveField, logAnalyticsEvent, definition?.schema_id]
  );

  /** Same synchronous guard as Keep, for the batch: a second press while a batch runs must
   * not start a second loop over the same queue. The rail also disables the button, but the
   * re-render that disables it can land after the second click. */
  const approvingAllGhostsRef = React.useRef(false);
  const [approveAllProgress, setApproveAllProgress] = useState<{ kept: number; total: number } | null>(null);

  const handleApproveAllGhosts = React.useCallback(async () => {
    if (approvingAllGhostsRef.current) return;
    const pending = ghostsRef.current.filter((ghost) => ghost.status === 'pending');
    if (pending.length === 0) return;
    approvingAllGhostsRef.current = true;
    setApproveAllProgress({ kept: 0, total: pending.length });
    try {
      // Sequential, not Promise.all: each Keep is its own `update-schema` call guarded by
      // `If-Match`, so applying them one at a time avoids every ghost racing the same stale version.
      for (const ghost of pending) {
        // eslint-disable-next-line no-await-in-loop
        const outcome = await handleKeepGhost(ghost.id);
        if (outcome === 'saved') {
          setApproveAllProgress((progress) => (progress ? { ...progress, kept: progress.kept + 1 } : progress));
        }
      }
    } finally {
      approvingAllGhostsRef.current = false;
      setApproveAllProgress(null);
    }
  }, [handleKeepGhost]);

  // The "are you sure" itself is `InspectorRailOrganism`'s inline confirm (InlineConfirmMolecule,
  // non-modal, next to the button that opened it) — this only runs once that confirm is accepted.
  const handleDiscardAllGhosts = React.useCallback(() => {
    setGhosts([]);
  }, []);
  const localize = React.useCallback<Localize>((key, fallback) => getTranslation(i18n, key, fallback), [i18n]);
  const sectionsRef = useRef<HTMLDivElement>(null);
  /**
   * Save draft (C11) must flush a field that's still focused, mid-edit —
   * `TypeFieldRowMolecule`'s text/multiline controls only call `onCommit` on
   * `onBlur`, so a keystroke that never blurred would otherwise be silently
   * dropped by an explicit Save draft click. Blurring only an element inside
   * the sections themselves (not, say, the Save draft button that triggered
   * this) is deliberate — nothing outside the section body ever holds a
   * committable field edit.
   */
  const flushFocusedField = React.useCallback(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && sectionsRef.current?.contains(active)) {
      active.blur();
      return 1;
    }
    return 0;
  }, []);
  const handleSaveDraft = React.useCallback(() => {
    // The flushed count is passed on so `sphere_type_draft_saved` can state how much
    // uncommitted work the click rescued — only this component knows a field was mid-edit.
    onSaveDraft?.(flushFocusedField());
  }, [flushFocusedField, onSaveDraft]);
  const seenSectionIds = useRef<Set<string>>(new Set());
  const sideRailRef = useRef<ComposerSideRailHandle>(null);

  /**
   * `onDraftAspect`'s `describe` route never reaches the host — it is forwarded to the live
   * agent thread as a chat turn (`ComposerSideRailHandle.submitAgentMessage`), the same "propose,
   * never write" path every other agent edit already takes (`handleReceiveProposal` below turns
   * the resulting `propose_type_edit` tool call into a ghost). Only `blank`/`extend` — a real
   * `POST /aspects/` — are delegated to the host's own `onDraftAspect` (EAINT-933 Phase 3.4).
   * Read through a ref, not a `useCallback` dep, so THIS function's identity never changes —
   * `AspectsSection`'s `onDraftAspectStateChange` effect depends on it, and a fresh identity
   * every render (from an unmemoized `t`, e.g.) would re-fire that effect every render, which
   * calls a state setter, which re-renders this component, which would make a fresh identity
   * again — the same update-depth-exceeded loop `ghostsRef` exists to avoid elsewhere in this file.
   */
  const draftAspectDepsRef = useRef({ onDraftAspect, t });
  draftAspectDepsRef.current = { onDraftAspect, t };
  const handleDraftAspect = React.useCallback(async (draft: NewAspectDraft): Promise<string | undefined> => {
    const { onDraftAspect: hostDraftAspect, t: translate } = draftAspectDepsRef.current;
    if (!hostDraftAspect) return undefined;
    if (draft.route === 'describe') {
      const message = draft.name
        ? translate('studio.typeEditor.aspects.draftNew.describeAgentMessageNamed', { name: draft.name, intent: draft.intent })
        : translate('studio.typeEditor.aspects.draftNew.describeAgentMessage', { intent: draft.intent });
      sideRailRef.current?.submitAgentMessage(message);
      return undefined;
    }
    return hostDraftAspect(draft);
  }, []);

  /**
   * `sel.kind === "pick"` (`type-rail/trail-composer.jsx:822-828`) — whether the attach-aspect
   * picker is the rail's current selection. `AspectsSection` still owns the picker's own state
   * (the catalog fetch, `availableToAttach`, `handleAttach`) — only WHICH ONE of the rail's
   * things is open moves up here, so the root can tell `InspectorRailOrganism` to show the
   * picker's head instead of the tab strip (Phase 3a skeleton; the attachment and rule sheets
   * make the same move in Phase 3c, per plan D3).
   */
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * The picker's render inputs (items/loading/error/handlers), reported by `AspectsSection` via
   * `onPickerStateChange` — `null` while closed. The section still owns the catalog fetch and
   * `handleAttach`; the root renders `PickerShellMolecule` itself from this state as the rail's
   * `pick` selection body (EAINT-928 Phase 1 — no portal, unlike `attach`/`rule`, which still
   * portal their built markup into a rail-mounted target div).
   */
  const [pickerRenderState, setPickerRenderState] = useState<AspectPickerRenderState | null>(null);
  /**
   * `sel.kind === "attach"` (`type-rail/trail-composer.jsx:837-860`) — whether an attachment's
   * own config sheet (`AspectAttachmentSheetMolecule`) is the rail's current selection. Lifted
   * the same way `pickerOpen` was (Phase 3a): `AspectsSection` still owns the sheet's fields and
   * `onChange`/`onRemove` wiring — only which aspect's sheet is open, and where its markup
   * mounts, live above it. Holds the aspect's `systemName` (not a boolean) so the root can look
   * up its display name and version for the rail head without `AspectsSection` reaching upward.
   */
  const [configuringAspect, setConfiguringAspect] = useState<string | null>(aspectsInitialConfiguring ?? null);
  /** The rail's portal target for the attachment sheet once `configuringAspect` is set (`pick`'s
   * former equivalent, `pickerPortalNode`, was retired in EAINT-928 Phase 1). */
  const [configuringPortalNode, setConfiguringPortalNode] = useState<HTMLDivElement | null>(null);
  /**
   * `sel.kind === "start"` — whether the "Draft a new aspect" dialog is the rail's current
   * selection. Lifted the same way `pickerOpen` was (EAINT-933 Phase 3.2): `AspectsSection`
   * still owns the catalog it searches and the create/use-existing wiring — only whether it is
   * open lives above it.
   */
  const [draftingAspect, setDraftingAspect] = useState(false);
  /** The "Draft a new aspect" dialog's render inputs, reported by `AspectsSection` — `null`
   * while `draftingAspect` is false, mirroring `pickerRenderState`. */
  const [draftAspectRenderState, setDraftAspectRenderState] = useState<DraftAspectRenderState | null>(null);
  /** Whether the open draft has anything typed worth losing (EAINT-933 Phase 3.3: "dirty draft
   * never lost silently") — reported by `NewAspectDialogMolecule` itself, since only it knows
   * which route's fields count. Reset whenever the dialog closes, dirty or not. */
  const [startDirty, setStartDirty] = useState(false);
  /** Showing the inline "discard this draft?" confirm in place of the dialog — the composer's own
   * `InlineConfirmMolecule` seam (the discard-all queue confirm, `sphere-type-editor__drop-confirm`
   * before it) rather than a modal, per the rail's non-modal surface decision. */
  const [confirmingDiscardStart, setConfirmingDiscardStart] = useState(false);
  // A fresh open must never inherit a stale confirm/dirty flag from the PREVIOUS draft — the
  // successful-create path (`onCreate` above) closes via `onDraftingAspectChange(false)` directly,
  // not `discardStartDraft`, so nothing else clears these on that path.
  React.useEffect(() => {
    if (draftingAspect) {
      setConfirmingDiscardStart(false);
      setStartDirty(false);
    }
  }, [draftingAspect]);
  /** The actual discard, run only once the inline confirm is accepted. */
  const discardStartDraft = React.useCallback(() => {
    setConfirmingDiscardStart(false);
    setStartDirty(false);
    setDraftingAspect(false);
  }, []);
  /**
   * Every path that can close the "Draft a new aspect" editor — the dialog's own Close/Cancel
   * (wired below), the rail head's close button and Escape (both reach here via
   * `handleCloseRailSelection`) — funnels through this so a draft with typed text is never
   * discarded without asking. An empty draft (nothing typed) closes straight away, same as before.
   */
  const handleRequestCloseStart = React.useCallback(() => {
    if (startDirty) {
      setConfirmingDiscardStart(true);
      return;
    }
    setDraftingAspect(false);
  }, [startDirty]);
  /** Same map `AspectsSection` builds from the same `aspectCatalog` prop — needed here too, only
   * for the rail head's display name, so the section's own copy stays the source of truth for
   * everything else it reads the catalog for. */
  const aspectCatalogByName = useMemo(() => new Map((aspectCatalog ?? []).map((row) => [row.system_name, row])), [aspectCatalog]);
  const configuringAspectRow = configuringAspect ? (definition?.aspects ?? []).find((aspect) => aspect.systemName === configuringAspect) : undefined;
  const rawRailSelection: InspectorRailSelection | undefined = pickerOpen
    ? {
        kind: t('studio.typeEditor.composer.sideRail.selectionPickKind'),
        title: t('studio.typeEditor.composer.sideRail.selectionPickTitle'),
        body: (
          <div className="composer-side-rail__picker-target" data-container="side > pick">
            {pickerRenderState && (
              <PickerShellMolecule<AspectRow>
                items={pickerRenderState.items}
                getSearchText={(row) => `${getLocalizedText(row.name, row.system_name)} ${row.system_name}`}
                getRowKey={(row) => row.system_name}
                renderRow={(row) => (
                  <div
                    className="sphere-type-editor__aspect-picker-row"
                    data-testid={`aspect-attach-option-${row.system_name}`}
                  >
                    {getLocalizedText(row.name, row.system_name)}
                  </div>
                )}
                onPickItem={(row) => pickerRenderState.onPick(row.system_name)}
                onCancel={pickerRenderState.onCancel}
                scopeLabel={t('studio.typeEditor.aspects.attach.scopeLabel', {
                  count: pickerRenderState.scopeLabelCount,
                })}
                loading={pickerRenderState.loading}
                loadingMessage={t('studio.typeEditor.aspects.attach.loading')}
                errorMessage={pickerRenderState.errorMessage}
                noMatchesMessage={t('studio.typeEditor.aspects.attach.noMatches')}
                searchPlaceholder={t('studio.typeEditor.aspects.attach.searchPlaceholder')}
                cancelLabel={t('common.cancel')}
                testIdPrefix="aspect-attach-option"
                className="sphere-type-editor__aspect-picker"
              />
            )}
          </div>
        ),
      }
    : configuringAspect
    ? {
        kind: t('studio.typeEditor.composer.sideRail.selectionAttachKind'),
        // `configuringAspectRow` is `definition.aspects`'s copy — absent for an aspect attached
        // THIS session (`handleCreateAspect`'s attach+open), since that only lands in `definition`
        // after the write round-trips (`useSphereType.saveField`'s re-resolve). The system_name
        // itself is always known (it's `configuringAspect`), so the head still opens and names it —
        // same "system_name is the last resort" fallback `aspectDisplayName` uses.
        title: configuringAspectRow ? aspectDisplayName(configuringAspectRow, aspectCatalogByName) : configuringAspect,
        sub: configuringAspectRow?.versionPin === undefined ? undefined : `v${configuringAspectRow.versionPin}`,
        body: <div ref={setConfiguringPortalNode} className="composer-side-rail__attach-target" data-container="side > attach" />,
      }
    : draftingAspect
    ? {
        kind: t('studio.typeEditor.composer.sideRail.selectionStartKind'),
        title: t('studio.typeEditor.composer.sideRail.selectionStartTitle'),
        body: (
          <div className="composer-side-rail__start-target" data-container="side > start">
            {confirmingDiscardStart && (
              <InlineConfirmMolecule
                tone="warning"
                icon="pi-exclamation-triangle"
                testId="composer-side-rail-discard-draft-confirm"
                titleId="composer-side-rail-discard-draft-confirm-title"
                title={t('studio.typeEditor.composer.sideRail.discardDraftTitle')}
                body={t('studio.typeEditor.composer.sideRail.discardDraftMessage')}
                actions={[
                  {
                    label: t('studio.typeEditor.composer.sideRail.discardDraftAccept'),
                    onClick: discardStartDraft,
                    variant: 'danger',
                    'data-testid': 'composer-side-rail-discard-draft-confirm-accept',
                  },
                  {
                    label: t('studio.typeEditor.composer.sideRail.discardDraftCancel'),
                    onClick: () => setConfirmingDiscardStart(false),
                    variant: 'text',
                    'data-testid': 'composer-side-rail-discard-draft-confirm-cancel',
                  },
                ]}
              />
            )}
            {/* `hidden`, not conditionally unmounted, while the confirm is showing — Cancelling the
                confirm has to hand back a dialog that still holds what was typed (EAINT-933 Phase
                3.3: "dirty draft never lost silently"), and swapping this subtree out for the
                confirm and back would remount it, resetting `name`/`intent`/`extendsAspect` to
                nothing, which is the exact silent loss the confirm exists to prevent. */}
            {draftAspectRenderState && (
              <div hidden={confirmingDiscardStart}>
                <NewAspectDialogMolecule
                  visible
                  railMode
                  forTypeName={definition ? localize(definition.localization_name_key, definition.name) : null}
                  aspectCatalog={draftAspectRenderState.aspectCatalog}
                  onClose={handleRequestCloseStart}
                  onCreate={draftAspectRenderState.onCreate}
                  onUseExisting={draftAspectRenderState.onUseExisting}
                  onDirtyChange={setStartDirty}
                  error={draftAspectRenderState.error}
                  submitting={draftAspectRenderState.submitting}
                />
              </div>
            )}
          </div>
        ),
      }
    : ruleEditTarget !== null
    ? {
        kind: t('studio.typeEditor.composer.sideRail.selectionRuleKind'),
        title:
          ruleEditTarget === 'new'
            ? t('studio.typeEditor.businessRules.untitledRule')
            : ruleEditTarget.name || t('studio.typeEditor.businessRules.untitledRule'),
        // `sub` (trail-composer.jsx:858-859): a new rule has none yet; an existing rule reads
        // its severity — the same three-way split the rule row itself uses to badge severity,
        // restated as prose for the rail head. The prototype's third branch, "declared by
        // {ancestor}" for an inherited rule, has no reachable state here: `onEdit` is only
        // wired for `!rule.is_inherited` (`:3327` — editing an inherited rule here would fork
        // it silently), so `ruleEditTarget` can never hold an inherited rule.
        sub:
          ruleEditTarget === 'new'
            ? undefined
            : ruleEditTarget.severity === 'error'
            ? t('studio.typeEditor.composer.sideRail.selectionRuleSubBlocks')
            : ruleEditTarget.severity === 'warning'
            ? t('studio.typeEditor.composer.sideRail.selectionRuleSubWarns')
            : t('studio.typeEditor.composer.sideRail.selectionRuleSubNotes'),
        body: <div ref={setRuleEditPortalNode} className="composer-side-rail__rule-target" data-container="side > rule" />,
      }
    : relationshipEditTarget !== null
    ? {
        kind: t('studio.typeEditor.composer.sideRail.selectionRelationshipKind'),
        title:
          relationshipEditTarget === 'new'
            ? t('studio.typeEditor.relationships.addRelationship')
            : relationshipEditTarget.name || t('studio.typeEditor.relationships.addRelationship'),
        body: (
          <>
            {confirmingDiscardRelationship && (
              <InlineConfirmMolecule
                tone="warning"
                icon="pi-exclamation-triangle"
                testId="composer-side-rail-discard-relationship-confirm"
                titleId="composer-side-rail-discard-relationship-confirm-title"
                title={t('studio.typeEditor.composer.sideRail.discardEditTitle')}
                body={t('studio.typeEditor.composer.sideRail.discardEditMessage')}
                actions={[
                  {
                    label: t('studio.typeEditor.composer.sideRail.discardEditAccept'),
                    onClick: discardRelationshipDraft,
                    variant: 'danger',
                    'data-testid': 'composer-side-rail-discard-relationship-confirm-accept',
                  },
                  {
                    label: t('studio.typeEditor.composer.sideRail.discardEditCancel'),
                    onClick: () => setConfirmingDiscardRelationship(false),
                    variant: 'text',
                    'data-testid': 'composer-side-rail-discard-relationship-confirm-cancel',
                  },
                ]}
              />
            )}
            {/* `hidden`, not conditionally unmounted, while the confirm is showing — same reason
                the `start` kind's dialog stays mounted: Cancelling the confirm has to hand back a
                sheet that still holds what was typed/changed. */}
            <div
              ref={setRelationshipEditPortalNode}
              className="composer-side-rail__relationship-target"
              data-container="side > relationship"
              hidden={confirmingDiscardRelationship}
            />
          </>
        ),
      }
    : overrideEditTarget !== null
    ? {
        kind: t('studio.typeEditor.composer.sideRail.selectionRelOverrideKind'),
        title: overrideEditTarget.name || t('studio.typeEditor.composer.sideRail.selectionRelOverrideKind'),
        body: (
          <>
            {confirmingDiscardOverride && (
              <InlineConfirmMolecule
                tone="warning"
                icon="pi-exclamation-triangle"
                testId="composer-side-rail-discard-override-confirm"
                titleId="composer-side-rail-discard-override-confirm-title"
                title={t('studio.typeEditor.composer.sideRail.discardEditTitle')}
                body={t('studio.typeEditor.composer.sideRail.discardEditMessage')}
                actions={[
                  {
                    label: t('studio.typeEditor.composer.sideRail.discardEditAccept'),
                    onClick: discardOverrideDraft,
                    variant: 'danger',
                    'data-testid': 'composer-side-rail-discard-override-confirm-accept',
                  },
                  {
                    label: t('studio.typeEditor.composer.sideRail.discardEditCancel'),
                    onClick: () => setConfirmingDiscardOverride(false),
                    variant: 'text',
                    'data-testid': 'composer-side-rail-discard-override-confirm-cancel',
                  },
                ]}
              />
            )}
            {/* `hidden`, not conditionally unmounted, while the confirm is showing — same reason
                the `relationship` kind's sheet stays mounted: Cancelling the confirm has to hand
                back a sheet that still holds what was adjusted. */}
            <div
              ref={setOverrideEditPortalNode}
              className="composer-side-rail__rel-override-target"
              data-container="side > relOverride"
              hidden={confirmingDiscardOverride}
            />
          </>
        ),
      }
    : perspectiveEditTarget !== null
    ? {
        kind: t('studio.typeEditor.composer.sideRail.selectionPerspectiveKind'),
        title: localize(perspectiveEditTarget.label_key, perspectiveEditTarget.label || perspectiveEditTarget.name),
        body: (
          <div ref={setPerspectiveEditPortalNode} className="composer-side-rail__perspective-target" data-container="side > perspective" />
        ),
      }
    : undefined;
  /**
   * Wraps whichever kind's body `rawRailSelection` picked with the SAME save-outcome facts the
   * top-of-document banner reads (`saving`/`saveError`/`conflict`, all `useSphereType`'s own —
   * see that prop's doc comment) — one source of truth, rendered a second place. An author with a
   * relationship/override/perspective/rule/attachment editor open in the rail is looking at the
   * rail, not the top of the document, so the in-flight/failed/stale-conflict state that already
   * exists for every `onSaveField` call needs to be visible there too (EAINT-933 Phase 3.3).
   */
  const railSelection: InspectorRailSelection | undefined = rawRailSelection && {
    ...rawRailSelection,
    body: (
      <>
        {saving && (
          <MessageAtom
            severity="neutral"
            data-testid="composer-side-rail-saving"
            text={<p>{t('studio.typeEditor.composer.sideRail.savingBanner')}</p>}
          />
        )}
        {saveError && (
          <MessageAtom
            severity="error"
            data-testid="composer-side-rail-save-error"
            text={<p>{t('studio.typeEditor.composer.saveErrorBanner')}</p>}
          />
        )}
        {conflict && (
          <MessageAtom
            severity="warn"
            data-testid="composer-side-rail-conflict"
            text={<p>{t('studio.typeEditor.composer.conflictBanner')}</p>}
          />
        )}
        {rawRailSelection.body}
      </>
    ),
  };
  // Visual-only phase acceptance row (spec Phase 3, side-by-side of the rail with each body
  // open) is deferred to the capture-index fix (plan Current Priority item 2) — a stale
  // .claude-temp/renders index refuses to record a baseline, so a side-by-side run now would
  // read against corrupted data; re-run once the index only keeps entries a full sweep saw.
  const handleCloseRailSelection = React.useCallback(() => {
    // The rail head's close button and Escape (`InspectorRailOrganism`'s own `useDialogFocus`
    // `onDismiss`) reach every kind through this one function, so the `start`, `relationship`
    // and `relOverride` kinds' dirty guards have to live here too, not only on their own
    // Close/Cancel — those already stop above, at `handleRequestCloseStart` /
    // `handleRequestCloseRelationship` / `handleRequestCloseOverride`, and never call this at
    // all (EAINT-933 Phase 3.3). `perspective` commits every field the moment it is edited (no
    // separate draft to lose — see the plan's Open Questions), so it has no guard here.
    if (draftingAspect) {
      handleRequestCloseStart();
      return;
    }
    if (relationshipEditTarget !== null) {
      handleRequestCloseRelationship();
      return;
    }
    if (overrideEditTarget !== null) {
      handleRequestCloseOverride();
      return;
    }
    setPickerOpen(false);
    setConfiguringAspect(null);
    setRuleEditTarget(null);
    setPerspectiveEditTarget(null);
  }, [
    draftingAspect,
    handleRequestCloseStart,
    relationshipEditTarget,
    handleRequestCloseRelationship,
    overrideEditTarget,
    handleRequestCloseOverride,
  ]);

  /**
   * The Sample tab's body (`type-composer.jsx:1838`, `:1841`, `:2099`) — owned here rather than by
   * `InspectorRailOrganism` (EAINT-921 Phase 2: that organism is generic and takes the tab's body
   * as a `sampleContent` slot). The composer owns the call rather than the section molecules
   * because both readings of the record — the panel and the Full-sphere dialog — offer it, and
   * they must share one sphere, not create two.
   */
  const sideRailApiClients = useApiClientsOrNull();
  const sandboxName = sampleRecordName(definition, t);
  const sandbox = useSandboxSphere({
    graphClient: sideRailApiClients?.graphClient,
    schemaSystemName: definition?.system_name,
    centerSphereId: definition?.tenant_id,
    schemaId: definition?.schema_id,
    sphereName: sandboxName,
  });
  const [sampleRecordPreviewOpen, setSampleRecordPreviewOpen] = useState(false);
  /** Once a sandbox sphere exists, `[Full sphere]` opens THAT sphere — the real one is a better
   * answer to "show me the whole thing" than the synthetic preview. With no sandbox (or no way to
   * navigate), it opens the preview dialog it always has. */
  const handleOpenFullSphere = React.useCallback(() => {
    if (sandbox.state.status === 'created' && sandbox.state.sphereId && onOpenSphere) {
      onOpenSphere(sandbox.state.sphereId);
      return;
    }
    setSampleRecordPreviewOpen(true);
  }, [sandbox.state, onOpenSphere]);

  /** The return leg of a ghost's provenance line: opens the Agent tab on the turn that produced
   * the proposal. Only reachable while the side rail is mounted (i.e. not locked). */
  const handleShowGhostOrigin = React.useCallback((turnId: string) => {
    sideRailRef.current?.focusAgentTurn(turnId);
  }, []);

  /** Spec Keyboard map: "Agent rail … Escape from a ghost returns to the thread." Ghosts render
   * inline in their section, not inside the rail itself, so "the thread" is reached by asking the
   * rail to switch to its Agent tab (if needed) and focus the thread's input. */
  const handleGhostEscape = React.useCallback(() => {
    sideRailRef.current?.focusAgentInput();
  }, []);

  /**
   * Moves DOM focus (and scrolls) to a section's heading — used by the gate
   * bar's check chips (spec `gateCheckLinksToItsSection`). Reaches into the
   * DOM rather than a ref map because the eleven section bodies are rendered
   * generically by id; a per-section ref would be the same lookup by another
   * name.
   */
  const focusSection = React.useCallback((sectionId: string) => {
    // A `side` block (Arcs/Health/Presentation) has no anchor in `sectionsRef` — it lives in the
    // rail's Inherited tab, so the jump opens that tab rather than looking for a doc heading.
    if (SIDE_SECTION_IDS.has(sectionId)) {
      sideRailRef.current?.focusInheritedTab();
      return;
    }
    const container = document.getElementById(anchorId(sectionId));
    const header = container?.querySelector<HTMLElement>('.collapsible-section__header');
    // jsdom (unit tests) has no `scrollIntoView` implementation — optional-call it.
    header?.scrollIntoView?.({ block: 'start' });
    header?.focus();
  }, []);

  // Only org-owned types need the name lookup — a platform schema's chip reads
  // "Platform" from `is_system_schema` alone, so skip the network call for it.
  const { publisherName } = usePublisherName(definition && !definition.is_system_schema ? definition.tenant_id : null);

  useEffect(() => {
    seenSectionIds.current = new Set();
  }, [definition]);

  // Ghosts belong to the type currently open, not the composer instance — a different
  // system_name (navigating to another type) clears them; a save-triggered definition refresh
  // (same system_name, new version_id) must not, or a just-landed second proposal would vanish
  // out from under the user mid-review.
  //
  // The comparison is against a ref rather than the effect's dependency alone because an effect
  // also fires on MOUNT, where nothing has changed — harmless while `ghosts` always started
  // empty, and a silent wipe of `initialGhosts` once it did not.
  const openTypeRef = useRef(definition?.system_name);
  useEffect(() => {
    if (openTypeRef.current === definition?.system_name) return;
    openTypeRef.current = definition?.system_name;
    setGhosts([]);
    setKeptGhostCount(0);
  }, [definition?.system_name]);

  useEffect(() => {
    if (!onSectionView || status !== 'ready' || !sectionsRef.current) return undefined;

    const targets = sectionsRef.current.querySelectorAll<HTMLElement>('[id^="sphere-type-section-"]');
    if (targets.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const sectionId = entry.target.id.replace('sphere-type-section-', '');
          if (seenSectionIds.current.has(sectionId)) return;
          seenSectionIds.current.add(sectionId);
          onSectionView(sectionId);
        });
      },
      { rootMargin: SECTION_VIEW_ROOT_MARGIN }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [onSectionView, status, definition]);

  if (status === 'loading') {
    return (
      <div
        className="sphere-type-editor sphere-type-editor--loading"
        data-testid="sphere-type-editor-loading"
        role="status"
        aria-label={t('studio.typeEditor.loading')}
      >
        <SkeletonAtom width="18rem" height="2rem" />
        <SkeletonAtom count={6} height="1.25rem" />
      </div>
    );
  }

  if (status === 'notFound') {
    return (
      <div className="sphere-type-editor sphere-type-editor--not-found" data-testid="sphere-type-editor-not-found">
        <EmptyStateAtom icon="pi-ban" title={t('studio.typeEditor.notFound.title')} description={t('studio.typeEditor.notFound.description')} />
      </div>
    );
  }

  if (status === 'degraded' || !definition) {
    return (
      <div className="sphere-type-editor sphere-type-editor--degraded" data-testid="sphere-type-editor-degraded">
        <MessageAtom
          severity="error"
          text={
            <>
              <strong>{t('studio.typeEditor.degraded.title')}</strong>
              <p>{t('studio.typeEditor.degraded.description')}</p>
            </>
          }
        />
      </div>
    );
  }

  const sections = buildSections(definition);
  // The doc scroll and index rail render everything but `side` blocks; those (Arcs/Health/
  // Presentation) render as the same card, just inside the rail's Inherited tab instead — see
  // `SectionSpec.side`.
  const docSections = sections.filter((section) => !section.side);
  const railSections = sections.filter((section) => section.side);
  // Fail-closed: anything other than an explicit `true` locks the composer
  // (spec `lockedModeDerivesFromBackendCanEditNotFromOwnerSphereId` — an
  // unknown/malformed `canEdit` must never read as editable).
  const locked = definition.canEdit !== true;
  const pendingGhostCount = ghosts.filter((ghost) => ghost.status === 'pending').length;
  // `type-composer.jsx:1856-1866` — the queue is a route, not a tally. Each block holding a
  // proposal gets its own row, in the document's order (not the ghosts' arrival order), so
  // "3 blocks pending" can actually be walked one at a time as the hint beneath it promises.
  const pendingBlocks = sections
    .filter((section) => ghosts.some((ghost) => ghost.sectionId === section.id && ghost.status === 'pending'))
    .map((section) => ({ id: section.id, label: t(section.labelKey), icon: section.icon }));
  // `undefined` (gate not yet loaded) is distinct from `0` (all clear) for the index rail's
  // messaging — only the Publish-blocking check below collapses that distinction.
  const openCheckCount = gate?.checks?.filter((check) => check.state === 'open').length;
  // `type-composer.jsx:826` (`blocked`) — a proposal still awaiting Keep/Discard holds Publish
  // exactly like an unmet check does; the footer's own gate-status text (below) names which.
  const publishBlocked = pendingGhostCount > 0 || (openCheckCount ?? 0) > 0 || gate?.publishable !== true;
  // `type-rail/trail-composer.jsx:913` — the header states the refusal only as `title`; the
  // always-visible reading lives in the pinned footer (`sphere-type-editor-gate-status`,
  // PublishGateBarMolecule), which the Publish button's `aria-describedby` points at instead of
  // duplicating the sentence beside the button (ux-craft I2).
  const publishTitle = publishBlocked ? t('studio.typeEditor.composer.gate.publishHeldReason') : undefined;

  /**
   * `type-composer.jsx:1170-1179` — the Aspects caption is one slot with TWO readings, not a
   * fixed sentence: while this type still owes answers the header carries the work left and
   * walking to it is one click; otherwise it says what a composition brings. The hint is
   * therefore NOT a `caption` string like every other block's — it is the else-branch of a
   * button, and rendering both would state the work is done while it isn't.
   */
  const composedAspects = definition.aspects ?? [];
  const unsettledAspects = unsettledAttachments(
    composedAspects,
    needsByAspect,
    aspectCatalog ? new Map(aspectCatalog.map((row) => [row.system_name, row])) : null
  );
  /**
   * The rules the engine cannot apply as written — `type-composer.jsx:1551`,
   * `ruleRows.some(r => !r.inherited && ruleTrouble(r) > 0)`. Inherited rules are
   * excluded: their fix belongs to the type that declared them.
   */
  const troubledRules = (definition?.businessRules ?? []).filter((rule) => !rule.is_inherited && blockingProblemCount(rule, definition?.properties ?? []) > 0);
  const rulesCaptionSlot =
    !locked && troubledRules.length > 0 ? (
      <ButtonAtom
        variant="ghost"
        size="small"
        icon="pi pi-exclamation-triangle"
        label={t('studio.typeEditor.businessRules.needsFixing')}
        onClick={() => {
          focusSection('business-rules');
          setRuleEditTarget(troubledRules[0]);
        }}
        data-testid="business-rules-needs-fixing"
        data-datum="rules.needs-fixing"
        data-kind="button"
        data-variant="quiet"
      />
    ) : undefined;

  const aspectsCaptionSlot =
    !locked && unsettledAspects.length > 0 ? (
      <ButtonAtom
        variant="ghost"
        size="small"
        icon="pi pi-exclamation-triangle"
        label={t('studio.typeEditor.aspects.unsettled', { count: unsettledAspects.length })}
        title={t('studio.typeEditor.aspects.unsettledTitle')}
        // The prototype also opens the first unsettled row; the row's sheet is owned by
        // `AspectDefSummaryMolecule`'s own `expanded` state and has no seam from here, so this
        // does the half that is real — it walks you to the section — rather than a control
        // that claims to open something and doesn't.
        onClick={() => focusSection('aspects')}
        data-testid="aspects-unsettled"
        data-datum="compose.attachment-needs"
        data-kind="button"
        data-variant="quiet"
        tone="warn"
      />
    ) : composedAspects.length > 0 ? (
      <span className="editor-block__caption" data-datum="compose.each-brings-its-own-translations" data-kind="hint">
        {t('studio.typeEditor.composer.aspects.caption')}
      </span>
    ) : undefined;

  /**
   * One block's card — chrome plus body plus its ghosts — shared by the doc scroll (`docSections`)
   * and the rail's Inherited tab (`railSections`): a `side` block is the same card, just mounted
   * somewhere else, not a different rendering of the block.
   */
  const renderSectionCard = (section: SectionSpec) => {
    const sectionGhosts = ghosts.filter((ghost) => ghost.sectionId === section.id);
    return (
      <EditorBlockMolecule
        key={section.id}
        chrome="card"
        anchorId={anchorId(section.id)}
        sectionId={section.id}
        icon={section.icon}
        label={t(section.labelKey)}
        count={section.count}
        countDatum={SECTION_COUNT_DATUM[section.id]}
        caption={section.id === 'aspects' || section.id === 'health' ? undefined : sectionCaption(section.id, locked, t)}
        captionDatum={SECTION_CAPTION_DATUM[section.id]}
        rightSlot={section.id === 'aspects' ? aspectsCaptionSlot : section.id === 'business-rules' ? rulesCaptionSlot : undefined}
        bodyText={sectionBodyText(section.id, t)}
        bodyTextDatum={SECTION_BODY_TEXT_DATUM[section.id]}
        bodyTextVariant={SECTION_BODY_TEXT_DATUM[section.id] ? 'lead' : 'note'}
        completion={sectionCompletion(section.id, definition, sectionGhosts.length > 0)}
        hasGhost={sectionGhosts.length > 0}
        ghostTagLabel={t('studio.typeEditor.composer.ghost.proposed')}
      >
        {sectionBody(
          section.id,
          definition,
          lineage,
          t,
          localize,
          locked,
          onSaveField,
          conflict,
          needsByAspect,
          focusSection,
          arcTemplates,
          arcsInitialView,
          aspectsInitialConfirmDrop,
          aspectsInitialDropped,
          aspectsInitialConfiguring,
          aspectCatalog,
          onDraftAspect ? handleDraftAspect : undefined,
          pickerOpen,
          setPickerOpen,
          setPickerRenderState,
          configuringAspect,
          setConfiguringAspect,
          configuringPortalNode,
          draftingAspect,
          setDraftingAspect,
          setDraftAspectRenderState,
          ruleEditTarget,
          setRuleEditTarget,
          ruleEditPortalNode,
          relationshipEditTarget,
          setRelationshipEditTarget,
          relationshipEditPortalNode,
          handleRequestCloseRelationship,
          setRelationshipDirty,
          overrideEditTarget,
          setOverrideEditTarget,
          overrideEditPortalNode,
          handleRequestCloseOverride,
          setOverrideDirty,
          perspectiveEditTarget,
          setPerspectiveEditTarget,
          perspectiveEditPortalNode,
          perspInitialDrafting,
          perspInitialAskSuppress,
          perspInitialConfirmDelete,
          perspInitialDeleted,
          perspInitialEdits,
          perspInitialDraft,
          parentOptions,
          onMoveParent,
          sectionCritique,
          dismissedCritiqueIds,
          handleDismissCritique,
          section.id === 'guide' ? sectionGhosts[0] : undefined,
          handleGhostFieldEdit,
          handleKeepGhost
        )}
        {/*
          `guide` renders its own ghost inline (`GuideSection`'s `ghost` branch, matching the
          prototype's dedicated `<Ghost>` wrapper) — the generic per-field rows and bottom bar
          below are every OTHER section's mechanism, and would duplicate guide's Keep action.
        */}
        {section.id !== 'guide' && sectionGhosts.map((ghost) => renderGhostRows(ghost, t, handleGhostFieldEdit, handleGhostEscape))}
        {/*
          The section's proposal bar, LAST inside the block - the contract puts it at the
          end of `arcs` (order 8/9) and of `compose` (order 4/5), after the rows it
          proposes for, so the summary reads as the answer to what is above it.
        */}
        {section.id !== 'guide' && sectionGhosts.map((ghost) => {
          const copy = ghostBarCopy(ghost, arcTemplates, definition, t);
          const datums = SECTION_GHOST_BAR_DATUM[section.id];
          return (
            <GhostBarMolecule
              key={`${ghost.id}-bar`}
              data-testid={`composer-ghost-bar-${section.id}`}
              message={copy.message}
              messageDatum={datums?.message}
              keepLabel={copy.keepLabel}
              keepDatum={datums?.keep}
              keeping={ghost.status === 'keeping'}
              provenance={ghost.provenance}
              onShowOrigin={locked ? undefined : handleShowGhostOrigin}
              onKeep={() => handleKeepGhost(ghost.id)}
              onDiscard={() => handleDiscardGhost(ghost.id)}
            />
          );
        })}
      </EditorBlockMolecule>
    );
  };

  /**
   * The Inherited tab's body (EAINT-897 Phase 5). Read-only by construction — every row is
   * text plus the ancestor it came from, and there is no control on any of them; the tab's
   * framing hint (owned by the rail) already says why.
   */
  const facts = inheritedFacts(definition);
  const inheritedAspectFacts = facts.filter((fact) => fact.kind === 'aspect');
  const inheritedArcFacts = facts.filter((fact) => fact.kind === 'arc');
  const parentName = definition.parent_schema_id
    ? resolveAncestorName(definition.parent_schema_id, lineage, localize, t) ?? resolveAncestorName(lineage?.ancestors[0]?.schemaId, lineage, localize, t)
    : undefined;
  const factOrigin = (fact: InheritedFact) =>
    t('studio.typeEditor.aspects.row.from', {
      schema: resolveAncestorName(fact.fromSchemaId, lineage, localize, t) ?? t('studio.typeEditor.row.unknownAncestor'),
    });
  // Only what the panel actually holds (`type-composer.jsx:576`): its rows, plus the parent
  // line when there is one. A count that included blocks rendered elsewhere would promise
  // content this tab does not have.
  const inheritCount = facts.length + (parentName ? 1 : 0);

  const renderInheritedRow = (fact: InheritedFact) => (
    <li key={`${fact.kind}-${fact.id}`} className="sphere-type-editor__inherited-row" data-testid={`inherited-${fact.kind}-${fact.id}`}>
      <span className="sphere-type-editor__inherited-name">{fact.id}</span>
      <TagAtom label={factOrigin(fact)} tone="neutral" />
    </li>
  );

  const inheritedContent = (
    <div className="sphere-type-editor__inherited">
      {inheritCount === 0 ? (
        <EmptyStateAtom
          data-testid="inherited-empty"
          title={t('studio.typeEditor.composer.sideRail.inheritedEmpty')}
          description={t('studio.typeEditor.composer.sideRail.inheritedEmptyDetail')}
        />
      ) : (
        <>
          {parentName && (
            <p className="sphere-type-editor__inherited-parent" data-testid="inherited-parent">
              {t('studio.typeEditor.composer.sideRail.inheritedParent', { schema: parentName })}
            </p>
          )}
          {inheritedAspectFacts.length > 0 && (
            <section className="sphere-type-editor__inherited-group">
              <h2 className="sphere-type-editor__inherited-heading">
                {t('studio.typeEditor.composer.sideRail.inheritedAspects', { count: inheritedAspectFacts.length })}
              </h2>
              <ul className="sphere-type-editor__inherited-list">{inheritedAspectFacts.map(renderInheritedRow)}</ul>
            </section>
          )}
          {inheritedArcFacts.length > 0 && (
            <section className="sphere-type-editor__inherited-group">
              <h2 className="sphere-type-editor__inherited-heading">
                {t('studio.typeEditor.composer.sideRail.inheritedArcs', { count: inheritedArcFacts.length })}
              </h2>
              <ul className="sphere-type-editor__inherited-list">{inheritedArcFacts.map(renderInheritedRow)}</ul>
            </section>
          )}
        </>
      )}
      {/*
        Arcs / Health / Presentation (`region: "side"` on every one of their contract datums) —
        the same card `docSections` renders, just mounted in the Inherited tab instead of the
        doc scroll (see `SectionSpec.side`).
      */}
      {railSections.map(renderSectionCard)}
    </div>
  );

  const headerActions = !locked && (
    <>
      {agentTarget && agentAvailable && (
        <span className="sphere-type-header__agent-toggle">
          <ButtonAtom
            variant="ghost"
            size="small"
            className="sphere-type-header__agent"
            icon="pi pi-comments"
            label={t('studio.typeEditor.composer.agent.tabLabel')}
            onClick={() => {
              setAgentRailOpen((open) => !open);
              sideRailRef.current?.focusAgentInput();
            }}
            aria-pressed={agentRailOpen}
            data-testid="sphere-type-header-agent-toggle"
            data-datum="header.agent"
            data-kind="button"
          />
          {pendingGhostCount > 0 && (
            <StatusBadgeAtom
              tone="info"
              label={String(pendingGhostCount)}
              size="small"
              className="sphere-type-header__agent-pending"
              data-testid="sphere-type-header-agent-pending"
              data-datum="header.pending-length"
              data-kind="count"
            />
          )}
        </span>
      )}
      <ButtonAtom
        variant="secondary"
        size="small"
        className="sphere-type-header__save-draft"
        label={savingDraft ? t('studio.typeEditor.composer.header.savingDraft') : t('studio.typeEditor.composer.header.saveDraft')}
        onClick={handleSaveDraft}
        disabled={savingDraft}
        data-testid="sphere-type-header-save-draft"
        data-datum="header.save-draft"
        data-kind="button"
      />
      {onPublish && (
        <ButtonAtom
          variant="primary"
          size="small"
          className="sphere-type-header__publish"
          label={publishing ? t('studio.typeEditor.composer.gate.publishing') : t('studio.typeEditor.composer.gate.publish')}
          onClick={onPublish}
          disabled={publishing || publishBlocked}
          title={publishTitle}
          // `type-rail/trail-composer.jsx:913` — the header states only `title`, never running
          // text beside the button; the always-visible reason lives in the pinned footer
          // (`sphere-type-editor-gate-status`, PublishGateBarMolecule). `aria-describedby` keeps
          // the same reason reachable by keyboard/touch/screen-reader (ux-craft I2) without
          // rendering it twice.
          aria-describedby={publishBlocked ? SPHERE_TYPE_GATE_STATUS_ID : undefined}
          data-testid="sphere-type-header-publish"
          data-datum="header.publish"
          data-kind="button"
        />
      )}
    </>
  );

  // The platform-owned / sealed / no-permission banner replaces the generic preview
  // notice: every field below is static text with a lock chip, not a disabled control,
  // and there is no editing affordance anywhere in this view (spec § Primary action and
  // reversal — "Composer (locked)"). Rendered in the shell's own `banner` slot — the same
  // slot the prototype's altitude bar occupies (`type-rail/trail-app.jsx:106-111`) — so it
  // reads as "why this document is the way it is", not an error. `neutral` (not `info`):
  // this is provenance, not a tip.
  const bannerContent = locked ? (
    <div className="sphere-type-editor__locked-banner" data-testid="sphere-type-editor-locked-banner">
      <MessageAtom severity="neutral" text={<p>{t(lockedBannerKey(definition.lockReason))}</p>} />
      <ButtonAtom
        variant="secondary"
        label={t('studio.typeEditor.composer.extendInstead')}
        onClick={onExtendInstead}
        data-testid="sphere-type-editor-extend-instead"
      />
    </div>
  ) : undefined;

  return (
    <div className="sphere-type-editor" data-testid="sphere-type-editor">
      {/* appendTo="self": PrimeReact defaults ConfirmDialog to a document.body portal, which
          would escape this subtree and lose the composer's own descendant styles. */}
      <ConfirmDialogAtom appendTo="self" />
      <EditorRailShellOrganism
        className="sphere-type-editor__rail-shell"
        altitude="type"
        locked={locked}
        documentRegion="doc"
        indexRegion="index"
        railRegion="side"
        banner={bannerContent}
        header={
          <SphereTypeHeaderMolecule
            schema={definition}
            lineage={lineage}
            onBack={onBack}
            actions={headerActions}
            scopePill={!definition.is_system_schema && publisherName ? { organizationName: publisherName } : undefined}
            // `published v3` / `draft` / `unknown`, from the version the read resolved —
            // `header.t-version` renders unconditionally in the contract, so this is never
            // omitted; an invented version number would be worse than saying it's unknown.
            versionPill={
              typeof definition.version === 'number'
                ? definition.version_is_draft
                  ? { published: false }
                  : { published: true, version: definition.version }
                : { unknown: true }
            }
          />
        }
        index={
          <ComposerIndexRailMolecule
            items={sections
              .filter((section) => !section.side)
              .map((section) => {
                const sectionGhosts = ghosts.filter((ghost) => ghost.sectionId === section.id);
                return {
                  id: section.id,
                  icon: section.icon,
                  label: t(section.labelKey),
                  advanced: section.advanced,
                  completion: sectionCompletion(section.id, definition, sectionGhosts.length > 0),
                };
              })}
            anchorId={anchorId}
            locked={locked}
            openCheckCount={openCheckCount}
            pendingGhostCount={pendingGhostCount}
            keptGhostCount={keptGhostCount}
            onInheritedClick={onInheritedClick ?? (() => sideRailRef.current?.focusInheritedTab())}
            namespace="studio.typeEditor.composer.indexRail"
            ariaLabelKey="studio.typeEditor.sectionIndexLabel"
          />
        }
        rail={
          // `type-composer.jsx:1795` — `<aside className="t-side">` renders for a
          // read-only type too; only its CONTENTS branch (the Agent tab is the one gated
          // on `t.drafting`). Gating the whole aside on `!locked` dropped Sample record
          // and Inherited from every platform, sealed and no-permission state — the
          // audience that most needs "here is the whole definition, and here is what it
          // produces". Same defect the footer had below.
          <InspectorRailOrganism
            ref={sideRailRef}
            locked={locked}
            namespace="studio.typeEditor.composer.sideRail"
            agentNamespace="studio.typeEditor.composer.agent"
            parity={SIDE_RAIL_AGENT_PARITY}
            selection={railSelection}
            onCloseSelection={handleCloseRailSelection}
            entity={definition ? { kind: 'schema', id: definition.schema_id, systemName: definition.system_name, label: definition.name ?? '' } : null}
            sampleContent={
              <SampleRecordPanelMolecule
                definition={definition}
                aspectCatalog={aspectCatalog}
                onOpenFullSphere={handleOpenFullSphere}
                onCreateSandboxSphere={sandbox.create}
                onDeleteSandboxSphere={sandbox.remove}
                sandbox={sandbox.state}
              />
            }
            agentEndpoint={agentEndpoint}
            agentGetToken={agentGetToken}
            agentTarget={agentTarget}
            agentDraftDigest={buildDraftDigest(definition)}
            onTypeEditProposal={handleTypeEditProposal}
            pendingGhostCount={pendingGhostCount}
            pendingBlocks={pendingBlocks}
            onJumpToBlock={focusSection}
            onApproveAllGhosts={handleApproveAllGhosts}
            approveAllProgress={approveAllProgress}
            onDiscardAllGhosts={handleDiscardAllGhosts}
            discardAllCount={ghosts.length}
            inheritCount={inheritCount}
            inheritedContent={inheritedContent}
            initialTab={initialSideTab}
            initialAgentItems={initialAgentItems}
            onAgentAvailabilityChange={setAgentAvailable}
            critique={visibleCritique}
            onDismissCritique={handleDismissCritique}
          />
        }
        footer={
          // `type-composer.jsx:1911` — `.t-foot` renders for a read-only type too; only
          // its contents branch on `readOnly`. Gating the whole bar on `!locked` dropped
          // the provenance note and the gate chips a platform type still shows.
          <PublishGateBarMolecule
            namespace="studio.typeEditor.composer.gate"
            statusId={SPHERE_TYPE_GATE_STATUS_ID}
            locked={locked}
            gateStatus={gateStatus}
            checks={gate?.checks ?? []}
            pendingGhostCount={pendingGhostCount}
            onCheckActivate={focusSection}
            onPreviewSample={() => sideRailRef.current?.focusSampleTab()}
          />
        }
      >
        {saveError && (
          <MessageAtom severity="error" data-testid="sphere-type-editor-save-error" text={<p>{t('studio.typeEditor.composer.saveErrorBanner')}</p>} />
        )}

        <div className="sphere-type-editor__sections" ref={sectionsRef}>
          {docSections.map(renderSectionCard)}
        </div>
      </EditorRailShellOrganism>
      {/* `[Full sphere]` (`:1838` → `:2058`) — the same derived record, opened wide. Rendered here
          rather than inside `InspectorRailOrganism` (EAINT-921 Phase 2: that organism is generic
          and knows nothing about sample records) but driven by the same sandbox state the Sample
          tab reads, so both readings of the record share one sphere. */}
      <SampleRecordDialogMolecule
        visible={sampleRecordPreviewOpen}
        definition={definition}
        aspectCatalog={aspectCatalog}
        onHide={() => setSampleRecordPreviewOpen(false)}
        onCreateSandboxSphere={sandbox.create}
        onDeleteSandboxSphere={sandbox.remove}
        sandbox={sandbox.state}
      />
    </div>
  );
};

SphereTypeComposerOrganism.displayName = 'SphereTypeComposerOrganism';

export default SphereTypeComposerOrganism;
